import { format } from 'date-fns';
import { FOOTER_BANNER_BASE64 } from './footerBanner';
import { LOGO_BASE64 } from './logoAsset';
import api, { savedReportsApi } from '@/services/api';
// Iter-110 Phase A1 — canonical checklist metadata (source of truth).
// Do NOT duplicate any of these maps inline; import from here so mobile
// stays byte-parity with the web app's PDF renderer.
import {
  effectiveStatus,
  statusLabel,
  statusColor,
  buildReportReferenceNumber,
  normaliseComponents,
  riskFor,
  failTextFor,
  DEFECT_CATEGORY_LABELS,
  DEFECT_CATEGORY_ORDER,
  FREQUENCY_JUSTIFICATION,
  CERTIFICATION_LABEL_ADVISORY_COPY,
  DOORSET_REPLACEMENT_STATEMENT,
  doorsetNeedsReplacement,
  isSuppressedOptionalFcp,
  isEvidenceBackedOptionalFail,
} from './reportChecklist';
import { FAILURE_REASON_GROUPS, ADVISORY_REASONS } from './failureReasons';
import { normaliseCustomAnswers } from './customAnswers';
import { captionFor } from './photoCaptions';
import { groupByLeaf, isDoubleDoor, leafSuffix } from './doorLeaves';
import { quoteHasReplacement, replacementDoorRefs, replacementScopeLines, splitScopeLine } from './replacementScope';
import { knownDoorRefs, quoteItemDoorKey } from './quoteDoors';
import { buildDoorDiagramSvg, gapRowsFor, GapRow } from './doorDiagram';
import {
  buildRemedialSchedule,
  indexTracking,
  trackingFor,
  TRACKING_STATUS_LABEL,
  type RemedialTrackingRow,
  consolidateRemedialActions,
} from './remedialSchedule';

// Iter-163l — Build stamp printed on every generated PDF so the user
// can tell at a glance whether the bundle running on their device is
// the latest version (or whether Metro / a browser is still serving
// the previous JS bundle). Bump this string whenever a fix lands in
// the PDF renderer.
const REPORT_ENGINE_BUILD = 'legal-p1 · 2026-06';

// Legal Phase 1 — statutory scope-&-limitations disclaimer + document
// metadata printed on EVERY assessment report (building + door). Rendered
// OUTSIDE the optional compliance toggle so it is non-removable by the user.
const renderLegalDisclaimer = (reference?: string): string => {
  const generatedAt = format(new Date(), 'dd/MM/yyyy HH:mm');
  const ref = reference || `FD-DOC-${format(new Date(), 'yyyyMMdd-HHmmss')}`;
  return `
    <div style="margin-top:22px; padding:12px 14px; border:1px solid #9CA3AF; border-radius:8px; background:#F9FAFB; page-break-inside:avoid; font-family:Arial, Helvetica, sans-serif;">
      <div style="font-size:10px; font-weight:800; letter-spacing:0.5px; color:#374151; text-transform:uppercase; margin-bottom:5px;">Important &mdash; scope &amp; limitations of this assessment</div>
      <p style="margin:0; font-size:9px; line-height:1.5; color:#4B5563;">
        This report records a visual, non-destructive fire-door assessment carried out at a single point in time on the date stated, covering only the doorsets and components that were accessible and visible at inspection. It is <strong>not</strong> a certificate, guarantee or warranty of compliance with any standard, regulation or legislation, and is not professional, legal or fire-engineering advice. &ldquo;Satisfactory&rdquo; means no remedial action was identified at the time of assessment; it does not certify ongoing compliance. Items marked &ldquo;Remedial action required&rdquo; or &ldquo;Further review required&rdquo; should be acted upon by a competent person. Responsibility for fire safety and for the fire resistance and suitability of each doorset remains at all times with the Responsible Person (Regulatory Reform (Fire Safety) Order 2005; Fire Safety (England) Regulations 2022); this assessment does not transfer that responsibility. Where any doubt exists, consult a competent fire-door inspector or the manufacturer. The author accepts no liability for actions taken, or not taken, on the basis of this report.
      </p>
      <p style="margin:6px 0 0 0; font-size:8px; color:#9CA3AF;">
        Document reference ${escapeHtml(ref)} &middot; Generated ${generatedAt} &middot; Report engine ${REPORT_ENGINE_BUILD} &middot; Assessment type: visual, non-destructive, point-in-time.
      </p>
    </div>`;
};

// Iter-163c — Pre-compute the union of every canonical BS-8214 reason
// string surfaced by the failure-reason picker. When we parse
// `failed_component_photos[i].notes` (a bullet list of the reasons the
// inspector ticked PLUS anything typed into the "Add your own answer"
// box), any bullet NOT in this set is treated as a bespoke inspector
// answer and is preserved on the PDF report even when the item was
// downgraded to an advisory (which previously suppressed all notes
// for the certification-label question).
const CANONICAL_FAILURE_REASONS: Set<string> = (() => {
  const s = new Set<string>();
  for (const group of FAILURE_REASON_GROUPS) {
    for (const r of group.reasons) s.add(String(r).trim());
  }
  for (const r of ADVISORY_REASONS) s.add(String(r).trim());
  return s;
})();

/**
 * Split fc.notes into custom-answer bullets only — canonical BS-8214
 * reasons and letterbox structured fields are filtered out. Returns
 * an empty array when the inspector didn't type anything bespoke.
 *
 * Rationale: the mobile form stores every ticked reason (canonical +
 * custom) as a bullet in `fc.notes`. On the PDF we want the canonical
 * copy (long advisory paragraph, canonical fail chips) to remain the
 * primary voice — but we ALSO want the inspector's bespoke additions
 * ("Add your own answer") to surface, otherwise the report silently
 * drops what the inspector typed.
 */
const extractInspectorCustomAnswers = (notes?: string): string[] => {
  const src = (notes || '').trim();
  if (!src) return [];
  return src
    .split(/\n/)
    .map((line) => line.replace(/^\s*[\u2022*\-]\s*/, '').trim())
    .filter(Boolean)
    // Drop the structured letterbox fields — they're rendered by
    // renderLetterboxFailureBlock() already.
    .filter((r) => !/^size of opening\s*:/i.test(r))
    .filter((r) => !/^reason for failure\s*:/i.test(r))
    // Drop the "Door needs replacing" prefix — that's a separate chip
    // rendered by the hard-fail path.
    .filter((r) => r.toLowerCase() !== 'door needs replacing')
    // Anything remaining that matches a canonical BS-8214 reason is
    // not "custom" — leave it to the canonical renderer.
    .filter((r) => !CANONICAL_FAILURE_REASONS.has(r));
};

/**
 * Iter-163g — Renders the "Door Replacement Details" HTML block for
 * inspections that ticked "This door needs replacing" AND opted to
 * include the details on the PDF. Used by both the building-report
 * mini-card renderer and the single-door PDF renderer — call sites
 * pick the `variant` (`'mini'` or `'full'`) which just tweaks font
 * sizes / padding for the surrounding context.
 *
 * When the inspection has no `door_replacement_details` block, or
 * has one with `include_on_pdf === false`, returns an empty string
 * so the report stays terse.
 */
const renderDoorReplacementDetails = (
  inspection: any,
  variant: 'mini' | 'full',
): string => {
  const cd = inspection?.checklist_data || {};
  const drd = cd.door_replacement_details;
  if (!drd || typeof drd !== 'object') return '';
  if (drd.include_on_pdf === false) return '';

  const yn = (v: any) =>
    v === true ? 'Yes' : v === false ? 'No' : '—';
  const fontMain = variant === 'mini' ? '11px' : '11pt';
  const fontLabel = variant === 'mini' ? '10px' : '10pt';
  const fontHeading = variant === 'mini' ? '11px' : '11pt';
  const pad = variant === 'mini' ? '8px 10px' : '10px 12px';

  const styleLabel = drd.door_style === 'Other' && drd.door_style_other
    ? `Other &mdash; ${escapeHtml(String(drd.door_style_other))}`
    : (drd.door_style ? escapeHtml(String(drd.door_style)) : '—');

  const rows: { l: string; v: string }[] = [
    { l: 'Frame needs replacing', v: yn(drd.frame_needs_replacing) },
  ];
  if (drd.frame_needs_replacing && drd.frame_width_mm != null) {
    rows.push({ l: 'Frame width', v: `${escapeHtml(String(drd.frame_width_mm))} mm` });
  }
  rows.push({ l: 'Floor level', v: yn(drd.floor_level) });
  rows.push({ l: 'New architraves needed', v: yn(drd.new_architraves_needed) });
  rows.push({ l: 'Door closer needed', v: yn(drd.needs_door_closer) });
  rows.push({ l: 'Signage needed', v: yn(drd.needs_signage) });
  rows.push({ l: 'Redecoration needed', v: yn(drd.needs_redecoration) });
  rows.push({ l: 'Replacement door style', v: styleLabel });

  const rowHtml = rows
    .map(
      (r) => `
      <div style="display:flex; justify-content:space-between; padding:3px 0; border-bottom:1px dashed #FCA5A5;">
        <div style="font-size:${fontLabel}; color:#7F1D1D; font-weight:600;">${r.l}</div>
        <div style="font-size:${fontLabel}; color:#111; font-weight:700;">${r.v}</div>
      </div>`,
    )
    .join('');

  const photos: string[] = Array.isArray(drd.photos) ? drd.photos : [];
  const photoHtml = photos.length > 0 ? `
      <div style="margin-top:8px;">
        <div style="font-size:${fontLabel}; font-weight:800; color:#7F1D1D; margin-bottom:4px;">Site photos (${photos.length})</div>
        <div style="display:flex; flex-wrap:wrap; gap:6px;">
          ${photos
            .slice(0, variant === 'mini' ? 4 : 8)
            .map((p) => {
              const w = variant === 'mini' ? '90px' : '130px';
              const cap = captionFor(inspection?.photo_captions, p);
              return `<div style="width:${w};"><img src="${p}" style="width:${w}; height:${w}; object-fit:cover; border-radius:6px; background:#fff; border:1px solid #FCA5A5;" />${cap ? `<div style="font-size:${variant === 'mini' ? '9px' : '8.5pt'}; color:#7F1D1D; margin-top:2px; line-height:1.25;">${escapeHtml(cap)}</div>` : ''}</div>`;
            })
            .join('')}
        </div>
      </div>
  ` : '';

  const notesHtml = drd.additional_notes ? `
      <div style="margin-top:8px; padding:6px 8px; background:#fff; border:1px solid #FCA5A5; border-radius:6px;">
        <div style="font-size:${fontLabel}; font-weight:800; color:#7F1D1D; margin-bottom:2px;">Additional notes</div>
        <div style="font-size:${fontMain}; color:#1a1a1a; line-height:1.5;">${escapeHtml(String(drd.additional_notes))}</div>
      </div>
  ` : '';

  return `
      <div style="margin-top:8px; padding:${pad}; background:#FFF7ED; border:1px solid #FDBA74; border-left:4px solid #B45309; border-radius:6px;">
        <div style="font-size:${fontHeading}; font-weight:800; color:#7C2D12; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:6px;">Replacement details</div>
        <div>${rowHtml}</div>
        ${notesHtml}
        ${photoHtml}
      </div>
  `;
};

interface InspectionReport {
  building: any;
  door: any;
  inspection: any;
  customSections?: CustomSections;
  includeCompliance?: boolean;
}

interface BuildingReport {
  building: any;
  doors: any[];
  inspections: any[];
  // Iter-110 Phase B2 — optional maintenance jobs across the building.
  // When provided, each door's mini-card renders a "Recent Maintenance
  // Evidence" panel (last 12 months, up to 3 jobs × 6 photos each with
  // BEFORE/AFTER phase pills).
  maintenanceJobs?: any[];
  customSections?: CustomSections;
  includeCompliance?: boolean;
  // Iter-185 — Remedial Tracking In-App. When provided, the Remedial
  // Action Schedule table gains a Status column (Open / Assigned /
  // In progress / Completed + who) from the manager's in-app tracking.
  remedialTracking?: RemedialTrackingRow[];
  // Iter-195 — set when the author reported on a SUBSET of the register
  // (e.g. only the doors re-inspected after repairs). Printed on the
  // cover / building info so the reader knows the scope.
  doorSelection?: { selected: number; total: number };
}

// Phase 5 — editable report sections (loaded from /api/report-templates and
// optionally overridden per-report via the Edit-Report screen).
export interface CustomSections {
  cover_letter?: string;
  executive_summary?: string;
  scope_of_works?: string;
  methodology?: string;
  recommendations?: string;
  company_terms?: string;
  disclaimer?: string;
}

// Escape user-entered text so paragraphs render safely as HTML.
export const escapeHtml = (s: string): string =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// Iter-163s — Same as escapeHtml, but converts newlines into <br/>
// tags so multi-line inspector notes (External Observations +
// Inspector Notes combined) render as two visible lines on the
// PDF instead of collapsing into one long paragraph.
const escapeHtmlWithBreaks = (s: string): string =>
  escapeHtml(s).replace(/\r?\n/g, '<br/>');

// Iter-221 — Normalise a door's apartment/floor into one clean label for
// reports. Source data is messy: `floor` sometimes already reads
// "Ground Floor" / "First Floor" (must NOT become "Floor Ground Floor"), or
// it holds an apartment label like "Apartment 5" (route it to the Apt side,
// not the Floor side — avoids the nonsensical "Floor Apartment 5"). Returns
// '' when there's nothing to show so callers can add their own placeholder.
// Iter-226 — Ordinal words for numeric floors so a bare "1"/"2" reads as
// "First Floor"/"Second Floor" (matching buildings whose other doors were
// captured as "First Floor", "Second Floor", …). Falls back to "Floor N".
function ordinalFloor(n: number): string {
  const words = [
    'Ground', 'First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth',
    'Seventh', 'Eighth', 'Ninth', 'Tenth', 'Eleventh', 'Twelfth',
    'Thirteenth', 'Fourteenth', 'Fifteenth', 'Sixteenth', 'Seventeenth',
    'Eighteenth', 'Nineteenth', 'Twentieth',
  ];
  if (n === 0) return 'Ground Floor';
  if (n >= 1 && n < words.length) return `${words[n]} Floor`;
  return `Floor ${n}`;
}

// Iter-226 — Normalise a raw floor value into a clean human label. Handles
// the messy real-world data: "G"/"GF"/"Ground" → "Ground Floor" (never the
// nonsensical "Floor G" / "Floor Ground"), values that already read as a
// floor are left as-is, and bare numbers become ordinal floors. Returns ''
// when there's nothing to show.
function normaliseFloorLabel(raw: any): string {
  const f = (raw ?? '').toString().trim();
  if (!f) return '';
  const low = f.toLowerCase().replace(/[._\-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (['g', 'gf', 'grd', 'grnd', 'ground', 'ground floor', 'g floor', 'floor g', 'floor ground'].includes(low)) return 'Ground Floor';
  if (['b', 'basement', 'floor b'].includes(low)) return 'Basement';
  if (['lg', 'lgf', 'lower ground', 'lower ground floor'].includes(low)) return 'Lower Ground Floor';
  if (['m', 'mezz', 'mezzanine'].includes(low)) return 'Mezzanine';
  // Already reads as a floor label (e.g. "First Floor", "Ground Floor").
  if (/\bfloor\b/i.test(f)) return f;
  const ordWords: Record<string, number> = {
    first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6,
    seventh: 7, eighth: 8, ninth: 9, tenth: 10,
  };
  if (ordWords[low] != null) return ordinalFloor(ordWords[low]);
  const numMatch = low.match(/^(\d+)(?:st|nd|rd|th)?$/);
  if (numMatch) return ordinalFloor(parseInt(numMatch[1], 10));
  return `Floor ${f}`;
}

// Iter-227 — Best-effort apartment number for a door that has no explicit
// apartment_number/floor. Only fires for apartment/flat entrance doors and
// only reuses a number already present in the door's own location text (e.g.
// "Apartment Entrance Door 11" → 11) or reference (e.g. "A11" → 11) — it
// surfaces existing data rather than inventing it, so the Apt/Floor column
// reads consistently instead of a mix of numbers and dashes.
function deriveApartment(door: any): string {
  const ref = (door?.door_reference ?? '').toString().trim();
  const loc = (door?.location ?? '').toString().trim();
  const type = (door?.door_type ?? '').toString().trim();
  const looksApartment = /\b(apartment|flat)\b/i.test(loc) || /\b(apartment|flat)\b/i.test(type);
  if (!looksApartment) return '';
  let m = loc.match(/\b(?:apartment|flat)\s*#?\s*(\d+[A-Za-z]?)\b/i);
  if (!m) m = loc.match(/\bdoor\s*#?\s*(\d+[A-Za-z]?)\b/i);
  if (m) return m[1];
  const rm = ref.match(/(\d+[A-Za-z]?)\s*$/);
  if (rm) return rm[1];
  return '';
}

// Iter-221/226 — Normalise a door's apartment/floor into one clean label for
// reports. Source data is messy: `floor` sometimes already reads
// "Ground Floor" / "First Floor" (must NOT become "Floor Ground Floor"),
// holds a bare code like "G" (→ "Ground Floor", not "Floor G"), or carries
// an apartment label like "Apartment 5" (route it to the Apt side, not the
// Floor side — avoids the nonsensical "Floor Apartment 5"). Returns '' when
// there's nothing to show so callers can add their own placeholder.
function fmtAptFloor(door: any): string {
  const rawApt = (door?.apartment_number ?? '').toString().trim();
  let rawFloor = (door?.floor ?? '').toString().trim();
  const bits: string[] = [];
  let aptVal = rawApt;
  if (/\b(apartment|apt|flat)\b/i.test(rawFloor)) {
    // The floor field actually carries an apartment label — lift it out.
    const lifted = rawFloor.replace(/\b(apartment|apt|flat)\b/gi, '').replace(/[·\-]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!aptVal) aptVal = lifted || rawFloor;
    rawFloor = '';
  }
  if (aptVal) bits.push(/\b(apartment|apt|flat)\b/i.test(aptVal) ? aptVal : `Apt ${aptVal}`);
  const floorLabel = normaliseFloorLabel(rawFloor);
  if (floorLabel) bits.push(floorLabel);
  // Iter-227 — nothing captured explicitly: surface the apartment number
  // already implied by the location/reference for apartment entrance doors.
  if (!bits.length) {
    const derived = deriveApartment(door);
    if (derived) bits.push(`Apt ${derived}`);
  }
  return bits.join(' · ');
}

// ─────────────────────────────────────────────────────────────────────
// Iter-183 — "Additional Questions" block. Company-defined questions
// answered on the inspection form. Answers may be Yes/No · Pass/Fail
// (optionally with a note + photos), free text, a number, or photos.
// `compact` is the mini-card variant used inside the building report;
// the default is the full single-door layout. Returns '' when the
// inspection carries no answers so callers can drop it in unconditionally.
// ─────────────────────────────────────────────────────────────────────
const renderCustomAnswersHtml = (inspection: any, compact = false): string => {
  const rows = normaliseCustomAnswers(inspection);
  if (!rows.length) return '';
  const fs = compact ? '10px' : '10.5pt';
  const thumb = compact ? '26mm' : '38mm';
  const pill = (r: { value: string; negative: boolean; positive: boolean }) => {
    if (!r.value) return '';
    const bg = r.negative ? '#FEE2E2' : r.positive ? '#D1FAE5' : '#F3F4F6';
    const fg = r.negative ? '#B91C1C' : r.positive ? '#065F46' : '#374151';
    return `<span style="display:inline-block; padding:1px 8px; border-radius:10px; background:${bg}; color:${fg}; font-weight:800; font-size:${fs};">${escapeHtml(r.value)}</span>`;
  };
  const body = rows.map((r) => `
    <div style="padding:${compact ? '4px 0' : '6px 0'}; border-bottom:1px solid #F3F4F6; page-break-inside:avoid;">
      <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:8px;">
        <div style="font-size:${fs}; font-weight:700; color:#1F2937; flex:1;">${escapeHtml(r.label)}</div>
        ${pill(r)}
      </div>
      ${r.note ? `<div style="font-size:${fs}; color:#4B5563; margin-top:2px; line-height:1.4;">${escapeHtmlWithBreaks(r.note)}</div>` : ''}
      ${r.photos.length ? `
        <div style="display:flex; flex-wrap:wrap; gap:2mm; margin-top:3px;">
          ${r.photos.map((p) => {
            const cap = captionFor(inspection?.photo_captions, p);
            return `<div style="width:${thumb};"><img src="${p}" style="width:${thumb}; height:${thumb}; object-fit:cover; border-radius:3px; border:1px solid #E5E7EB;" />${cap ? `<div style="font-size:${compact ? '9px' : '8.5pt'}; color:#4B5563; margin-top:2px; line-height:1.25;">${escapeHtml(cap)}</div>` : ''}</div>`;
          }).join('')}
        </div>` : ''}
    </div>`).join('');
  if (compact) {
    return `
      <div style="margin-top:8px; padding:6px 10px; background:#F8FAFC; border-left:3px solid #64748B; border-radius:4px;">
        <div style="font-size:10px; font-weight:700; color:#334155; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:2px;">Additional Questions</div>
        ${body}
      </div>`;
  }
  return `
    <div class="section-title">Additional Questions</div>
    <div style="padding:0 4px 6px 4px;">${body}</div>`;
};

// ─────────────────────────────────────────────────────────────────────
// Iter-172 — quote lines are presented PER DOOR: each door lists the
// jobs to be done (no price against a job) and carries one door total.
// These two helpers are shared by the generic layout and the RM York
// layout so both bucket and word the lines the same way.
// ─────────────────────────────────────────────────────────────────────

// A quote line's door key. Modern lines carry door_reference / door_id;
// legacy and imported lines only name the door in the description
// ("Door: LG-01 — Ground floor · hinges").
// Iter-194 — shared resolver (explicit door → "Door:" prefix → the text
// names a door already on the quote), so a line like "Door must come off
// frame to carry out works — RAF-FIRST-STAI-004" sits under that door
// instead of "Additional Works".
const quoteLineDoorKey = (it: any, refs?: string[]): string => quoteItemDoorKey(it, refs);

// The job wording shown under a door heading: drops the internal
// prefixes, the door reference the heading already shows, and the
// "×2 (both leaves)" tail (the quantity is rendered separately).
const cleanJobText = (text: any, ref?: string): string => {
  let d = String(text || '')
    .replace(/^\s*Door:\s*[^·]*(·\s*)?/i, '')
    .replace(/^\s*Repair:\s*/i, '')
    .replace(/^\s*⚠\s*Unmatched\s*[\u2014-]\s*/i, '')
    .replace(/[·\u2014-]?\s*×\s*\d+\s*\(both leaves\)\s*$/i, '')
    .trim();
  if (ref) {
    const safeRef = ref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    d = d.replace(new RegExp('[\\u2014-]\\s*' + safeRef + '\\s*$', 'i'), '').trim();
  }
  return d.replace(/[·\u2014-]\s*$/, '').trim();
};


// Iter-173 — "Door photos on quote". The per-door failure photo comes
// from GET /api/quotes/{id}/door-photos and is keyed by BOTH
// door_reference and door_id so it matches whichever key the quote's
// line items carry (see quoteLineDoorKey above).
type QuoteDoorPhoto = {
  door_id?: string | null;
  door_reference?: string | null;
  src: string;
  caption?: string | null;
  source?: string | null;
};

const buildDoorPhotoMap = (list?: QuoteDoorPhoto[] | null): Record<string, QuoteDoorPhoto> => {
  const map: Record<string, QuoteDoorPhoto> = {};
  (Array.isArray(list) ? list : []).forEach((p) => {
    if (!p || !p.src) return;
    if (p.door_reference) map[String(p.door_reference)] = p;
    if (p.door_id) map[String(p.door_id)] = p;
  });
  return map;
};

// Medium thumbnail (default 45mm wide, 4:3) with the failure caption
// underneath. `object-fit: cover` keeps every door photo the same size
// so the quote reads as a tidy column of evidence.
const doorPhotoBlock = (p: QuoteDoorPhoto | undefined, widthMm = 45): string => {
  if (!p || !p.src) return '';
  const h = Math.round(widthMm * 0.75);
  return `
    <div style="width:${widthMm}mm;">
      <img src="${p.src}" style="width:${widthMm}mm; height:${h}mm; object-fit:cover; display:block; border-radius:4px; border:1px solid #E5E7EB; background:#FAFAFA;" />
      ${p.caption ? `<div style="font-size:8pt; color:#6B7280; margin-top:2px; line-height:1.25;">${escapeHtml(String(p.caption))}</div>` : ''}
    </div>`;
};


// Iter-174 — optional "Door photographs" appendix at the back of a
// quote. Renders the SAME one photo per door that prints beside each
// door, but large (two per row at 82mm) so a client querying a
// specific door can see the defect properly. Off unless the user turns
// it on for that quote.
/**
 * Iter-193 — "Replacement door — scope of works". Printed ONCE on any quote
 * that carries a door-replacement line so the client sees exactly what the
 * price allows for. Wording comes from the company quote template
 * (`quote_replacement_scope`) or the standard list.
 */
const renderReplacementScopeHtml = (quote: any, maintenanceCompany: any, accent: string = '#ED1C24'): string => {
  if (!quoteHasReplacement(quote)) return '';
  const lines = replacementScopeLines(maintenanceCompany);
  if (!lines.length) return '';
  const refs = replacementDoorRefs(quote);
  const applies = refs.length
    ? `Applies to: ${refs.map((r) => `<strong>${escapeHtml(r)}</strong>`).join(', ')}`
    : '';
  const rows = lines.map((line) => {
    const { lead, rest } = splitScopeLine(line);
    const isNote = line.trim().startsWith('*');
    const body = lead
      ? `<strong>${escapeHtml(lead)}</strong> &ndash; ${escapeHtml(rest)}`
      : escapeHtml(rest);
    return isNote
      ? `<div style="margin:6px 0 0 0; padding:6px 9px; background:#FFFBEB; border-radius:5px; font-size:9.5pt; color:#92400E; line-height:1.45;">${escapeHtml(line.replace(/^\*\s*/, ''))}</div>`
      : `<div style="display:flex; gap:8px; margin:0 0 5px 0; line-height:1.45; font-size:10pt; color:#1f2937;"><span style="color:${accent}; font-weight:800;">&bull;</span><span style="flex:1; text-align:justify;">${body}</span></div>`;
  }).join('');
  return `
    <div style="margin-top:18px; padding:14px 16px; border:1px solid #E5E7EB; border-left:4px solid ${accent}; border-radius:6px; background:#FFFFFF; page-break-inside:avoid;">
      <div style="font-weight:800; font-size:12.5pt; color:#1a1a1a; margin-bottom:${applies ? 2 : 8}px;">Replacement door &mdash; scope of works</div>
      ${applies ? `<div style="font-size:9.5pt; color:#6B7280; margin-bottom:8px;">${applies}</div>` : ''}
      ${rows}
    </div>`;
};

const renderQuotePhotoAppendix = (
  photos: QuoteDoorPhoto[] | null | undefined,
  doorLocations: Record<string, string> = {},
): string => {
  const list = (Array.isArray(photos) ? photos : []).filter((p) => p && p.src);
  if (!list.length) return '';
  const cells = list.map((p) => {
    const ref = String(p.door_reference || p.door_id || '');
    const loc = doorLocations[ref];
    return `
      <td style="border:none; padding:0 6px 16px 6px; width:50%; vertical-align:top;">
        <div style="font-size:11pt; font-weight:800; color:#1a1a1a; margin-bottom:4px;">
          Door ${escapeHtml(ref)}${loc ? `<span style="font-weight:600; color:#6B7280;"> &middot; ${escapeHtml(String(loc))}</span>` : ''}
        </div>
        <img src="${p.src}" style="width:82mm; height:62mm; object-fit:cover; display:block; border-radius:5px; border:1px solid #E5E7EB; background:#FAFAFA;" />
        ${p.caption ? `<div style="font-size:9pt; color:#6B7280; margin-top:3px;">${escapeHtml(String(p.caption))}</div>` : ''}
      </td>`;
  });
  const rows: string[] = [];
  for (let i = 0; i < cells.length; i += 2) {
    rows.push(`<tr style="page-break-inside:avoid;">${cells[i]}${cells[i + 1] || '<td style="border:none; padding:0;"></td>'}</tr>`);
  }
  return `
    <div style="page-break-before: always;"></div>
    <div style="margin-top: 18px;">
      <div style="background:#1a1a1a; color:#fff; padding:8px 12px; font-weight:800; font-size:12.5pt; border-radius:4px; margin-bottom:10px;">Appendix &mdash; Door photographs</div>
      <p style="color:#475569; font-size:10.5pt; line-height:1.5; margin:0 0 12px 0;">
        Larger copies of the photographs shown against each door, so any door you want to look at
        more closely can be seen clearly. All photographs were taken on site during the fire door
        inspection.
      </p>
      <table style="width:100%; border-collapse:collapse; margin:0;">${rows.join('')}</table>
    </div>`;
};

// Map door_reference → door_location from a quote's line items, used to
// label the photo appendix.
const quoteDoorLocations = (quote: any): Record<string, string> => {
  const map: Record<string, string> = {};
  const _refs = knownDoorRefs(Array.isArray(quote?.items) ? quote.items : []);
  (Array.isArray(quote?.items) ? quote.items : []).forEach((it: any) => {
    const ref = quoteLineDoorKey(it, _refs);
    if (ref && it?.door_location && !map[ref]) map[ref] = String(it.door_location);
    if (it?.door_id && it?.door_location && !map[String(it.door_id)]) {
      map[String(it.door_id)] = String(it.door_location);
    }
  });
  return map;
};


// Convert a plain-text block (with blank-line paragraphs) into HTML.
const toParagraphs = (txt?: string): string => {
  if (!txt || !txt.trim()) return '';
  return txt
    .split(/\n\s*\n/)
    .map((p) => `<p style="margin: 0 0 8px 0; white-space: pre-wrap;">${escapeHtml(p.trim())}</p>`)
    .join('');
};

const renderSection = (title: string, body?: string): string => {
  if (!body || !body.trim()) return '';
  return `
    <div class="section-title avoid-break">${title}</div>
    <div class="custom-block avoid-break">${toParagraphs(body)}</div>
  `;
};

// Sections that go near the top of the report (before data tables)
const renderTopSections = (s?: CustomSections): string => {
  if (!s) return '';
  // Iter-108g — Cover Letter intentionally EXCLUDED here. The building
  // report already renders a hardcoded, client-facing cover letter as
  // the top section; adding the template one below it was producing a
  // duplicate cover letter. Everything else (Exec Summary / Scope /
  // Methodology) still flows through.
  return (
    renderSection('Executive Summary', s.executive_summary) +
    renderSection('Scope of Works', s.scope_of_works) +
    renderSection('Methodology', s.methodology)
  );
};

// Sections that go near the bottom (after data tables)
const renderBottomSections = (s?: CustomSections): string => {
  if (!s) return '';
  return (
    renderSection('Recommendations', s.recommendations) +
    renderSection('Company Terms', s.company_terms) +
    renderSection('Disclaimer', s.disclaimer)
  );
};

/**
 * Parse the `notes` blob saved on each failed_component_photos entry
 * (`• reason 1\n• reason 2\n• Other free text`) back into a list of strings
 * and render them as red chip pills. Mirrors what the inspector saw in the
 * failure-reasons picker so the printed PDF is now self-describing.
 *
 * @param notes      original notes blob from failed_component_photos
 * @param hideLetterboxFields  when true, drop the "Size of opening:" and
 *                             "Reason for failure:" bullets so they aren't
 *                             rendered twice (they get the emphasised block).
 */
const renderReasonChips = (notes?: string, hideLetterboxFields = false, fallbackKey?: string): string => {
  // Iter-110ak — Fallback: when the inspector didn't tick any specific
  // reason (notes empty) we now render the canonical "Fail (if ✗)"
  // text from `FAIL_TEXT_BY_KEY` so the red card in the "Failed
  // Components — Reasons" block always has something under the
  // question. Prevents the "just a question, no chip" cards Alex
  // spotted on the Darlington report.
  let reasonsSrc: string = (notes || '').trim();
  if (!reasonsSrc && fallbackKey) {
    reasonsSrc = failTextFor(fallbackKey) || '';
  }
  if (!reasonsSrc) return '';
  let reasons = reasonsSrc
    .split(/\n/)
    .map((r) => r.replace(/^\s*[\u2022*\-]\s*/, '').trim())
    .filter(Boolean);
  if (hideLetterboxFields) {
    reasons = reasons.filter(
      (r) => !/^size of opening\s*:/i.test(r) && !/^reason for failure\s*:/i.test(r),
    );
  }
  if (reasons.length === 0) return '';
  return `
    <div style="display:flex; flex-direction:column; gap:4px; margin-top:6px;">
      ${reasons.map((r) => `
        <div style="display:block; background:#FEE2E2; color:#991B1B; border:1px solid #FCA5A5; border-left:3px solid #DC2626; font-size:9.5px; font-weight:600; padding:6px 10px; border-radius:6px; line-height:1.5; word-break:break-word;">${escapeHtml(r)}</div>
      `).join('')}
    </div>
  `;
};

/**
 * Emphasised "Letterbox Failure" block for the PDF reports — used inside the
 * Failed Components list (and on the building-report mini-card) whenever the
 * inspector recorded that the letterbox does NOT operate correctly. Surfaces
 * the structured "Size of opening" + "Reason for failure" fields above the
 * reason chips so they're impossible to miss.
 */
const renderLetterboxFailureBlock = (checklistData: any): string => {
  if (!checklistData) return '';
  if (checklistData.letterbox_operates_correctly !== 'No') return '';
  const size = (checklistData.letterbox_opening_size || '').toString().trim();
  const reason = (checklistData.letterbox_failure_reason || '').toString().trim();
  if (!size && !reason) return '';
  return `
    <div style="margin-top:6px; padding:8px 10px; background:#FEF2F2; border:1.5px solid #DC2626; border-radius:6px;">
      <div style="display:flex; align-items:center; gap:6px; margin-bottom:6px;">
        <span style="background:#DC2626; color:#fff; font-size:9px; font-weight:800; letter-spacing:0.5px; padding:2px 6px; border-radius:4px;">LETTERBOX FAILURE</span>
      </div>
      ${size ? `
        <div style="font-size:10px; font-weight:700; color:#7F1D1D; text-transform:uppercase; letter-spacing:0.4px;">Size of opening</div>
        <div style="font-size:11px; color:#1a1a1a; margin-bottom:6px;">${escapeHtml(size)}</div>
      ` : ''}
      ${reason ? `
        <div style="font-size:10px; font-weight:700; color:#7F1D1D; text-transform:uppercase; letter-spacing:0.4px;">Reason for failure</div>
        <div style="font-size:11px; color:#1a1a1a;">${escapeHtml(reason)}</div>
      ` : ''}
    </div>
  `;
};

/**
 * Cross-platform "print HTML to a PDF / printable artifact" helper.
 *
 * Native (iOS/Android): uses expo-print to write a real PDF file and returns
 * its `file://` URI \u2014 same behaviour as before.
 *
 * Web: expo-print is unsupported. We instead create a Blob URL containing the
 * HTML so the browser can open it in a new tab; the user then uses the
 * browser's native "Print \u2192 Save as PDF" dialog. The Blob URL is returned so
 * callers can reuse it (e.g. open / share / iframe).
 */
/**
 * Convert an HTML string into a PDF file on native OR a downloadable
 * Blob URL on web.
 *
 * Iter-108e — `filename` is now honoured on BOTH platforms so the
 * saved PDF carries a human-friendly name (e.g. "Elm Court - Apt 12
 * inspection 2026-06-10") instead of a random UUID:
 *   • Web: we inject a <title> element into the HTML head. The browser
 *     uses that title as the default "Save as PDF" filename when the
 *     user hits Print → Save. We also try a hidden anchor <a download>
 *     dance which some browsers respect for the file save prompt.
 *   • Native: expo-print picks a UUID by default. We copy the file to
 *     the cache directory under our friendly name before returning so
 *     Sharing.shareAsync surfaces the right filename to Files / Mail
 *     / WhatsApp / etc.
 *
 * `filename` is the desired stem (no extension); we sanitise it to
 * strip characters that are unsafe on Windows / iOS Files / Android.
 */
const sanitiseFilename = (raw: string): string => {
  return String(raw || 'fdcheck-report')
    .replace(/[/\\?%*:|"<>]/g, '')     // characters no filesystem tolerates
    .replace(/\s+/g, ' ')              // collapse whitespace
    .trim()
    .slice(0, 120)                     // keep it sensible (Windows path limit)
    || 'fdcheck-report';
};

// Iter-250 — render a PDF via the backend BACKGROUND-JOB endpoint so a single
// request never hits the gateway/request timeout on low-CPU deploys (large
// 100+ door building reports). Starts the job, then polls a tiny status
// endpoint until the PDF is ready. Falls back to the synchronous endpoint if
// the async route isn't available (older backend). Returns base64 PDF or null.
const _renderPdfViaJob = async (
  html: string,
  fileName: string,
  saveMeta?: Record<string, any> | null,
): Promise<{ pdf: string; savedReportId: string | null } | null> => {
  const body = { html, file_name: fileName, save: saveMeta || null };
  let jobId: string | null = null;
  try {
    const start = await api.post(
      '/reports/render-html-to-pdf-async',
      body,
      { timeout: 30_000 },
    );
    jobId = start?.data?.job_id || null;
  } catch {
    jobId = null; // async route unavailable — fall through to sync below
  }
  if (jobId) {
    try { delete (globalThis as any).__fdcheck_render_backgrounded; } catch { /* ignore */ }
    const deadline = Date.now() + 300_000; // allow up to 5 min of rendering
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2000));
      let st: any;
      try {
        st = await api.get(`/reports/render-job/${jobId}`, { timeout: 30_000 });
      } catch {
        continue; // transient poll error — keep polling until the deadline
      }
      const status = st?.data?.status;
      if (status === 'done') {
        const savedId = st?.data?.saved_report_id || null;
        let pdf = st?.data?.pdf_base64 || null;
        // Large reports aren't inlined in the job response (would exceed
        // Mongo's 16 MB doc limit) — they're saved straight to the library,
        // so pull the bytes from there using the returned saved_report_id.
        if (!pdf && savedId) {
          try {
            const dl = await api.get(`/saved-reports/${savedId}/download`, { timeout: 120_000 });
            pdf = dl?.data?.pdf_base64 || null;
          } catch { /* fall through */ }
        }
        if (pdf) return { pdf, savedReportId: savedId };
        // Saved server-side but we couldn't fetch the bytes — tell the user
        // to open it from Reports rather than dead-ending on an HTML preview.
        if (savedId) { try { (globalThis as any).__fdcheck_render_backgrounded = true; } catch { /* ignore */ } }
        return null;
      }
      if (status === 'error') throw new Error(`PDF render failed: ${st?.data?.error || 'unknown'}`);
    }
    // The render keeps running server-side (and self-saves because save
    // meta was supplied) even though we stop waiting for the bytes here.
    try { (globalThis as any).__fdcheck_render_backgrounded = true; } catch { /* ignore */ }
    throw new Error('PDF render still finishing in the background');
  }
  // Fallback: synchronous render (small reports / older backend).
  const resp = await api.post(
    '/reports/render-html-to-pdf',
    body,
    { timeout: 120_000 },
  );
  const pdf = resp?.data?.pdf_base64 || null;
  return pdf ? { pdf, savedReportId: resp?.data?.saved_report_id || null } : null;
};

// Iter-266 — free-trial reports are VIEW-ONLY. When the backend blocks the PDF
// render with 402 trial_view_only, we wrap the already-built report HTML in a
// framed, print-discouraged preview (sticky banner + an "Upgrade" CTA) so the
// trial user can still READ the report on screen but gets no downloadable /
// printable PDF artifact.
const makeViewOnlyPreview = (html: string, detail?: string): string => {
  const msg = detail || 'Reports are view-only during your free trial. Subscribe to download, print and share PDF reports.';
  let origin = '';
  try { origin = (globalThis as any).location?.origin || ''; } catch { origin = ''; }
  const banner = `<div class="fdc-trial-banner" style="position:sticky;top:0;z-index:99999;background:#FEF3C7;border-bottom:2px solid #F59E0B;padding:10px 16px;font-family:system-ui,-apple-system,sans-serif;font-size:13px;line-height:1.4;color:#92400E;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;"><span><strong>Preview only</strong> — ${msg}</span><a href="${origin}/settings?tab=subscription" style="background:#F59E0B;color:#fff;padding:6px 14px;border-radius:999px;text-decoration:none;font-weight:700;white-space:nowrap;">View plans</a></div>`;
  const printBlock = `<style>@media print{html,body{visibility:hidden!important}html::before{content:"FD Check — subscribe to download or print this report";visibility:visible;display:block;padding:48px 24px;font:700 18px system-ui,sans-serif;color:#111;text-align:center}}</style>`;
  let out = html;
  out = /<\/head>/i.test(out) ? out.replace(/<\/head>/i, `${printBlock}</head>`) : printBlock + out;
  out = /<body[^>]*>/i.test(out) ? out.replace(/<body[^>]*>/i, (m) => `${m}${banner}`) : banner + out;
  return out;
};


export const htmlToPdf = async (html: string, filename = 'fdcheck-report'): Promise<string> => {
  const safeName = sanitiseFilename(filename);

  // Iter-251 — read-once save metadata that webReports stashes right
  // before invoking a report generator. When present, the backend render
  // persists the PDF to the saved-reports library itself, so the report
  // survives even if this browser's poll times out on a huge render.
  let saveMeta: Record<string, any> | null = null;
  try {
    const g = globalThis as any;
    if (g.__fdcheck_pending_save_meta) {
      saveMeta = g.__fdcheck_pending_save_meta;
      delete g.__fdcheck_pending_save_meta;
    }
  } catch { /* ignore */ }

  {
    // Iter-108w — try to render a REAL PDF server-side via WeasyPrint
    // so (a) the user sees a proper PDF preview and (b) the caller can
    // pipe the same bytes into saveToBackend(). If the render call
    // fails (offline, backend down, unauthenticated) we fall back to
    // the old "open HTML in a new tab" behaviour so the user isn't
    // dead-ended.
    const titleTag = `<title>${safeName}</title>`;
    const withTitle = /<title>[\s\S]*?<\/title>/i.test(html)
      ? html.replace(/<title>[\s\S]*?<\/title>/i, titleTag)
      : html.replace(/<head[^>]*>/i, (m) => `${m}\n${titleTag}`);

    try {
      const rendered = await _renderPdfViaJob(withTitle, safeName, saveMeta);
      if (rendered && rendered.pdf) {
        const pdfB64 = rendered.pdf;
        // Convert base64 → Uint8Array → Blob → object URL.
        const byteChars = atob(pdfB64);
        const byteNums = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
        const byteArr = new Uint8Array(byteNums);
        const blob = new Blob([byteArr], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        // Cache the base64 bytes (and any server-side saved_report_id) on
        // the URL so saveToBackend can re-use them without re-rendering.
        try { (globalThis as any).__fdcheck_pdf_cache = (globalThis as any).__fdcheck_pdf_cache || {}; (globalThis as any).__fdcheck_pdf_cache[url] = { pdf_base64: pdfB64, file_name: safeName, saved_report_id: rendered.savedReportId || null }; } catch { /* ignore */ }
        try { (globalThis as any).window?.open?.(url, '_blank'); } catch { /* ignore */ }
        return url;
      }
    } catch (e) {
      const r = (e as any)?.response;
      if (r?.status === 402 && r?.data?.code === 'trial_view_only') {
        // Free-trial: show the report as a framed, print-discouraged preview
        // (no PDF download / save) instead of the generic fallback.
        const previewHtml = makeViewOnlyPreview(withTitle, r?.data?.detail);
        const blob = new Blob([previewHtml], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        try { (globalThis as any).__fdcheck_view_only_preview = true; } catch { /* ignore */ }
        try { (globalThis as any).window?.open?.(url, '_blank'); } catch { /* ignore */ }
        return url;
      }
      console.warn('Server-side HTML→PDF render failed, falling back to HTML preview:', e);
    }

    // Fallback: open the HTML directly so the user can still preview /
    // print. Saving to backend is skipped for this branch.
    const blob = new Blob([withTitle], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    try { (globalThis as any).window?.open?.(url, '_blank'); } catch { /* ignore */ }
    return url;
  }
};

// ===== Standard wording (from FD Check Ltd report template) =====
const FD_OFFICE_LINE =
  'FD Check Ltd · 63-65 Heworth Road, York YO31 0AA · Tel 07878 899537 · Email Dave@fdcheck.ltd · www.fdcheck.com';

// Iter-108s — Associated Services block appended to every FD Check
// inspection/building/external report. Points customers at RM York Ltd
// for remedial works, keeping FD Check purely inspection-focused.
const ASSOCIATED_SERVICES_HTML = `
  <div style="margin-top: 22px; padding: 14px 16px; border: 1px solid #E5E7EB; border-left: 4px solid #ED1C24; border-radius: 6px; background: #FAFAFA; font-size: 10.5pt; color: #1f2937; line-height: 1.5; text-align: left;">
    <div style="font-weight: 800; font-size: 12pt; color: #ED1C24; margin-bottom: 6px;">Associated Services</div>
    <p style="margin: 0 0 8px 0;">Issued by <strong>FD Check Ltd</strong> &mdash; Fire Door Inspections &amp; Compliance.</p>
    <p style="margin: 0 0 8px 0;">Remedial repairs, adjustments, and replacements can be arranged via our trusted partner: <strong>RM York Ltd</strong></p>
    <p style="margin: 0 0 8px 0; color: #4B5563;">Fire Door Repair Specialists &middot; Fully compliant with UK standards and regulations.</p>
    <div style="margin-top: 6px;">
      <strong>Contact for quotes:</strong><br/>
      Tel: Alex <a href="tel:07824513332" style="color: #1f2937; text-decoration: none;">07824 513332</a><br/>
      Email: <a href="mailto:Alex@rmyork.com" style="color: #1f2937; text-decoration: none;">Alex@rmyork.com</a><br/>
      Web: <a href="https://www.rmyork.com" style="color: #1f2937; text-decoration: none;">www.RMYork.com</a>
    </div>
  </div>
`;

// Iter-226 — Remediation timescale KEY. The Remedial Recommendations and
// Remedial Action Schedule no longer print a fixed per-item deadline (that
// was unrealistic — remedial works are quoted, approved by the managing
// agent / owner, then scheduled with a contractor). Each action carries a
// risk LEVEL only; this key explains the ideal, real-world timescale for
// each level, measured from the date the works are approved.
const REMEDIAL_TIMESCALE_KEY_HTML = `
  <div class="avoid-break" style="margin-top:12px; padding:12px 14px; border:1px solid #E5E7EB; border-left:4px solid #111827; border-radius:6px; background:#FAFAFA;">
    <div style="font-weight:800; font-size:12px; color:#111827; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:5px;">Recommended remediation timescales &mdash; key</div>
    <p style="font-size:10.5px; color:#4B5563; line-height:1.55; margin:0 0 10px 0;">
      Each action is graded by risk rather than tied to a fixed completion date. In practice most remedial works must be
      quoted, approved by the managing agent and/or building owner, and then scheduled with a certified fire-door contractor.
      The targets below therefore run <strong>from the date the works are approved</strong> and assume reasonable interim
      safety measures are maintained in the meantime. Certified doorsets, frames and fire-rated glazing are manufactured to
      order, so where a like-for-like item cannot be sourced quickly the interim measures should remain in place until it is installed.
    </p>
    <div style="display:flex; flex-direction:column; gap:6px;">
      <div style="display:flex; gap:8px; align-items:flex-start;">
        <span style="flex:0 0 auto; min-width:66px; text-align:center; padding:2px 8px; border-radius:999px; background:#FEE2E2; color:#7F1D1D; font-size:9.5px; font-weight:800; letter-spacing:0.3px; text-transform:uppercase;">Critical</span>
        <span style="flex:1; font-size:10.5px; color:#1a1a1a; line-height:1.5;">Immediate interim safety measures on the day of inspection (e.g. temporary signage, more frequent fire-warden checks). Permanent works prioritised and completed as soon as materials and access allow &mdash; typically within <strong>4 weeks of approval</strong>.</span>
      </div>
      <div style="display:flex; gap:8px; align-items:flex-start;">
        <span style="flex:0 0 auto; min-width:66px; text-align:center; padding:2px 8px; border-radius:999px; background:#FEE2E2; color:#991B1B; font-size:9.5px; font-weight:800; letter-spacing:0.3px; text-transform:uppercase;">High</span>
        <span style="flex:1; font-size:10.5px; color:#1a1a1a; line-height:1.5;">Address as a priority, typically within <strong>4 to 8 weeks of approval</strong>.</span>
      </div>
      <div style="display:flex; gap:8px; align-items:flex-start;">
        <span style="flex:0 0 auto; min-width:66px; text-align:center; padding:2px 8px; border-radius:999px; background:#FEF3C7; color:#92400E; font-size:9.5px; font-weight:800; letter-spacing:0.3px; text-transform:uppercase;">Medium</span>
        <span style="flex:1; font-size:10.5px; color:#1a1a1a; line-height:1.5;">Complete within <strong>8 to 12 weeks of approval</strong>, or at the next planned maintenance visit.</span>
      </div>
      <div style="display:flex; gap:8px; align-items:flex-start;">
        <span style="flex:0 0 auto; min-width:66px; text-align:center; padding:2px 8px; border-radius:999px; background:#E0F2FE; color:#075985; font-size:9.5px; font-weight:800; letter-spacing:0.3px; text-transform:uppercase;">Low</span>
        <span style="flex:1; font-size:10.5px; color:#1a1a1a; line-height:1.5;">Address at the <strong>next planned maintenance cycle</strong> &mdash; no separate visit required.</span>
      </div>
    </div>
  </div>
`;

// Removed: REPORT_INTRODUCTION — replaced by the client-facing cover letter
// rendered inline in generateBuildingReport (see "Cover Letter" section).
// Keeping this comment as a breadcrumb so we don't accidentally re-add a
// second short intro paragraph that duplicates the cover letter.

// Iter-163p — Multi-attempt no-access history.
// Since every no-access visit already creates an inspection row with
// `status='no_access'` and its own `inspection_date`, we can derive
// the attempt log for a door for free by grouping historical
// inspections — no schema change required. Sorted oldest-first so
// the cover sheet reads chronologically.
type NoAccessAttempt = {
  date: string;              // ISO string from inspection_date
  inspector: string;         // inspector_name at the time
  note: string;              // cd.no_access_note (or inspection.notes as fallback)
};

const collectNoAccessAttempts = (
  doorId: string | undefined,
  inspections: any[],
): NoAccessAttempt[] => {
  if (!doorId) return [];
  const attempts: NoAccessAttempt[] = [];
  for (const insp of inspections || []) {
    if (!insp || insp.door_id !== doorId) continue;
    const isNoAcc = insp.status === 'no_access' || insp?.checklist_data?.no_access === true;
    if (!isNoAcc) continue;
    // Iter-163s — Combine "External Observations" (no_access_note)
    // with the separate "Inspector Notes" field when both are set,
    // so nothing entered on site is lost on the PDF. Deduped if
    // the two happen to be identical (some historic inspections
    // mirrored the same string into both fields).
    const extObs = String(insp?.checklist_data?.no_access_note || '').trim();
    const inspNotes = String(insp?.notes || '').trim();
    const note = (extObs && inspNotes && extObs !== inspNotes)
      ? `${extObs}\n${inspNotes}`
      : (extObs || inspNotes);
    attempts.push({
      date: insp.inspection_date,
      inspector: insp.inspector_name || '—',
      note,
    });
  }
  attempts.sort(
    (a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime(),
  );
  return attempts;
};

// Points of failure — 14 components explained per the FD Check template
const POINTS_OF_FAILURE: { title: string; body: string }[] = [
  {
    title: 'Door Frame',
    body: 'The frame should be firmly fixed, in good condition, and free from distortion or damage that could affect the fit of the door leaf. Loose fixings, damaged linings, or movement between the frame and surrounding structure can affect fire and smoke performance.',
  },
  {
    title: 'Door Lock and Handle',
    body: 'Locks, latches and lever furniture should be secure, compatible with the door assembly, and operate correctly without binding. Faulty or unsuitable ironmongery can affect self-closing, latching, security, and safe egress.',
  },
  {
    title: 'Door Frame to Door Gap',
    body: 'Gaps at the head and jambs are typically expected to be within 2&nbsp;mm to 4&nbsp;mm unless the manufacturer&rsquo;s certification states otherwise. Excessive, uneven, or inconsistent gaps can reduce smoke control and fire performance, while gaps that are too tight can prevent the door from closing correctly.',
  },
  {
    title: 'Smoke / Fire Seals',
    body: 'Intumescent strips and, where required, cold smoke seals should be present, continuous, correctly fitted, and in good condition. Missing, painted-over, damaged, or poorly fitted seals can significantly reduce the ability of the door assembly to restrict the passage of fire and smoke.',
  },
  {
    title: 'Smoke Seal Test',
    body: 'Where smoke seals are fitted, they should make consistent contact without excessive compression or gaps. Poor contact may indicate wear, distortion, incorrect adjustment, or installation issues that could reduce smoke control.',
  },
  {
    title: 'Door Glass',
    body: 'Any glazing should be fire-resisting where required, correctly installed, and free from damage. Cracked glass, incorrect glazing materials, or unapproved alterations can invalidate the fire performance of the door assembly.',
  },
  {
    title: 'Door and Fire Door Type',
    body: 'The door assembly should be appropriate for its location and supported by suitable identification, certification, or test evidence where available. Damage, excessive modification, or uncertainty about the door&rsquo;s specification may affect whether it can be relied upon to achieve its intended performance.',
  },
  {
    title: 'Glass Beading',
    body: 'Glazing beads and associated components should be secure, undamaged, and suitable for the fire-resisting glazing system fitted. Missing, loose, or damaged beading can affect the integrity of the glazed aperture in fire conditions.',
  },
  {
    title: 'Door Closer',
    body: 'Where a self-closing device is required, it should return the door fully into the frame and allow it to latch positively from any open position permitted by the door&rsquo;s normal operation. A defective or poorly adjusted closer can leave the door ineffective in service.',
  },
  {
    title: 'Door Hinges',
    body: 'Hinges should be suitable for the fire door assembly, securely fixed, and fitted with all required screws. Many timber fire doors use three hinges, but the correct number and specification should follow the manufacturer&rsquo;s instructions or certification evidence.',
  },
  {
    title: 'Signage',
    body: 'Mandatory fire door signage should be present, legible, and appropriate to the function of the door, for example &lsquo;Fire door keep shut&rsquo; or &lsquo;Fire door keep locked shut&rsquo; where applicable. Missing or incorrect signage can affect how the door is used in normal service and in an emergency.',
  },
  {
    title: 'Push / Kick Plates',
    body: 'Where fitted, push plates or kick plates should be secure, suitable for the door assembly, and not interfere with the operation or certification of the door. Oversized, loose, or unapproved items can affect performance and may require review against manufacturer guidance.',
  },
  {
    title: 'Drop-down Seal',
    body: 'Where fitted, a drop-down or threshold smoke seal should operate correctly and provide effective smoke control at the threshold in accordance with the door specification. Damage, poor adjustment, or incompatibility can reduce performance.',
  },
  {
    title: 'Spy Viewer',
    body: 'Where a viewer is fitted, it should be secure, suitable for use in a fire door, and free from damage or unauthorised replacement. Inappropriate or poorly fitted hardware can affect the integrity of the door leaf.',
  },
];

const renderPointsOfFailureHtml = (heading: string = 'Points of Failure', subtitle?: string, pageBreakBefore = false) => `
  <div class="section ${pageBreakBefore ? 'page-break' : ''}">
    <div class="section-title">${heading}</div>
    ${subtitle ? `<p style="font-size:12px;color:#555;margin:-8px 0 14px 0;">${subtitle}</p>` : ''}
    ${POINTS_OF_FAILURE.map(
      (p) => `
        <div class="avoid-break" style="margin-bottom: 10px; padding: 10px 12px; background-color:#fafafa; border-left: 3px solid #ED1C24; border-radius: 4px;">
          <div style="font-weight:700; font-size:13px; color:#1a1a1a; margin-bottom:4px;">${p.title}</div>
          <div style="font-size:11px; color:#333; line-height:1.5;">${p.body}</div>
        </div>
      `
    ).join('')}
  </div>
`;

// ── Iter-110 Phase B2 — Maintenance evidence helpers ─────────────
//
// Normalise every possible photo location an engineer might have used
// during job completion into a single `{ url, caption, phase }[]` shape.
// Phase drives the BEFORE / AFTER pill next to each photo.
type MaintenancePhoto = { url: string; caption: string; phase: string };

const collectMaintenancePhotos = (job: any): MaintenancePhoto[] => {
  const raw: any[] = [];
  if (Array.isArray(job?.completion_photos)) raw.push(...job.completion_photos);
  if (Array.isArray(job?.photos)) raw.push(...job.photos);
  if (Array.isArray(job?.before_photos)) raw.push(...job.before_photos.map((p: any) => ({ ...(typeof p === 'object' ? p : { url: p }), _phase: 'before' })));
  if (Array.isArray(job?.after_photos))  raw.push(...job.after_photos.map((p: any) =>  ({ ...(typeof p === 'object' ? p : { url: p }), _phase: 'after'  })));
  // Legacy emergency-flow single-shot fields.
  if (job?.before_photo) raw.push({ url: job.before_photo, _phase: 'before' });
  if (job?.after_photo)  raw.push({ url: job.after_photo,  _phase: 'after'  });
  // Per-item completion photos (BS 8214 evidence).
  if (Array.isArray(job?.item_completions)) {
    for (const ic of job.item_completions) {
      if (Array.isArray(ic?.photos)) {
        for (const p of ic.photos) {
          if (typeof p === 'string') raw.push({ url: p, caption: ic.component || ic.description || '' });
          else if (p) raw.push({ ...p, caption: p.caption || ic.component || ic.description || '' });
        }
      }
    }
  }

  const out: MaintenancePhoto[] = [];
  for (const p of raw) {
    if (!p) continue;
    if (typeof p === 'string') {
      out.push({ url: p, caption: '', phase: '' });
      continue;
    }
    const url = p.url || p.photo || p.data_uri || p.data_url || p.base64 || null;
    if (!url) continue;
    out.push({
      url: String(url),
      caption: String(p.caption || p.label || p.note || p.notes || ''),
      phase: String(p._phase || p.phase || ''),
    });
  }
  return out;
};

// Render the "Recent Maintenance Evidence" panel for a single door.
// Filters to the last 12 months, dedupes, newest-first, caps at 3 jobs
// and 6 photos per job (spec §4). Each photo gets a BEFORE / AFTER pill
// when the phase field is set.
const renderMaintenanceEvidence = (jobs: any[]): string => {
  if (!Array.isArray(jobs) || jobs.length === 0) return '';
  const twelveMonthsAgo = Date.now() - 365 * 24 * 60 * 60 * 1000;
  const recent = jobs
    .filter((j) => {
      const stamp = new Date(j?.completed_at || j?.updated_at || j?.created_at || 0).getTime();
      return isFinite(stamp) && stamp >= twelveMonthsAgo;
    })
    .sort((a, b) => {
      const ta = new Date(a?.completed_at || a?.updated_at || a?.created_at || 0).getTime();
      const tb = new Date(b?.completed_at || b?.updated_at || b?.created_at || 0).getTime();
      return tb - ta;
    })
    .slice(0, 3);
  if (recent.length === 0) return '';

  return `
    <div style="margin-top:10px; padding:10px 12px; background:#F0FDF4; border:1px solid #BBF7D0; border-left:4px solid #16A34A; border-radius:6px;">
      <div style="font-size:11px; font-weight:800; color:#166534; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:8px;">
        Recent Maintenance Evidence &middot; last 12 months
      </div>
      ${recent.map((job: any) => {
        const status = String(job?.status || '').toLowerCase();
        const isCompleted = status === 'completed';
        const statusBg = isCompleted ? '#DCFCE7' : '#FEF9C3';
        const statusFg = isCompleted ? '#166534' : '#854D0E';
        const statusTxt = isCompleted ? 'COMPLETED' : (status ? status.toUpperCase() : 'IN PROGRESS');
        const stamp = job?.completed_at || job?.updated_at || job?.created_at;
        const dateStr = stamp ? format(new Date(stamp), 'dd/MM/yyyy') : '—';
        const photos = collectMaintenancePhotos(job).slice(0, 6);
        const notes = String(
          job?.completion_notes || job?.resolution_notes || job?.work_done || job?.notes || ''
        ).trim();
        return `
          <div style="margin-bottom: 10px; padding: 8px 10px; background:#fff; border:1px solid #DCFCE7; border-radius:6px;">
            <div style="display:flex; align-items:center; gap:8px; margin-bottom:4px; flex-wrap:wrap;">
              <div style="font-size:12px; font-weight:800; color:#111827; flex:1;">${escapeHtml(job?.title || 'Maintenance job')}</div>
              <span style="display:inline-block; padding:2px 7px; border-radius:999px; background:${statusBg}; color:${statusFg}; font-size:9.5px; font-weight:800; letter-spacing:0.3px;">${statusTxt}</span>
              <span style="font-size:10.5px; color:#6B7280;">${dateStr}</span>
            </div>
            ${notes ? `<div style="font-size:11px; color:#1a1a1a; line-height:1.5; margin: 4px 0 6px 0;">${escapeHtml(notes).slice(0, 400)}${notes.length > 400 ? '…' : ''}</div>` : ''}
            ${photos.length > 0 ? `
              <div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;">
                ${photos.map((p) => {
                  const phaseBg = p.phase === 'before' ? '#FEF3C7' : p.phase === 'after' ? '#DBEAFE' : '';
                  const phaseFg = p.phase === 'before' ? '#92400E' : p.phase === 'after' ? '#1E40AF' : '';
                  const phaseTxt = p.phase === 'before' ? 'BEFORE' : p.phase === 'after' ? 'AFTER' : '';
                  return `
                    <div style="position:relative; width:calc(33% - 4px); border:1px solid #DCFCE7; border-radius:4px; background:#fff; padding:2px;">
                      ${phaseTxt ? `<span style="position:absolute; top:4px; left:4px; padding:1px 6px; border-radius:4px; background:${phaseBg}; color:${phaseFg}; font-size:8.5px; font-weight:800; letter-spacing:0.4px;">${phaseTxt}</span>` : ''}
                      <img src="${p.url}" style="display:block; width:100%; max-height:110px; object-fit:cover; border-radius:3px;" />
                      ${p.caption ? `<div style="font-size:9px; color:#4B5563; padding:2px 4px 0 4px; line-height:1.2;">${escapeHtml(p.caption).slice(0, 80)}</div>` : ''}
                    </div>
                  `;
                }).join('')}
              </div>
            ` : ''}
          </div>
        `;
      }).join('')}
    </div>
  `;
};

// Component Inspection Results grid — all 19 items in a 2-column
// pass/fail/na layout. Renders even for "not inspected" doors (with
// every row as 'n/a') so the doorset audit trail stays complete.
const renderComponentResultsGrid = (inspection: any | null): string => {
  if (!inspection) {
    return `
      <div style="margin-top:10px; padding:10px 12px; background:#F3F4F6; border-radius:6px;">
        <div style="font-size:11px; color:#6B7280; font-style:italic;">Component-level results will appear here once this door has been inspected.</div>
      </div>
    `;
  }
  // Iter-163l — Belt-and-braces guard against the "grid still shows on
  // doorset-replacement doors" recurrence. Even if a NEW caller of
  // this function is added and forgets the `needsReplacement ? '' :
  // renderComponentResultsGrid(inspection)` wrap at the call site,
  // the function itself refuses to render the grid when the doorset
  // is being fully replaced. The individual component passes/fails
  // would contradict the overriding "FULL DOORSET REPLACEMENT"
  // statement below on the same door page.
  if (doorsetNeedsReplacement(inspection)) return '';
  // Iter-163o — Same belt-and-braces for NO ACCESS doors. When
  // nothing was actually inspected (inspector could not enter),
  // rendering the full grid as PASS / N/A misleads the reader
  // into thinking every component was checked and passed. Callers
  // should short-circuit before this function, but this guard makes
  // sure we never render a phantom-passed grid on a no-access door.
  if (inspection?.status === 'no_access' || inspection?.checklist_data?.no_access === true) {
    return '';
  }
  const comps = normaliseComponents(inspection);
  const pill = (s: string) => {
    // Iter-110ak — Added `advisory` state so Low-risk fails (missing
    // cert label, missing signage, spy viewer, letterbox present)
    // render in blue "ADVISORY" rather than red "FAIL". Matches the
    // "1 ADVISORY (NOT A FAILURE)" block below the grid, so the
    // reader never sees the same fact presented two different ways.
    const map: Record<string, { bg: string; fg: string; text: string }> = {
      pass:     { bg: '#D1FAE5', fg: '#065F46', text: 'Satisfactory' },
      fail:     { bg: '#FEE2E2', fg: '#991B1B', text: 'Defect' },
      advisory: { bg: '#DBEAFE', fg: '#1E3A8A', text: 'Advisory' },
      na:       { bg: '#F3F4F6', fg: '#6B7280', text: 'N/A' },
    };
    const p = map[s] || map.na;
    return `<span style="display:inline-block; padding:2px 7px; border-radius:999px; background:${p.bg}; color:${p.fg}; font-size:9px; font-weight:800; letter-spacing:0.4px;">${p.text}</span>`;
  };
  const rows = comps.map((c) => `
    <div style="display:flex; align-items:center; justify-content:space-between; gap:8px; padding:4px 8px; border-bottom:1px solid #F3F4F6;">
      <span style="font-size:11px; color:#1a1a1a; flex:1;">${escapeHtml(c.label)}</span>
      ${pill(c.status)}
    </div>
  `);
  // Split into two roughly-equal columns.
  const half = Math.ceil(rows.length / 2);
  const left = rows.slice(0, half).join('');
  const right = rows.slice(half).join('');
  return `
    <div style="margin-top:10px;">
      <div style="font-size:11px; font-weight:800; color:#374151; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:6px;">Component Inspection Results</div>
      <div style="display:flex; gap:12px; align-items:flex-start;">
        <div style="flex:1; border:1px solid #E5E7EB; border-radius:6px; overflow:hidden;">${left}</div>
        <div style="flex:1; border:1px solid #E5E7EB; border-radius:6px; overflow:hidden;">${right}</div>
      </div>
    </div>
  `;
};

// Compact per-door mini-report for inclusion inside the Building Report PDF.
// Each card is wrapped in .avoid-break so a door never splits across pages.
const renderDoorMiniReport = (
  door: any,
  inspection: any | null,
  options: {
    pageBreakBefore?: boolean;
    maintenanceJobsForDoor?: any[];
    // Iter-229 — Per-door Remedial Action Schedule rows for THIS door,
    // so an apartment owner's extracted page carries its own remedials.
    remedialActionsForDoor?: any[];
    // Iter-163p — Multi-attempt no-access history. Passed by
    // generateBuildingReport so the mini-card can list every prior
    // access attempt (date + inspector + reason) without a schema
    // change. When empty, the no-access panel falls back to the
    // single-attempt "Inspector's notes: …" wording.
    noAccessAttempts?: NoAccessAttempt[];
  } = {},
) => {
  const freqLabel = (f: string) =>
    f === '3_month' ? '3 Months' : f === '6_month' ? '6 Months' : f === '12_month' ? '12 Months' : f || '—';
  const aptFloor = fmtAptFloor(door);

  // Iter-110 Phase B2 — effective-status pill (matches web app).
  // Uses the canonical effectiveStatus() / statusLabel() / statusColor()
  // helpers so a door with only Low-risk / advisory fails renders as
  // PASS WITH OBSERVATIONS in sky-blue, not FAIL in red.
  const effStatus = inspection ? effectiveStatus(inspection) : 'not_inspected';
  const effColour = inspection ? statusColor(effStatus) : '#9CA3AF';
  const effLabel = inspection ? statusLabel(effStatus) : 'Not inspected';
  const inspDate = inspection?.inspection_date
    ? format(new Date(inspection.inspection_date), 'dd/MM/yyyy')
    : '—';

  const failed = ((inspection?.failed_component_photos || []) as { key?: string; question: string; photo?: string; notes?: string; advisory?: boolean; leaf?: string | null }[]).filter((fc: any) => !isSuppressedOptionalFcp(fc));

  // Iter-108g — surface gap measurements + any over-tolerance points on
  // the per-door mini-card. Previously the building report showed only
  // the failed component photos, so a door that only failed on gap
  // measurements looked "clean". Now the reader sees the gap flag and
  // the specific points that exceeded 4 mm.
  const cd = inspection?.checklist_data || {};
  const gapMeasurements = cd.gap_measurements || {};
  const gapKeys = Object.keys(gapMeasurements);
  const gapPositionLabels: Record<string, string> = {
    lock_top: 'Lock top', lock_mid: 'Lock mid', lock_bot: 'Lock bot',
    bot_lock: 'Bottom (lock)', bot_mid: 'Bottom (mid)', bot_hinge: 'Bottom (hinge)',
    hinge_bot: 'Hinge bot', hinge_mid: 'Hinge mid', hinge_top: 'Hinge top',
    top_hinge: 'Top (hinge)', top_mid: 'Top (mid)', top_lock: 'Top (lock)',
  };
  const overTolerancePoints: { label: string; mm: number }[] = [];
  gapKeys.forEach(k => {
    const n = parseFloat(String(gapMeasurements[k] || '0'));
    if (!isNaN(n) && n > 4) {
      overTolerancePoints.push({ label: gapPositionLabels[k] || k, mm: n });
    }
  });
  const gapFailsMini = inspection && cd.gap_within_tolerance === false;

  // Iter-163i — When the doorset is being fully replaced, suppress
  // BOTH the "Component Inspection Results" grid AND the gap-fails
  // block on the mini-card. The single "FULL DOORSET REPLACEMENT"
  // statement below is now the authoritative message for this door
  // — the individual pass/fail component pills contradicted it and
  // made property managers think the door still had salvageable
  // components. (User feedback: "the report is still putting all
  // the questions on the report with passes… if replacement door
  // is selected then on the reports it should ignore all the
  // questions and just put door fails door replacement required.")
  const needsReplacement = doorsetNeedsReplacement(inspection);

  // Iter-163o — When the inspector marked the door "No Access",
  // nothing was actually inspected. Rendering the full Component
  // Inspection Results grid (all rows as PASS / N/A) was misleading
  // — the client saw "all components PASS" for a door where the
  // inspector never even opened it. Instead, show the door photo
  // (already rendered above) + the inspector's on-site notes so the
  // client knows exactly why access was refused.
  const isNoAccess = inspection?.status === 'no_access' || cd?.no_access === true;
  // Iter-163s — Combine both note fields (External Observations +
  // Inspector Notes) when both are populated. See collectNoAccessAttempts
  // helper for the same logic used on the multi-attempt path.
  const _extObs = String(cd?.no_access_note || '').trim();
  const _inspNotes = String(inspection?.notes || '').trim();
  const noAccessNote: string = (_extObs && _inspNotes && _extObs !== _inspNotes)
    ? `${_extObs}\n${_inspNotes}`
    : (_extObs || _inspNotes);

  // Iter-229 — On-site photos captured during a no-access visit (the
  // locked door / access notice / exterior) are the ONLY evidence for
  // that door, so surface them in the no-access panel below. The
  // register stock photo (door.door_photo) is already shown top-right,
  // so here we add the inspection's own label + general photos.
  const noAccessPhotos: string[] = [
    ...(inspection?.label_photo ? [inspection.label_photo] : []),
    ...((inspection?.photos || []) as string[]),
  ].filter(Boolean);

  // Point the reader at wherever the exterior evidence actually is —
  // the register photo top-right, the on-site strip below, or state
  // plainly that none was captured. Avoids "see the photograph above"
  // printing when there is no photo above.
  const noAccessPhotoRef: string = door.door_photo
    ? 'See the door photograph above for the exterior condition captured on site.'
    : (noAccessPhotos.length > 0
        ? 'See the on-site photographs below for the exterior condition captured on site.'
        : 'No exterior photograph was captured on site.');

  // Iter-260 — bring the numbered measurement-point DIAGRAM (added to the
  // single-door PDF in iter-108) onto the Building Report mini-card so a
  // portfolio report shows the same door diagram + gap table per inspected
  // door. Hidden for no-access / replacement / uninspected doors and when
  // no gap readings were recorded.
  const miniIsDouble = isDoubleDoor(door, inspection);
  const miniDropdownSeal = cd.threshold_gap_bypassed_by_seal === true;
  const { rows: miniDiagramRows, threshold: miniThresholdRows } = gapRowsFor({ double: miniIsDouble, dropdownSeal: miniDropdownSeal });
  const miniReadGap = (k: string): number => {
    const v = gapMeasurements[k];
    return typeof v === 'string' ? parseFloat(v) : (typeof v === 'number' ? v : NaN);
  };
  const miniFailedNums = new Set<number>();
  let miniHasGap = false;
  const miniRenderGapRow = (r: GapRow): string => {
    const n = miniReadGap(r.key);
    const has = !isNaN(n);
    if (has) miniHasGap = true;
    const outOfTol = has && n > 4;
    if (outOfTol && r.num > 0) miniFailedNums.add(r.num);
    const hot = outOfTol ? 'background:#FEF2F2;color:#EF4444;font-weight:700;' : '';
    const numCell = r.num > 0 ? String(r.num) : '—';
    const valCell = has ? `${n} mm` : '—';
    return `<tr><td style="padding:3px 5px;border:1px solid #ddd;text-align:center;${hot}">${numCell}</td><td style="padding:3px 5px;border:1px solid #ddd;${hot}">${r.pos}</td><td style="padding:3px 5px;border:1px solid #ddd;text-align:center;${hot}">${valCell}</td></tr>`;
  };
  const miniGapNumbered = miniDiagramRows.map(miniRenderGapRow).join('');
  const miniGapThreshold = miniThresholdRows.map(miniRenderGapRow).join('');
  const miniGapDiagramSvg = buildDoorDiagramSvg({ double: miniIsDouble, dropdownSeal: miniDropdownSeal, failedNums: miniFailedNums });
  const showMiniGapDiagram = !!inspection && !isNoAccess && !needsReplacement && miniHasGap;

  return `
    <div class="avoid-break door-mini ${options.pageBreakBefore ? 'page-break' : ''}" style="margin: 14px 0; padding: 14px; border: 1px solid #e0e0e0; border-radius: 10px; background:#fff;">
      <div style="display:flex; align-items:flex-start; gap:14px;">
        <div style="flex:1;">
          <div style="display:flex; align-items:center; gap:10px; margin-bottom:6px;">
            <span style="font-size:16px; font-weight:800; color:#ED1C24;">${door.door_reference}</span>
            <span style="font-size:11px; font-weight:700; color:#fff; background:${effColour}; padding:3px 8px; border-radius:10px;">${effLabel}</span>
          </div>
          <div style="font-size:12px; color:#333; margin-bottom:2px;"><strong>${door.location}</strong>${aptFloor ? ` &middot; ${aptFloor}` : ''}</div>
          <div style="font-size:11px; color:#555;">
            ${door.door_type === 'communal' ? 'Communal' : 'Apartment'} &middot; ${door.door_leaf_type === 'double' ? 'Double Door' : 'Single Door'}
            ${door.fd_rating ? ` &middot; ${door.fd_rating}` : ''}
            &middot; ${freqLabel(door.inspection_frequency)}
          </div>
          ${inspection ? `
            <div style="font-size:11px; color:#666; margin-top:4px;">
              Last inspected ${inspDate} by ${inspection.inspector_name || '—'}
            </div>
          ` : `
            <div style="font-size:11px; color:#999; margin-top:4px; font-style:italic;">No inspection recorded yet.</div>
          `}
        </div>
        ${door.door_photo ? `
          <img src="${door.door_photo}" style="width:auto; max-width:110px; max-height:110px; height:auto; object-fit:contain; border-radius:6px; border:1px solid #e0e0e0; background:#fafafa;" />
        ` : ''}
      </div>

      <!-- Iter-110 Phase B2 — Asset Info panel.
           Rating / Configuration / Inspection Frequency / Certification
           status + regulator-aligned frequency justification. Mirrors
           the web app door mini-card spec §4.2. -->
      <div style="margin-top:10px; padding:8px 10px; background:#F9FAFB; border:1px solid #E5E7EB; border-radius:6px;">
        <div style="display:grid; grid-template-columns: 1fr 1fr; gap:4px 12px; font-size:10.5px;">
          <div style="color:#6B7280;">Rating</div>
          <div style="color:#111827; font-weight:600;">${door.fd_rating || 'Not specified'}</div>
          <div style="color:#6B7280;">Configuration</div>
          <div style="color:#111827; font-weight:600;">${door.door_leaf_type === 'double' ? 'Double leaf' : 'Single leaf'}${door.door_type === 'communal' ? ' — communal' : ' — flat entrance'}</div>
          <div style="color:#6B7280;">Inspection frequency</div>
          <div style="color:#111827; font-weight:600;">${freqLabel(door.inspection_frequency)}</div>
          <div style="color:#6B7280;">Certification label</div>
          <div style="color:#111827; font-weight:600;">${isNoAccess ? 'Not inspected (no access)' : ((inspection?.checklist_data?.custom_items?.door_label_present === true || inspection?.checklist_data?.door_label_present === true) ? 'Visible' : (inspection ? 'Not visible' : 'Not yet inspected'))}</div>
        </div>
        ${door.inspection_frequency && FREQUENCY_JUSTIFICATION[door.inspection_frequency] ? `
          <div style="margin-top:6px; padding-top:6px; border-top:1px dashed #E5E7EB; font-size:10px; color:#6B7280; font-style:italic; line-height:1.4;">
            ${FREQUENCY_JUSTIFICATION[door.inspection_frequency]}
          </div>
        ` : ''}
      </div>

      ${(needsReplacement || isNoAccess) ? '' : renderComponentResultsGrid(inspection)}

      ${isNoAccess ? `
        <!-- Iter-163o — NO ACCESS observation block. Replaces the
             Component Inspection Results grid + failed-components /
             advisories blocks for doors the inspector could not
             enter. Client sees the door photo (top-right of the
             card) plus the inspector's own on-site note explaining
             why access was refused.
             Iter-163p — Now enumerates every prior access attempt
             so the responsible person can see how many times the
             door has been visited without success. -->
        <div style="margin-top:10px; padding:10px 12px; background:#F3F4F6; border:1px solid #D1D5DB; border-left:4px solid #6B7280; border-radius:6px;">
          <div style="font-size:11px; font-weight:800; color:#374151; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:4px;">
            No access — door not inspected${(options.noAccessAttempts && options.noAccessAttempts.length > 1) ? ` · ${options.noAccessAttempts.length} attempts` : ''}
          </div>
          ${(options.noAccessAttempts && options.noAccessAttempts.length > 0) ? `
            <div style="font-size:10.5px; color:#4B5563; line-height:1.5; margin-top:4px;">
              <table style="width:100%; border-collapse:collapse; font-size:10.5px;">
                <thead>
                  <tr style="background:#E5E7EB;">
                    <th style="text-align:left; padding:4px 6px; border-bottom:1px solid #D1D5DB; width:82px;">Attempt</th>
                    <th style="text-align:left; padding:4px 6px; border-bottom:1px solid #D1D5DB; width:110px;">Inspector</th>
                    <th style="text-align:left; padding:4px 6px; border-bottom:1px solid #D1D5DB;">Inspector notes</th>
                  </tr>
                </thead>
                <tbody>
                  ${options.noAccessAttempts.map((a, i) => `
                    <tr>
                      <td style="padding:4px 6px; border-bottom:1px solid #E5E7EB; color:#111827; font-weight:600;">
                        #${i + 1} · ${a.date ? format(new Date(a.date), 'dd/MM/yyyy') : '—'}
                      </td>
                      <td style="padding:4px 6px; border-bottom:1px solid #E5E7EB; color:#374151;">${escapeHtml(a.inspector || '—')}</td>
                      <td style="padding:4px 6px; border-bottom:1px solid #E5E7EB; color:#4B5563;">${a.note ? escapeHtmlWithBreaks(a.note) : '<em style="color:#9CA3AF;">No inspector notes recorded</em>'}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : `
            <div style="font-size:11px; color:#4B5563; line-height:1.5;">
              ${noAccessNote
                ? `<strong>Inspector's notes:</strong> ${escapeHtmlWithBreaks(noAccessNote)}`
                : 'The inspector could not gain access to this door. No component-level checks were carried out.'}
            </div>
          `}
          <div style="font-size:10px; color:#6B7280; font-style:italic; margin-top:6px; line-height:1.4;">
            ${(options.noAccessAttempts && options.noAccessAttempts.length >= 2)
              ? 'Multiple access attempts have failed — the responsible person should escalate resident engagement (e.g. written notice) before the next inspection cycle.'
              : `This doorset should be re-attempted at the next planned inspection cycle. ${noAccessPhotoRef}`}
          </div>
          ${noAccessPhotos.length > 0 ? `
            <div style="margin-top:8px;">
              <div style="font-size:10px; font-weight:800; color:#374151; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:4px;">On-site photos (${noAccessPhotos.length})</div>
              <div style="display:flex; flex-wrap:wrap; gap:6px;">
                ${noAccessPhotos.map((p) => `<img src="${p}" style="width:auto; max-width:120px; max-height:120px; height:auto; object-fit:contain; border-radius:6px; border:1px solid #D1D5DB; background:#fff;" />`).join('')}
              </div>
            </div>
          ` : ''}
        </div>
      ` : ''}

      ${(gapFailsMini && !needsReplacement && !isNoAccess) ? `
        <div style="margin-top:10px; padding:8px 10px; background:#FEF2F2; border:1px solid #FCA5A5; border-left:4px solid #DC2626; border-radius:6px;">
          <div style="font-size:11px; font-weight:800; color:#991B1B; text-transform:uppercase; letter-spacing:0.4px;">
            Door-to-frame gaps outside tolerance (max 4&nbsp;mm)
          </div>
          ${overTolerancePoints.length > 0 ? `
            <div style="font-size:11px; color:#7F1D1D; margin-top:4px;">
              ${overTolerancePoints.map(p => `<span style="display:inline-block; margin-right:8px;"><strong>${escapeHtml(p.label)}:</strong> ${p.mm}&nbsp;mm</span>`).join('')}
            </div>
          ` : ''}
          ${Array.isArray(cd.gap_recommendations) && cd.gap_recommendations.length > 0 ? (() => {
            // Iter-108bk — Dedupe by title to avoid repeating the same
            // remedy line for each height that failed. First-seen wins.
            const seenT = new Set<string>();
            const deduped = (cd.gap_recommendations as any[]).filter(r => {
              const t = String(r?.title || '').trim().toLowerCase();
              if (!t) return true;
              if (seenT.has(t)) return false;
              seenT.add(t);
              return true;
            });
            return `
            <div style="font-size:11px; color:#7F1D1D; margin-top:6px;">
              ${deduped.slice(0, 3).map((r: any) => `<div>• <strong>${escapeHtml(r.title || 'Remedy')}</strong>${r.detail ? ' — ' + escapeHtml(r.detail) : ''}</div>`).join('')}
            </div>
          `;
          })() : ''}
        </div>
      ` : ''}

      ${showMiniGapDiagram ? `
        <div class="avoid-break" style="margin-top:10px;">
          <div style="font-size:11px; font-weight:800; color:#374151; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:5px;">Door-to-frame gap measurements (mm)</div>
          <div style="display:flex; gap:12px; align-items:flex-start;">
            <div style="flex:0 0 140px; width:140px; text-align:center;">
              ${miniGapDiagramSvg}
              <div style="font-size:8px;color:#6B7280;margin-top:2px;">Measurement point reference${miniIsDouble ? ' — double leaf' : ''}${miniDropdownSeal ? ' · dropdown seal fitted' : ''}</div>
            </div>
            <table style="flex:1; border-collapse:collapse; font-size:9px;">
              <thead><tr>
                <th style="background:#1a1a1a;color:#fff;padding:3px 5px;border:1px solid #1a1a1a;width:34px;text-align:center;">Point</th>
                <th style="background:#1a1a1a;color:#fff;padding:3px 5px;border:1px solid #1a1a1a;text-align:left;">Position</th>
                <th style="background:#1a1a1a;color:#fff;padding:3px 5px;border:1px solid #1a1a1a;width:62px;text-align:center;">Gap</th>
              </tr></thead>
              <tbody>
                ${miniGapNumbered}
                ${miniGapThreshold ? `<tr><td colspan="3" style="padding:3px 5px;border:1px solid #ddd;background:#F3F4F6;font-weight:700;color:#374151;">Threshold / under-door (not numbered)</td></tr>${miniGapThreshold}` : ''}
              </tbody>
            </table>
          </div>
          <div style="font-size:8px;color:#6B7280;margin-top:4px;">Target perimeter gap 2&ndash;4&nbsp;mm; readings over 4&nbsp;mm are highlighted.</div>
        </div>
      ` : ''}

      ${(() => {
        // Iter-163o — For NO ACCESS doors, we've already rendered the
        // photo + inspector's notes block above. Skip the entire
        // hard-fails / advisories / replacement decision tree — none
        // of the components were actually assessed.
        if (isNoAccess) return '';

        // Iter-109g — Partition failed components into hard failures
        // (red) and advisories (amber). Advisory items were flagged by
        // the inspection form as "Not a failure — recommendation only"
        // (e.g. the solid 44 mm notional-fire-door reason). Renders
        // them in distinct sections so the client / building manager
        // sees advice without the alarm of a red-highlighted fail.
        //
        // Iter-110ak — DERIVE advisory status FROM the risk level, not
        // just from `fc.advisory`. Root cause of the "some red, some
        // blue on the same report" bug Alex reported for Darlington:
        // inspections filed on the web-form (older code path) didn't
        // stamp `advisory: true` on Low-risk fails like the missing
        // certification label. Reading the canonical DEFECT_RISK map
        // means the PDF is consistent regardless of which client
        // wrote the inspection.
        const isAdvisoryEntry = (fc: any): boolean => {
          // Iter-227 — an evidence-backed OPTIONAL component fail (spy viewer /
          // letterbox present) is a HARD fail, overriding any stale advisory/
          // observation/Low stamp from an older build. Renders red in Main
          // Failed with its photo.
          if (isEvidenceBackedOptionalFail(fc)) return false;
          // Iter-110ak — Explicit hard-fail override wins. Set by
          // the inspection form when the inspector ticked
          // "This door needs replacing" on the cert-label question.
          if (fc?.advisory === false) return false;
          if (fc?.needs_replacing === true) return false;
          if (fc?.severity === 'critical') return false;
          if (fc?.advisory === true) return true;
          if (fc?.severity === 'observation') return true;
          const risk = (fc?.risk as string) || riskFor(fc?.key || '');
          return risk === 'Low';
        };
        const hardFails = failed.filter((fc: any) => !isAdvisoryEntry(fc));
        const advisoryItems = failed.filter((fc: any) => isAdvisoryEntry(fc));

        // Iter-163f — "Door needs replacing" override. When the inspector
        // has ticked the "This door needs replacing" checkbox on the
        // cert-label question, the entire doorset is non-compliant.
        // Individual component failures/advisories become noise —
        // replace them with a single overriding replacement statement
        // so the client isn't chasing seals/closers on a door that's
        // being pulled out.
        if (doorsetNeedsReplacement(inspection)) {
          return `
      <div style="margin-top:10px; padding-top:10px; border-top:1px solid #f0f0f0;">
        <div style="font-size:11px; font-weight:800; color:#B91C1C; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:6px;">
          Doorset replacement required
        </div>
        <div style="padding:10px 12px; background:#FEF2F2; border:1.5px solid #DC2626; border-left:5px solid #B91C1C; border-radius:6px;">
          <div style="display:inline-block; background:#B91C1C; color:#fff; font-size:9px; font-weight:800; letter-spacing:0.5px; padding:2px 8px; border-radius:4px; margin-bottom:6px;">FULL DOORSET REPLACEMENT</div>
          <div style="font-size:11px; color:#1a1a1a; line-height:1.55;">${DOORSET_REPLACEMENT_STATEMENT}</div>
        </div>
        ${renderDoorReplacementDetails(inspection, 'mini')}
      </div>
          `;
        }

        return `
      ${hardFails.length > 0 ? `
        <div style="margin-top:10px; padding-top:10px; border-top:1px solid #f0f0f0;">
          <div style="font-size:11px; font-weight:700; color:#EF4444; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:6px;">
            ${hardFails.length} Failed Component${hardFails.length === 1 ? '' : 's'}
          </div>
          ${groupByLeaf(hardFails.slice(0, 8), isDoubleDoor(door, inspection)).map((g) => `
          ${g.heading ? `<div style="font-size:10px; font-weight:800; color:#7C2D12; background:#FFF7ED; border-left:3px solid #EA580C; padding:3px 8px; border-radius:4px; margin:6px 0 4px;">${escapeHtml(g.heading)}</div>` : ''}
          <div style="display:flex; flex-wrap:wrap; gap:8px;">
            ${g.items.map((fc: any) => {
              const isLetterbox = fc.key === 'letterbox_operates_correctly';
              // Iter-163d — Surface any 'Add your own answer' text on
              // hard-fail cards too. renderReasonChips already renders
              // every bullet (canonical + custom) as red chips, but
              // custom answers are visually indistinguishable from
              // canonical BS-8214 wording — a separate cyan block
              // makes it clear which observations were the
              // inspector's own words.
              const customAnswers = extractInspectorCustomAnswers(fc.notes);
              const customAnswerBlock = customAnswers.length > 0 ? `
                <div style="margin-top:6px; padding:5px 7px; background:#F0F9FF; border-left:3px solid #0284C7; border-radius:4px;">
                  <div style="font-size:9px; font-weight:800; color:#075985; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:2px;">Inspector's additional observation${customAnswers.length === 1 ? '' : 's'}</div>
                  ${customAnswers.map((a) => `<div style="font-size:10px; color:#0C4A6E; line-height:1.4;">&bull; ${escapeHtml(a)}</div>`).join('')}
                </div>
              ` : '';
              return `
              <div style="flex: 0 0 calc(50% - 4px); border:1px solid #f0f0f0; border-radius:6px; padding:6px; background:#fafafa;">
                <div style="font-size:10px; font-weight:700; color:#1a1a1a; margin-bottom:4px;">${fc.question}</div>
                ${fc.photo ? `<img src="${fc.photo}" style="display:block; max-width:100%; max-height:140px; width:auto; height:auto; object-fit:contain; border-radius:4px; margin:0 auto; background:#fff;" />${captionFor(inspection?.photo_captions, fc.photo) ? `<div style="font-size:9.5px; color:#4B5563; text-align:center; margin-top:3px; line-height:1.3;">${escapeHtml(captionFor(inspection?.photo_captions, fc.photo))}</div>` : ''}` : ''}
                ${isLetterbox ? renderLetterboxFailureBlock(inspection?.checklist_data) : ''}
                ${renderReasonChips(fc.notes, isLetterbox, fc.key)}
                ${customAnswerBlock}
              </div>
            `;}).join('')}
          </div>
          `).join('')}
          ${hardFails.length > 8 ? `<div style="font-size:10px; color:#888; margin-top:6px; font-style:italic;">+${hardFails.length - 8} more documented in the full single-door report.</div>` : ''}
        </div>
      ` : ''}
      ${advisoryItems.length > 0 ? `
        <div style="margin-top:10px; padding:10px 12px; background:#EFF6FF; border:1px solid #93C5FD; border-left:4px solid #2563EB; border-radius:6px;">
          <div style="font-size:11px; font-weight:800; color:#1E40AF; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:6px;">
            ${advisoryItems.length} Advisory${advisoryItems.length === 1 ? '' : ' items'} (not a failure)
          </div>
          ${advisoryItems.map((fc: any) => {
            // Iter-110 Phase B2 — certification-label observations get
            // the canonical BS-8214-aligned wording so property
            // managers understand a missing label does NOT
            // automatically mean the door is non-compliant.
            const isCertLabel = fc.key === 'door_label_present';
            // Iter-163c — Preserve any bespoke text the inspector
            // typed into "Add your own answer" so it isn't silently
            // dropped when the item downgrades to an advisory. The
            // canonical advisory reason bullets are filtered out by
            // extractInspectorCustomAnswers().
            const customAnswers = extractInspectorCustomAnswers(fc.notes);
            // Iter-163e — The long BS-8214 canonical paragraph must
            // ONLY render when the specific "Certification could not
            // be confirmed…" advisory box has been ticked by the
            // inspector. Previously it showed for every non-Yes
            // answer, even when the inspector picked a different
            // canonical reason (e.g. "Identification labels or plugs
            // are missing.") — over-claiming compliance guidance the
            // inspector never signed off.
            const advisoryBoxTicked = isCertLabel && !!fc.notes &&
              Array.from(ADVISORY_REASONS).some((r) => String(fc.notes).includes(String(r)));
            const showCanonicalCopy = isCertLabel && advisoryBoxTicked;
            const customAnswerBlock = customAnswers.length > 0 ? `
              <div style="margin-top:6px; padding:6px 8px; background:#F0F9FF; border-left:3px solid #0284C7; border-radius:4px;">
                <div style="font-size:9.5px; font-weight:800; color:#075985; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:3px;">Inspector's additional observation${customAnswers.length === 1 ? '' : 's'}</div>
                ${customAnswers.map((a) => `<div style="font-size:10.5px; color:#0C4A6E; line-height:1.45;">&bull; ${escapeHtml(a)}</div>`).join('')}
              </div>
            ` : '';
            // Notes text (canonical ticked reasons) — rendered when
            // we're NOT showing the long canonical paragraph. Custom
            // answers are stripped so they only surface in the cyan
            // block above (no duplication).
            const canonicalOnlyNotes = (!showCanonicalCopy && fc.notes)
              ? String(fc.notes)
                  .split(/\n/)
                  .map((l) => l.replace(/^\s*[\u2022*\-]\s*/, '').trim())
                  .filter(Boolean)
                  .filter((r) => !/^size of opening\s*:/i.test(r))
                  .filter((r) => !/^reason for failure\s*:/i.test(r))
                  .filter((r) => r.toLowerCase() !== 'door needs replacing')
                  .filter((r) => CANONICAL_FAILURE_REASONS.has(r))
                  .join(' · ')
              : '';
            return `
              <div style="margin-top:6px; padding: 6px 8px; background:#fff; border:1px solid #DBEAFE; border-radius:4px;">
                <div style="font-size:11.5px; font-weight:700; color:#1E3A8A; margin-bottom:${showCanonicalCopy ? '4px' : '2px'};">${escapeHtml(fc.question)}</div>
                ${showCanonicalCopy ? `<div style="font-size:11px; color:#1E3A8A; line-height:1.55;">${CERTIFICATION_LABEL_ADVISORY_COPY}</div>` : ''}
                ${canonicalOnlyNotes ? `<div style="font-size:11px; color:#1E3A8A; margin-top:0;">${escapeHtml(canonicalOnlyNotes)}</div>` : ''}
                ${customAnswerBlock}
              </div>
            `;
          }).join('')}
        </div>
      ` : ''}
        `;
      })()}

      ${(options.remedialActionsForDoor && options.remedialActionsForDoor.length > 0) ? `
        <div style="margin-top:10px; padding:10px 12px; background:#FFF7ED; border:1px solid #FED7AA; border-left:4px solid #EA580C; border-radius:6px;">
          <div style="font-size:11px; font-weight:800; color:#9A3412; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:6px;">Remedial actions for this door (${options.remedialActionsForDoor.length})</div>
          <table style="width:100%; border-collapse:collapse; font-size:10.5px;">
            <thead>
              <tr style="background:#FFEDD5;">
                <th style="text-align:left; padding:5px 7px; border-bottom:1px solid #FED7AA; width:26%;">Defect</th>
                <th style="text-align:left; padding:5px 7px; border-bottom:1px solid #FED7AA; width:12%;">Risk</th>
                <th style="text-align:left; padding:5px 7px; border-bottom:1px solid #FED7AA;">Recommended action</th>
                <th style="text-align:left; padding:5px 7px; border-bottom:1px solid #FED7AA; width:22%;">Timescale</th>
              </tr>
            </thead>
            <tbody>
              ${options.remedialActionsForDoor.map((r: any) => {
                const riskColour = r.risk === 'Critical' ? '#7F1D1D' : r.risk === 'High' ? '#991B1B' : r.risk === 'Medium' ? '#92400E' : '#075985';
                const riskBg = (r.risk === 'Critical' || r.risk === 'High') ? '#FEE2E2' : r.risk === 'Medium' ? '#FEF3C7' : '#E0F2FE';
                return `
                  <tr>
                    <td style="padding:5px 7px; border-bottom:1px solid #FEEBD5; vertical-align:top;">${escapeHtml(r.defect || '')}</td>
                    <td style="padding:5px 7px; border-bottom:1px solid #FEEBD5; vertical-align:top;"><span style="display:inline-block; padding:1px 7px; border-radius:999px; background:${riskBg}; color:${riskColour}; font-size:9px; font-weight:800; text-transform:uppercase;">${r.risk}</span></td>
                    <td style="padding:5px 7px; border-bottom:1px solid #FEEBD5; vertical-align:top;">${escapeHtml(r.action || '')}</td>
                    <td style="padding:5px 7px; border-bottom:1px solid #FEEBD5; vertical-align:top;">${escapeHtml(r.timescale || '')}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
      ` : ''}

      <!-- Iter-110 Phase B2 — Recent Maintenance Evidence panel.
           Rendered when the caller passed maintenanceJobsForDoor
           into renderDoorMiniReport(). Filters to last 12 months,
           up to 3 jobs × 6 photos each with BEFORE/AFTER pills. -->
      ${renderMaintenanceEvidence(options.maintenanceJobsForDoor || [])}

      ${renderCustomAnswersHtml(inspection, true)}

      ${inspection?.notes ? `
        <div style="margin-top:8px; padding:6px 10px; background:#fffbeb; border-left:3px solid #F59E0B; border-radius:4px;">
          <div style="font-size:10px; font-weight:700; color:#92400E; text-transform:uppercase; letter-spacing:0.4px;">Inspector Notes</div>
          <div style="font-size:11px; color:#1a1a1a; margin-top:2px;">${inspection.notes}</div>
        </div>
      ` : ''}
    </div>
  `;
};

// ---------------------------------------------------------------------------
// Compliance & Golden Thread rendering helpers.
//
// The FDCheck app is new to most clients — so every generated PDF ships with
// a short cover-page blurb (renderComplianceIntroHtml) explaining WHY this
// report/quote/invoice makes the property manager compliant, plus a full
// appendix at the very end (renderComplianceAppendixHtml) covering the
// legislation, the Building Safety Act "Golden Thread", and how FDCheck
// delivers it. Both sections can be toggled per-report at generation time.
// ---------------------------------------------------------------------------

const renderComplianceIntroHtml = (): string => `
  <div class="section avoid-break" style="margin-top: 22px; margin-bottom: 8px;">
    <div style="border: 2px solid #ED1C24; border-radius: 10px; padding: 16px 18px; background: #FFFBFB;">
      <div style="display:flex; align-items:center; gap:8px; margin-bottom: 8px;">
        <div style="font-size: 15px; font-weight: 800; color: #ED1C24; letter-spacing: 0.3px;">
          COMPLIANCE &amp; THE BUILDING SAFETY GOLDEN THREAD
        </div>
      </div>
      <p style="font-size: 11.5px; line-height: 1.55; color: #1a1a1a; margin: 0 0 8px 0;">
        This document forms part of a live, digital compliance record maintained on your behalf by FDCheck.
        Every inspection, photograph, remedial action and sign&#8209;off is captured with the engineer&rsquo;s identity,
        GPS location and timestamp &mdash; giving you a defensible audit trail that satisfies current UK
        fire&#8209;safety legislation and the Building Safety Act&rsquo;s &ldquo;Golden Thread&rdquo; duty.
      </p>
      <table style="width: 100%; font-size: 10.5px; border-collapse: collapse; margin-top: 6px;">
        <tr>
          <td style="padding: 4px 8px; border-bottom: 1px solid #F3D2D3; color:#7F1D1D; font-weight:700; width: 46%;">Fire Safety (England) Regulations 2022</td>
          <td style="padding: 4px 8px; border-bottom: 1px solid #F3D2D3; color:#1a1a1a;">Reg 10 quarterly / annual door checks &mdash; scheduled &amp; evidenced</td>
        </tr>
        <tr>
          <td style="padding: 4px 8px; border-bottom: 1px solid #F3D2D3; color:#7F1D1D; font-weight:700;">Regulatory Reform (Fire Safety) Order 2005</td>
          <td style="padding: 4px 8px; border-bottom: 1px solid #F3D2D3; color:#1a1a1a;">Responsible Person&rsquo;s findings &amp; actions recorded per Art. 17</td>
        </tr>
        <tr>
          <td style="padding: 4px 8px; border-bottom: 1px solid #F3D2D3; color:#7F1D1D; font-weight:700;">Building Safety Act 2022</td>
          <td style="padding: 4px 8px; border-bottom: 1px solid #F3D2D3; color:#1a1a1a;">Golden Thread &mdash; digital, accurate, accessible</td>
        </tr>
        <tr>
          <td style="padding: 4px 8px; color:#7F1D1D; font-weight:700;">BS 8214 &amp; BS EN 1634&#8209;1</td>
          <td style="padding: 4px 8px; color:#1a1a1a;">Structured inspection maps to recognised industry standards</td>
        </tr>
      </table>
      <p style="font-size: 10.5px; line-height: 1.5; color: #6B7280; margin: 10px 0 0 0; font-style: italic;">
        A full breakdown of how this report contributes to the Golden Thread is provided in the compliance
        appendix at the end of this document.
      </p>
    </div>
  </div>
`;

const renderComplianceAppendixHtml = (): string => `
  <div class="section page-break" style="margin-top: 20px;">
    <div class="section-title" style="background-color:#FEF2F2; border-left:4px solid #ED1C24;">
      Compliance &amp; The Building Safety Golden Thread
    </div>

    <p style="font-size: 12px; line-height: 1.6; color:#1a1a1a; margin: 4px 0 12px 0;">
      The Building Safety Act 2022 requires the Accountable Person to maintain the
      <strong>Golden Thread</strong> &mdash; a single, digital, accurate and accessible record of the
      building&rsquo;s safety information for its entire lifecycle. FDCheck is purpose-built to deliver
      that thread for every fire door on your estate.
    </p>

    <div class="section-title" style="background-color:#F9FAFB; font-size:14px;">The legislation this document addresses</div>
    <table style="width:100%; font-size:11px; border-collapse:collapse; margin-bottom:14px;">
      <thead>
        <tr style="background:#FEF2F2;">
          <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #F3D2D3; color:#7F1D1D; width: 32%;">Regulation / Standard</th>
          <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #F3D2D3; color:#7F1D1D; width: 34%;">Requirement</th>
          <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #F3D2D3; color:#7F1D1D; width: 34%;">How FDCheck delivers it</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;"><strong>Fire Safety (England) Regs 2022 &mdash; Reg 10</strong></td>
          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;">Quarterly checks of communal fire doors in buildings &gt;11m; annual checks of flat entrance doors.</td>
          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;">Scheduled inspections per door with automatic due&#8209;date tracking and audit trail.</td>
        </tr>
        <tr>
          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;"><strong>Regulatory Reform (Fire Safety) Order 2005</strong></td>
          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;">Responsible Person must record significant findings and remedial actions.</td>
          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;">Every inspection, photo, failure reason and remedial action is timestamped and preserved.</td>
        </tr>
        <tr>
          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;"><strong>Fire Safety Act 2021</strong></td>
          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;">Structure, external walls and flat entrance doors fall within FRA scope.</td>
          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;">Building &rarr; Floor &rarr; Door hierarchy with plan&#8209;pinning captures the full asset register.</td>
        </tr>
        <tr>
          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;"><strong>Building Safety Act 2022</strong> (Higher&#8209;Risk Buildings)</td>
          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;">Accountable Person must maintain the Golden Thread of information.</td>
          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;">Immutable inspection history, engineer identity, GPS location and photo evidence per door.</td>
        </tr>
        <tr>
          <td style="padding:8px 10px; vertical-align:top;"><strong>BS 8214 / BS EN 1634&#8209;1</strong></td>
          <td style="padding:8px 10px; vertical-align:top;">Fire door installation &amp; inspection standards (gaps, seals, hinges, closer, glazing).</td>
          <td style="padding:8px 10px; vertical-align:top;">Structured inspection form mapped to BS 8214 components with advisory vs failure distinction.</td>
        </tr>
      </tbody>
    </table>

    <div class="section-title" style="background-color:#F9FAFB; font-size:14px;">The Golden Thread &mdash; delivered</div>
    <p style="font-size:11.5px; line-height:1.55; color:#1a1a1a; margin: 4px 0 8px 0;">
      The Building Safety Act requires a <strong>single, digital, accurate and accessible</strong> record for the
      life of the building. FDCheck creates it automatically:
    </p>
    <ol style="font-size:11.5px; line-height:1.6; color:#1a1a1a; margin: 0 0 12px 20px; padding: 0;">
      <li><strong>Asset Register</strong> &mdash; every door catalogued with location, floor&#8209;plan pin, fire rating and installation data.</li>
      <li><strong>Chain of Custody</strong> &mdash; every inspection stamped with engineer ID, GPS coordinates and timestamp (tamper&#8209;evident).</li>
      <li><strong>Photographic Evidence</strong> &mdash; label photos, failure photos and post&#8209;remedial photos preserved per door.</li>
      <li><strong>Historical Audit Trail</strong> &mdash; all prior inspections visible; nothing is overwritten or deleted.</li>
      <li><strong>Remedial Traceability</strong> &mdash; failures &rarr; quotes &rarr; approved work &rarr; post&#8209;work reports linked to the same door record.</li>
      <li><strong>Handover&#8209;Ready Reports</strong> &mdash; identical PDFs on mobile and web, ready for BSR / fire officer / insurer submission.</li>
      <li><strong>Client Portal</strong> &mdash; building owners have real&#8209;time read&#8209;only access to their Golden Thread without waiting for reports.</li>
    </ol>

    <div class="section-title" style="background-color:#F9FAFB; font-size:14px;">Additional compliance wins</div>
    <ul style="font-size:11.5px; line-height:1.6; color:#1a1a1a; margin: 0 0 12px 20px; padding: 0;">
      <li><strong>AI extraction</strong> from remedial job sheets &rarr; structured, searchable data (not lost in PDFs or emails).</li>
      <li><strong>Offline&#8209;first field capture</strong> &mdash; engineers can work in basements and risers without losing the evidence chain.</li>
      <li><strong>Contractor branding</strong> on post&#8209;work reports &mdash; clear accountability for remedial trades.</li>
      <li><strong>Interactive floor plans</strong> with pinned doors &mdash; a visual asset map that satisfies the &ldquo;accessible information&rdquo; duty.</li>
      <li><strong>Advisory vs failure</strong> distinction &mdash; mirrors regulator language and avoids overstating risk.</li>
    </ul>

    <div style="margin: 16px 0 14px 0; padding: 14px 16px; background:#FFFBFB; border-left: 4px solid #ED1C24; border-radius: 6px;">
      <div style="font-size: 11px; font-weight: 800; color:#ED1C24; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;">
        Bottom line for the property manager / owner
      </div>
      <p style="font-size: 12px; line-height: 1.6; color:#1a1a1a; margin: 0; font-style: italic;">
        &ldquo;With FDCheck, every door has a living digital passport. If the fire officer, BSR inspector,
        insurer or a resident asks &lsquo;when was this last checked, by whom, what did they find, and what did
        you do about it?&rsquo; &mdash; the answer is one tap away, backed by photographic and GPS evidence.&rdquo;
      </p>
    </div>

    <p style="font-size: 10px; color: #6B7280; text-align:center; margin: 14px 0 0 0;">
      Powered by <strong style="color:#ED1C24;">FDCheck</strong> &mdash; the compliance platform for fire door professionals.
    </p>
  </div>
`;

export const pdfService = {
  // Iter-108t — RM York branded quote template.
  //
  // When the quote's issuing contractor is RM York Ltd we swap the
  // generic FD Check quote layout for a bespoke document that mirrors
  // the RM York reference quote (see 112 The Residence). Highlights:
  //   • "Quotation – <site name>" title with Ref / Client / Date /
  //     Address meta block.
  //   • Full RM York boilerplate: Legal compliance, Point of work
  //     inspection, Contractor Competence & Client Liability.
  //   • Line items grouped by DOOR (not per-item pricing) — each
  //     door shows a bulleted list of works and a single door total.
  //   • Standing "Fire door inspection and compliance report" line
  //     when the quote has an item flagged as an inspection report.
  //   • Full RM York Terms and conditions verbatim.
  //   • Sub-total / VAT / Total block.
  //   • Compliance, Quality & Peace of Mind closing.
  _isRmYorkContractor: (contractor: any): boolean =>
    !!(contractor && contractor.name && /rm\s*york/i.test(String(contractor.name))),

  _renderRmYorkQuoteHtml: (
    quote: any,
    opts: {
      maintenanceCompany?: any;
      customFields?: any[];
      displayMode?: 'labour_materials' | 'total_only' | null;
      doorPhotos?: QuoteDoorPhoto[] | null;
      showDoorPhotos?: boolean;
      photoAppendix?: boolean;
    } = {},
  ): string => {
    const contractor = quote.contractor || {};
    // Iter-173 — one failure photo per door, shown beside that door's
    // scope of works so the client can see what they're paying for.
    // Iter-174 — the same photos can also print large in an appendix.
    const doorPhotoMap = opts.showDoorPhotos === false ? {} : buildDoorPhotoMap(opts.doorPhotos);
    const photoAppendixHtml = opts.photoAppendix
      ? renderQuotePhotoAppendix(opts.doorPhotos, quoteDoorLocations(quote))
      : '';
    // Iter-108bg — Quote-template settings on the RM York branded path.
    // Mirrors the generic renderer: labour/materials mode adds an extra
    // line per item plus subtotals in the totals box, intro wording
    // appears above the Description section, footer wording appears
    // below Terms and Conditions. The RM York logo/branding is
    // untouched — this is layer of information on top of the existing
    // bespoke template.
    const mc = opts.maintenanceCompany || {};
    // Iter-172 — pricing presentation resolves quote → client → company
    // (the caller does that and passes the winner in `displayMode`).
    const tplMode: 'labour_materials' | 'total_only' =
      (opts.displayMode || quote.display_mode || mc.quote_display_mode) === 'total_only' ? 'total_only' : 'labour_materials';
    const tplIntro: string = String(mc.quote_intro_wording || '').trim();
    const tplFooter: string = String(mc.quote_footer_wording || '').trim();
    const tplLabourRatio: number = (() => {
      const r = Number(mc.quote_default_labour_ratio);
      if (!isFinite(r) || r < 0 || r > 1) return 0.6;
      return r;
    })();
    const splitLine = (it: any): { labour: number; materials: number } => {
      const cost = Number(it.cost || 0);
      const el = it.labour_cost;
      const em = it.materials_cost;
      if (typeof el === 'number' || typeof em === 'number') {
        if (typeof el === 'number' && typeof em !== 'number') {
          return { labour: el, materials: Math.max(0, cost - el) };
        }
        if (typeof em === 'number' && typeof el !== 'number') {
          return { labour: Math.max(0, cost - em), materials: em };
        }
        return { labour: Number(el || 0), materials: Number(em || 0) };
      }
      const labour = +(cost * tplLabourRatio).toFixed(2);
      return { labour, materials: +(cost - labour).toFixed(2) };
    };
    const created = format(new Date(quote.created_at), 'dd/MM/yy');
    const validUntil = quote.valid_until
      ? format(new Date(quote.valid_until), 'dd/MM/yyyy')
      : null;
    const quoteRef = quote.id ? String(quote.id).slice(-8).toUpperCase() : 'DRAFT';
    // Prefer the RM York in-app reference format ("ANG030726") built
    // from the customer initials + created date when we can — falls
    // back to the short quote ID for legacy quotes.
    const custInitials = (quote.customer_name || '')
      .split(/\s+/)
      .map((s: string) => s.trim()[0])
      .filter(Boolean)
      .join('')
      .toUpperCase()
      .slice(0, 3);
    const shortDate = quote.created_at
      ? format(new Date(quote.created_at), 'ddMMyy')
      : '';
    const displayRef = custInitials && shortDate
      ? `${custInitials}${shortDate}`
      : quoteRef;

    const clientName = quote.customer_name || contractor.name || '—';
    const customerAddress = (quote as any).customer_address
      || (quote as any).site_address
      || (quote as any).building_address
      || quote.title
      || '';
    // Title is typically "Repairs for <door_reference>" or "<site> — communal";
    // strip common prefixes so it reads cleanly in the "Quotation – …" header.
    const siteName = String(quote.title || 'Fire Door Remedial Works')
      .replace(/^Repairs for\s*/i, '')
      .replace(/^Quote for\s*/i, '')
      .trim() || 'Fire Door Remedial Works';

    // ---- Group items by DOOR ---------------------------------------------
    // Iter-165 — EVERY door (apartment OR communal) gets its OWN section
    // with its OWN total, listing each job as a bullet (NO per-item
    // prices) — matching RM York's own quote layout. Items without a
    // door reference (ad-hoc extra jobs) fall under "Additional Works".
    const items: any[] = Array.isArray(quote.items) ? quote.items : [];
    const knownRefs = knownDoorRefs(items);
    type DoorBucket = { ref: string; loc: string; type: string; items: any[]; total: number };
    const doorBuckets: Record<string, DoorBucket> = {};
    const nonDoor: any[] = [];
    items.forEach((it: any) => {
      const ref = quoteLineDoorKey(it, knownRefs);
      const lineTotal = (Number(it.cost) || 0) * (Number(it.quantity) || 1);
      if (ref) {
        const key = String(ref);
        const slot = doorBuckets[key] || {
          ref: key,
          loc: it.door_location || '',
          type: String(it.door_type || '').toLowerCase(),
          items: [],
          total: 0,
        };
        if (!slot.loc && it.door_location) slot.loc = it.door_location;
        slot.items.push(it);
        slot.total += lineTotal;
        doorBuckets[key] = slot;
      } else {
        nonDoor.push(it);
      }
    });
    const doorList = Object.values(doorBuckets);
    const doorOrder: string[] = Array.isArray(quote.door_order) ? quote.door_order.map(String) : [];
    doorList.sort((a, b) => {
      // Iter-168 — explicit door order (set in the editor) wins.
      if (doorOrder.length) {
        const ia = doorOrder.indexOf(String(a.ref));
        const ib = doorOrder.indexOf(String(b.ref));
        const aa = ia === -1 ? 9999 : ia;
        const bb = ib === -1 ? 9999 : ib;
        if (aa !== bb) return aa - bb;
      }
      // Group same-prefix refs together, numeric suffix aware
      // (RAF-...-001, RAF-...-002 …), otherwise plain alphabetical.
      const stemA = String(a.ref).replace(/\d+/g, '');
      const stemB = String(b.ref).replace(/\d+/g, '');
      if (stemA === stemB) {
        const na = parseInt(String(a.ref).replace(/\D+/g, ''), 10);
        const nb = parseInt(String(b.ref).replace(/\D+/g, ''), 10);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
      }
      return String(a.ref).localeCompare(String(b.ref));
    });

    // A single job line under a door — description bullet, NO per-item
    // price (each door carries ONE total). Strips "Repair:" / "Unmatched"
    // prefixes, the repeated door reference and the "×2 (both leaves)"
    // tail so it reads like a scope line.
    const renderItemLine = (it: any, ref?: string): string => {
      const qty = Number(it.quantity) || 1;
      const cleaned = cleanJobText(it.description || it.component, ref)
        || cleanJobText(it.component, ref);
      return `<div style="display:flex; gap:8px; margin:0 0 7px 0; line-height:1.5;"><span style="color:#ED1C24; font-weight:800;">•</span><span style="flex:1; text-align:justify;">${escapeHtml(cleaned)}${qty > 1 ? ` <strong>&times;${qty}</strong>` : ''}</span></div>`;
    };

    const doorNotesMap: Record<string, string> = (quote.door_notes && typeof quote.door_notes === 'object') ? quote.door_notes : {};
    const doorSections = doorList
      .map((d) => {
        const note = doorNotesMap[d.ref] || '';
        const photo = doorPhotoMap[String(d.ref)]
          || doorPhotoMap[String(d.items.find((it: any) => it.door_id)?.door_id || '')];
        const bodyHtml = `
          ${note ? `<div style="margin: -2px 0 10px 0; font-size: 10pt; font-style: italic; color: #92400E; background:#FFFBEB; border-radius:5px; padding:6px 9px;">${escapeHtml(String(note)).replace(/\n/g, '<br/>')}</div>` : ''}
          <div style="font-size: 10.5pt; color: #1f2937;">${d.items.map((it: any) => renderItemLine(it, String(d.ref))).join('')}</div>`;
        return `
        <div style="margin-top: 18px; padding: 14px 16px; border: 1px solid #E5E7EB; border-left: 4px solid #ED1C24; border-radius: 6px; background: #FAFAFA; page-break-inside: avoid;">
          <div style="display:flex; justify-content: space-between; align-items: baseline; margin-bottom: 10px; gap: 12px;">
            <div style="font-weight: 800; font-size: 13.5pt; color: #1a1a1a;">Door: ${escapeHtml(String(d.ref))}${d.loc ? ` <span style="color:#6B7280; font-weight: 600; font-size: 11pt;">— ${escapeHtml(String(d.loc))}</span>` : ''}</div>
            <div style="font-weight: 800; font-size: 14pt; color: #1a1a1a; white-space: nowrap;">£${d.total.toFixed(2)}</div>
          </div>
          ${photo ? `
            <table style="width:100%; border-collapse:collapse; margin:0;">
              <tr>
                <td style="border:none; padding:0; vertical-align:top;">${bodyHtml}</td>
                <td style="border:none; padding:0 0 0 10px; width:50mm; vertical-align:top;">${doorPhotoBlock(photo, 48)}</td>
              </tr>
            </table>
          ` : bodyHtml}
        </div>
      `;
      })
      .join('');

    const nonDoorSection = nonDoor.length
      ? `
        <div style="margin-top: 22px; padding: 14px 16px; border: 1px solid #E5E7EB; border-radius: 6px; background: #FAFAFA;">
          <div style="font-weight: 800; font-size: 13pt; color: #1a1a1a; margin-bottom: 8px;">Additional Works</div>
          ${nonDoor.map((it: any) => {
            // Iter-197 — an extra job carries its own name (often the
            // door / area it is for) AND a description of the work; print
            // both so the client can see where the job belongs.
            const name = cleanJobText(it.component);
            let detail = cleanJobText(it.description);
            if (detail.toLowerCase() === name.toLowerCase()) detail = '';
            const qty = Number(it.quantity) || 1;
            return `
            <div style="display:flex; justify-content: space-between; align-items: flex-start; gap: 12px; border-bottom: 1px dotted #E5E7EB; padding: 6px 0;">
              <div style="flex:1; font-size: 10.5pt; color:#1f2937;"><strong>${escapeHtml(name || detail)}</strong>${qty > 1 ? ` &times;${qty}` : ''}${name && detail ? ` &mdash; ${escapeHtml(detail)}` : ''}</div>
              <div style="font-weight: 700; font-size: 11pt; color:#1a1a1a; white-space: nowrap;">£${((Number(it.cost) || 0) * qty).toFixed(2)}</div>
            </div>`;
          }).join('')}
        </div>
      `
      : '';

    const rmYorkTerms = `1 day of uninterrupted access required 9-5pm. 2-3 weeks lead time. Parking is to be provided at the property for the duration of the job, no parking costs allowed for, any costs incurred will be chargeable. Assumed use of household facilities for welfare provision, along with disposal of waste which can fit in normal household bins. Working area will not be cleaned nor tools removed at the end of each shift, unless otherwise agreed prior to quoting. Materials will also remain in the property for the duration of the works. Where decoration is expressly allowed for correct paint must be supplied prior to work commencing, otherwise standard quick drying white gloss/satin or best match emulsion will be used. Any paint supply allowance will include standard Johnsons/Leyland/Dulux only, unless otherwise stated and any cost uplift will be chargeable. This quotation is based on information provided by a third party and solely based upon the aspects of the report that were shared. Unless original inspection or additional adequate inspection conducted by our trusted partners. A subsequent inspection and compliance report will only be included where expressly stated and may uncover additional issues previously not identified. All works will be carried out in accordance with current fire door compliance standards and manufacturer guidance where supplied. If upon inspection and adjustment, alternative methods can be utilised to correct the highlighted issues and/or bring the door within tolerance, we reserve the right to do so without alteration to the quotation. Where an overhead door closer or self closing hinges are already present, adjustments will be attempted. However, if after adjustment they are found to be insufficient to achieve compliance or safe operation, replacement with suitable certified hardware may be required and will be advised accordingly. Unless specifically stated no allowance for dropdown seals or threshold strips to account for uneven floors, but these may be required for compliance once installed. Any filling and repairs to the door or frame are completed in good faith in an attempt to bring the door within compliance, they may not fully address an inspector's concerns as preference would always be to completely replace the door. Where replacement doors are required, the quote allows for standard size FD30 unfinished doors, or where the existing structural opening is not of standard size a blank flush unfinished door will be used. Where quoted for full door and frame replacements: if the existing structural openings are standard, we will install fully certified assemblies with complete manufacturer handover packs. If we discover on site that the openings are non-standard and were not picked up prior to quote acceptance, we will discuss the best route forward with the client, either adjust order to solid flush doors (blanks) which can be cut to size on site and will include full certification packs, or under instruction we can modify standard size leaves on site to fit the structural opening. If we modify standard leaves, this custom adjustment precludes the issuance of compliance reports/handover packs. If a project must be paused, altered, or aborted due to structural site dimensions that were not expressly picked up prior to quote acceptance or awaiting client response/approval, we reserve the right to invoice for all materials already secured for the project, alongside our scheduled labour and attended site costs for that day. There is no allowance for structural alteration unless expressly stated. No allowance for making good of plasterwork around the doorway, this isn't always an issue but in older properties the occasional door can shed loose plaster when removing/adjusting frames. Requirement to fill any alterations to frame such as hinge relocation, will be reviewed dependent on ability to leave door ajar, drying times etc. No decoration allowed for, unless specifically stated, however caulk and minor touch up may be carried out if correct paint supplied and drying times allow. If decoration allowed will be quick drying brilliant white unless alternative paint provided, door will need to be left ajar for several hours following visit or risk sticking. If oil-based paint provided door will need to remain ajar overnight or risk paint sticking, in the absence of this limited redecoration will take place and/or industry mitigation methodologies may be employed to reduce the issues. We are more than happy to quote for any decoration, making good or any other additional work. Each line within this quotation remains independently chargeable / invoiceable, regardless of the completion of the other issues. We also reserve the right to invoice for the works on an interim basis, either per visit, weekly or monthly. Payment to be received within 10 days of invoice. Any agreed deductions must have been previously agreed and evidenced accordingly, but also requires a full breakdown of the deduction to be provided.`;

    // Iter-188 — T&Cs waterfall: contractor's own → company quote-template
    // terms → the RM York boilerplate.
    const companyTerms = String(mc.quote_terms || '').trim();
    const termsHtml = (contractor.default_terms && String(contractor.default_terms).trim())
      ? String(contractor.default_terms).replace(/\n/g, '<br/>')
      : (companyTerms ? escapeHtml(companyTerms).replace(/\n/g, '<br/>') : escapeHtml(rmYorkTerms));
    const rmPaymentTerms = Number.isFinite(Number(quote.payment_terms_days)) && quote.payment_terms_days !== null && quote.payment_terms_days !== undefined
      ? Number(quote.payment_terms_days) : null;

    const bankHtml = contractor.bank_details
      ? `
        <div style="margin-top: 14px; padding: 12px 14px; background: #F0FDF4; border: 1px solid #10B981; border-radius: 6px; font-size: 10.5pt;">
          <div style="font-weight: 800; margin-bottom: 4px;">Payment Details</div>
          ${String(contractor.bank_details).replace(/\n/g, '<br/>')}
        </div>
      `
      : '';

    const issuerLogo = contractor.logo_base64 || LOGO_BASE64;
    const issuerName = contractor.name || 'RM York Ltd';
    const issuerAddress = contractor.address || '';
    const issuerContactBits: string[] = [];
    if (contractor.phone) issuerContactBits.push(`Tel ${contractor.phone}`);
    if (contractor.email) issuerContactBits.push(contractor.email);
    if (contractor.website) issuerContactBits.push(contractor.website);
    const issuerContact = issuerContactBits.join(' · ');

    return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  @page { size: A4; margin: 14mm 14mm 20mm 14mm; @bottom-right { content: "Page " counter(page) " of " counter(pages); font-family: Arial, sans-serif; font-size: 9pt; color: #888; } @bottom-left { content: "${issuerName.replace(/"/g, '\\"')} · Quotation"; font-family: Arial, sans-serif; font-size: 9pt; color: #888; } }
  body { font-family: Arial, sans-serif; color: #1a1a1a; padding: 4px; }
  .brand-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #ED1C24; padding-bottom: 14px; margin-bottom: 18px; }
  .brand-header img { max-width: 200px; height: auto; }
  .brand-block { text-align: right; font-size: 10.5pt; color: #4B5563; line-height: 1.4; }
  .brand-block .name { font-size: 15pt; font-weight: 800; color: #1a1a1a; }
  h1.quote-title { font-size: 20pt; color: #ED1C24; margin: 0 0 12px 0; }
  .meta-grid { display: grid; grid-template-columns: 140px 1fr; gap: 4px 12px; font-size: 11pt; margin-bottom: 18px; padding: 12px 14px; background: #F9FAFB; border-left: 4px solid #ED1C24; border-radius: 4px; }
  .meta-grid .lbl { color: #6B7280; font-weight: 700; }
  .section-title { background: #1a1a1a; color: #fff; padding: 8px 12px; font-weight: 800; font-size: 12.5pt; margin: 22px 0 10px 0; border-radius: 4px; }
  .body-copy { font-size: 10.5pt; line-height: 1.55; color: #1f2937; text-align: justify; margin: 0 0 10px 0; }
  .totals-box { margin: 24px 0 0 auto; width: 55%; border: 2px solid #1a1a1a; border-radius: 6px; overflow: hidden; }
  .totals-row { display: flex; justify-content: space-between; padding: 8px 14px; font-size: 11.5pt; border-bottom: 1px solid #E5E7EB; }
  .totals-row.grand { background: #ED1C24; color: #fff; font-weight: 800; font-size: 13pt; border-bottom: none; }
  .terms { margin-top: 26px; padding: 12px 14px; background: #F9FAFB; border-radius: 6px; font-size: 9pt; line-height: 1.5; color: #374151; text-align: justify; }
  .peace { margin-top: 22px; padding: 14px 16px; border: 1px solid #ED1C24; border-radius: 6px; background: #FFF5F5; }
  .peace .head { color: #ED1C24; font-weight: 800; font-size: 12.5pt; margin-bottom: 6px; }
  .peace p { margin: 0; font-size: 10.5pt; line-height: 1.6; color: #1f2937; text-align: justify; }
</style></head><body>

  <div class="brand-header">
    <img src="${issuerLogo}" alt="${escapeHtml(issuerName)}" />
    <div class="brand-block">
      <div class="name">${escapeHtml(issuerName)}</div>
      ${issuerAddress ? `<div>${escapeHtml(issuerAddress).replace(/\n/g, '<br/>')}</div>` : ''}
      ${issuerContact ? `<div>${escapeHtml(issuerContact)}</div>` : ''}
      ${contractor.vat_number ? `<div style="font-size:9.5pt;">VAT ${escapeHtml(contractor.vat_number)}</div>` : ''}
    </div>
  </div>

  <h1 class="quote-title">Quotation &ndash; ${escapeHtml(siteName)}</h1>

  <div class="meta-grid">
    <div class="lbl">Quote Reference</div><div><strong>${escapeHtml(displayRef)}</strong></div>
    <div class="lbl">Client</div><div>${escapeHtml(clientName)}</div>
    <div class="lbl">Date</div><div>${created}</div>
    ${customerAddress ? `<div class="lbl">Address</div><div>${escapeHtml(customerAddress).replace(/\n/g, '<br/>')}</div>` : ''}
    ${validUntil ? `<div class="lbl">Valid Until</div><div>${validUntil}</div>` : ''}
    ${rmPaymentTerms !== null ? `<div class="lbl">Payment terms</div><div>${rmPaymentTerms} day${rmPaymentTerms === 1 ? '' : 's'} from invoice</div>` : ''}
  </div>

  ${tplIntro ? `
    <div style="margin: 0 0 18px 0; padding: 12px 14px; background:#F8FAFC; border-left:4px solid #ED1C24; border-radius:6px; font-size:10.5pt; color:#1a1a1a; line-height:1.55; white-space:pre-wrap; text-align: justify;">${escapeHtml(tplIntro)}</div>
  ` : ''}

  <div class="section-title">Description</div>
  <p class="body-copy"><strong>Legal compliance</strong> &mdash; All remediation work will be completed/monitored by fire door specialists to BS 8214 standards, utilizing fully fire-rated, certified, and intumescent components. All necessary safety data will be logged throughout the installation in order to issue a comprehensive Compliance Report for legal/insurance purposes.</p>
  <p class="body-copy"><strong>Point of work inspection</strong> &mdash; Ease and adjust door to centre, adjust overhead door closer to ensure door latches properly, carry out a pre-work certified fire door inspection, to assess whether less intrusive frame build outs or specialist products can make door compliant with regulations, therefore reducing disruption, dust and potential re-decoration, for tenants and/or landlord. If alternative course of action not feasible then continue with frame reset.</p>

  <div class="section-title">Contractor Competence &amp; Client Liability</div>
  <p class="body-copy">Property managers and owners hold the legal responsibility to appoint competent persons who utilize safe systems of work and provide full statutory documentation. ${escapeHtml(issuerName)} fulfils these strict requirements at highly competitive rates. Selecting an alternative contractor based solely on a significantly lower price introduces substantial risk regarding their legal competence, potentially leaving the client liable for any subsequent operational or safety failures.</p>

  <div class="section-title">Scope of Works</div>
  ${doorSections || '<p class="body-copy" style="text-align:center; color:#6B7280;">No door-level items have been added to this quote yet.</p>'}
  ${nonDoorSection}
  ${renderReplacementScopeHtml(quote, mc)}

  <div class="totals-box">
    ${tplMode === 'labour_materials' ? (() => {
      // Iter-108bg — Labour / Materials subtotals sit above the Sub-total
      // when the company template is set to "Labour + Materials".
      let labourSum = 0;
      let materialsSum = 0;
      (quote.items || []).forEach((it: any) => {
        const qty = it.quantity || 1;
        const sp = splitLine(it);
        labourSum += sp.labour * qty;
        materialsSum += sp.materials * qty;
      });
      return `
        <div class="totals-row"><span>Labour</span><span>£${labourSum.toFixed(2)}</span></div>
        <div class="totals-row"><span>Materials</span><span>£${materialsSum.toFixed(2)}</span></div>
      `;
    })() : ''}
    <div class="totals-row"><span>Sub-total</span><span>£${(Number(quote.subtotal) || 0).toFixed(2)}</span></div>
    ${(Number(quote.discount_amount) || 0) > 0 ? `<div class="totals-row" style="color:#047857;"><span>${escapeHtml(String(quote.discount_label || 'Discount'))}</span><span>&minus;£${(Number(quote.discount_amount) || 0).toFixed(2)}</span></div>` : ''}
    <div class="totals-row"><span>Plus VAT @ 20%</span><span>£${(Number(quote.vat) || 0).toFixed(2)}</span></div>
    <div class="totals-row grand"><span>Total</span><span>£${(Number(quote.total) || 0).toFixed(2)}</span></div>
  </div>

  ${quote.notes ? `
    <div class="section-title">Notes</div>
    <p class="body-copy">${escapeHtml(String(quote.notes)).replace(/\n/g, '<br/>')}</p>
  ` : ''}

  ${photoAppendixHtml}

  <div class="section-title">Terms and Conditions</div>
  <div class="terms">${termsHtml}</div>

  ${tplFooter ? `
    <div style="margin-top: 18px; padding: 14px 16px; background:#F9FAFB; border-left:4px solid #6B7280; border-radius:6px; font-size:10.5pt; color:#374151; line-height:1.55; white-space:pre-wrap; text-align: justify;">${escapeHtml(tplFooter)}</div>
  ` : ''}

  ${bankHtml}

  <div class="peace">
    <div class="head">Compliance, Quality &amp; Peace of Mind</div>
    <p>Navigating fire safety legislation can be complex, but ensuring your properties are fully compliant is our job. When you partner with ${escapeHtml(issuerName)}, you are gaining complete peace of mind. We handle all the strict safe systems of work and comprehensive documentation required by law, protecting you and your business from the liabilities of non-compliant work. We&rsquo;ve got your back every step of the way, and we look forward to keeping your buildings safe, secure, and fully covered. You can rely on us.</p>
  </div>

  <!-- Iter-108u — RM York branded footer with registered office, contacts and waste-carrier licence -->
  <div style="margin-top: 26px; padding-top: 14px; border-top: 4px solid #ED1C24; border-bottom: 3px solid #ED1C24; padding-bottom: 12px;">
    <table style="width: 100%; border-collapse: collapse; font-size: 10pt; color: #1f2937;">
      <tr>
        <td style="vertical-align: top; padding: 8px 4px; width: 30%;">
          <div style="font-weight: 800; color: #1a1a1a;">Registered Office:</div>
          <div>63-65 Heworth Road York</div>
          <div>YO31 0AA.</div>
        </td>
        <td style="vertical-align: top; padding: 8px 4px; width: 35%;">
          <div style="display: flex; align-items: flex-start; gap: 8px;">
            <span style="color:#ED1C24; font-size: 14pt; line-height: 1;">&#9993;</span>
            <div>
              <div><a href="mailto:dave@rmyork.co.uk" style="color:#0066cc; text-decoration:underline;">dave@rmyork.co.uk</a></div>
              <div><a href="mailto:alex@rmyork.com" style="color:#0066cc; text-decoration:underline;">alex@rmyork.com</a></div>
            </div>
          </div>
        </td>
        <td style="vertical-align: top; padding: 8px 4px; width: 35%; text-align: right;">
          <div style="display: inline-flex; align-items: flex-start; gap: 8px;">
            <div>
              <div style="font-weight: 700;">0787899537</div>
              <div style="font-weight: 700;">0782451332</div>
            </div>
            <span style="color:#ED1C24; font-size: 14pt; line-height: 1;">&#128241;</span>
          </div>
        </td>
      </tr>
    </table>
    <div style="display:flex; justify-content: space-between; align-items: center; margin-top: 10px; padding-top: 8px; border-top: 1px solid #E5E7EB; font-size: 9.5pt; color: #374151;">
      <span><strong>RM YORK LTD:</strong> 13521002</span>
      <span><strong>Waste Carrier Registration No:</strong> CBDU293109</span>
      <span><strong>All waste to be disposed of in a controlled manner.</strong></span>
    </div>
  </div>

</body></html>`;
  },

  // Generate single door inspection report (FD Check template — BS 8214:2026)
  //
  // Iter-108bt — Post-work inspection contractor branding.
  // When `inspection.is_post_work` is true AND a linked contractor is
  // resolved on the record (backend inspections.getOne populates
  // `inspection.contractor` from the linking maintenance job), the
  // masthead, page footer, page-header text and Signature block are
  // switched to the contractor's identity (e.g. RM York Ltd). The FD
  // Check role is downgraded to a small "Produced using the FD Check
  // app" line so it's clear the inspection was carried out by the
  // contractor — not by an FD Check inspector.
  generateDoorReport: async (data: InspectionReport) => {
    const { building, door, inspection } = data as any;
    const includeCompliance = data.includeCompliance !== false;

    const postWorkContractor = (inspection?.is_post_work && inspection?.contractor)
      ? inspection.contractor
      : null;
    const brandLogo = postWorkContractor?.logo_base64 || LOGO_BASE64;
    const brandName = postWorkContractor?.name || 'FD Check Ltd';
    // Contact line for the page footer — either the contractor's own
    // details (address · phone · email) or the FD Check standard line.
    const brandContactLine = postWorkContractor
      ? [postWorkContractor.address, postWorkContractor.phone, postWorkContractor.email]
          .map((v) => (v || '').trim()).filter(Boolean).join(' · ')
      : FD_OFFICE_LINE;

    // Iter-108d — the door-report generator now handles two shapes:
    //   1. Full inspection present → the historical BS 8214 layout.
    //   2. No inspection yet → a lighter "survey" report showing the
    //      door's captured metadata + door photo + any door notes.
    // This exists because inspectors kept getting the blocking "No
    // inspection records" alert when they'd captured a door from
    // outside (NO ACCESS) or during a survey but hadn't gone through
    // the full inspection form yet — the client still needs to see
    // what was captured on site.
    const isUninspected = !inspection;
    const inspectionDate = inspection
      ? format(new Date(inspection.inspection_date), 'dd/MM/yyyy HH:mm')
      : format(new Date(), 'dd/MM/yyyy HH:mm');
    const cd = inspection?.checklist_data || {};
    const ck = cd.custom_items || {};

    const statusColor = ({ pass: '#10B981', fail: '#EF4444', review: '#F59E0B', no_access: '#6B7280' } as any)[inspection?.status] || '#6B7280';

    // Iter-163i — Suppress the Inspection Checklist table, Gap-
    // measurements table and gap-summary box on the single-door PDF
    // whenever the doorset is being fully replaced. The individual
    // pass/fail rows contradicted the "FULL DOORSET REPLACEMENT"
    // statement further down — property managers were seeing a
    // Doorset Replacement Required banner AND a table of individual
    // component passes on the same page.
    const needsReplacement = doorsetNeedsReplacement(inspection);

    // Build inspection-item table rows following the docx layout
    const yes = '<span style="color:#10B981;font-weight:bold;font-size:14px">✓</span>';
    const no  = '<span style="color:#EF4444;font-weight:bold;font-size:14px">✗</span>';
    const na  = '<span style="color:#6B7280;font-weight:bold">N/A</span>';
    const mark = (v: any) => {
      if (v === true) return yes;
      if (v === false) return no;
      if (v === 'Yes') return yes;
      if (v === 'No') return no;
      if (v === 'N/A' || v === 'Unknown' || v == null) return na;
      return na;
    };
    const isFail = (v: any) => v === false || v === 'No';

    // Iter-108g — build a lookup so each checklist row can show the
    // ACTUAL reasons the inspector picked (from failed_component_photos
    // notes), instead of a generic hardcoded "Frame damaged" line.
    const failedByKey = new Map<string, any>();
    (inspection?.failed_component_photos || []).forEach((fc: any) => {
      if (fc?.key) failedByKey.set(fc.key, fc);
    });

    // Parse the "• reason" bullets a persistInspection() emits back
    // into a clean array. Drops the structured letterbox size / reason
    // sub-lines so the checklist column doesn't repeat those (they
    // live in the dedicated letterbox block further down the PDF).
    const parseFcReasons = (notes: string | undefined): string[] => {
      if (!notes) return [];
      return String(notes)
        .split('\n')
        .map(l => l.trim())
        .filter(l => l.startsWith('•'))
        .map(l => l.replace(/^•\s*/, '').trim())
        .filter(b =>
          !!b
          && !b.toLowerCase().startsWith('size of opening:')
          && !b.toLowerCase().startsWith('reason for failure:')
        );
    };
    const fmtReasons = (reasons: string[]): string => {
      if (reasons.length === 0) return '';
      if (reasons.length === 1) return escapeHtml(reasons[0]);
      return `<ul style="margin:0; padding-left:16px;">${
        reasons.map(r => `<li>${escapeHtml(r)}</li>`).join('')
      }</ul>`;
    };

    // Iter-108g — each row now carries its checklist key so the
    // renderer can pull the matching failed_component_photos entry and
    // display the actual reasons + free-text the inspector selected.
    const rows: {label: string; key?: string; value: any; failTxt: string; action: string}[] = [
      { label: 'Door identification label / certification', key: 'door_label_present', value: ck.door_label_present, failTxt: 'Fire door certification could not be verified at the time of inspection.', action: 'Verify label or replace door' },
      { label: 'Door frame secure, correctly installed, and free from damage', key: 'door_frame_secure', value: ck.door_frame_secure, failTxt: 'Frame loose, damaged, or not adequately fixed', action: 'Secure or replace frame and ensure correct installation' },
      { label: 'Door leaf in good condition and operates correctly', key: 'door_operates_correctly', value: ck.door_operates_correctly, failTxt: 'Door damaged or does not operate correctly', action: 'Adjust or replace door leaf' },
      { label: 'Door to frame gaps within acceptable tolerances (2–4mm)', key: '__gap_tolerance__', value: cd.gap_within_tolerance, failTxt: 'Gaps excessive, uneven or outside tolerance', action: 'Adjust hinges, realign door, or remedial works' },
      // Iter-108ah — Threshold gap is a distinct fail item so it can be
      // priced separately from perimeter (top/hinge/lock) gaps. When
      // `threshold_gap_within_tolerance` is undefined on legacy
      // inspections the row stays hidden (undefined ≠ false in isFail).
      { label: 'Threshold gap within tolerance (≤ 4 mm at bottom of door)', key: '__threshold_gap__', value: cd.threshold_gap_within_tolerance, failTxt: 'Threshold gap over 4 mm', action: 'Fit dropdown seal / threshold bar, adjust or replace existing seal' },
      { label: 'Intumescent and smoke seals are present, continuous, and secure', key: 'smoke_fire_seals_intact', value: ck.smoke_fire_seals_intact, failTxt: 'Seals missing, damaged or poorly fitted', action: 'Install or replace seals' },
      { label: 'Smoke seals provide effective contact', key: 'smoke_seal_test_pass', value: ck.smoke_seal_test_pass, failTxt: 'Smoke seals not effective where required', action: 'Replace seals' },
      { label: 'Fire-resisting glazing intact', key: 'glass_intact', value: ck.glass_intact, failTxt: 'Glazing damaged or missing', action: 'Replace glazing per fire-rated specification' },
      { label: 'Glazing beads secure and suitable', key: 'window_beads_secure', value: ck.window_beads_secure, failTxt: 'Beads missing or insecure', action: 'Refit or replace glazing beads' },
      { label: 'Ironmongery secure and functioning', key: 'handle_lock_secure', value: ck.handle_lock_secure, failTxt: 'Ironmongery fitted not confirmed as fire-rated', action: 'Repair or replace ironmongery' },
      { label: 'Door closer closes door fully', key: 'door_closer_working', value: ck.door_closer_working, failTxt: 'Closer not closing door fully or latching', action: 'Adjust or replace closer' },
      { label: 'Hinges fitted, secure and correct (3 hinges)', key: 'three_hinges_fitted', value: ck.three_hinges_fitted, failTxt: 'Number/type of hinges is not compliant with fire door requirements', action: 'Fit three correct fire-rated hinges' },
      { label: 'Door-to-stop bead in place and secure', key: 'door_to_stop_bead', value: ck.door_to_stop_bead, failTxt: 'Stop bead missing or damaged', action: 'Install or secure stop bead' },
      { label: 'Signage present and visible', key: 'signage_in_place', value: ck.signage_in_place, failTxt: 'Signage missing or incorrect', action: 'Install correct signage (Keep Locked / Shut)' },
      { label: 'Push/kick plates secure', key: 'push_plates_secure', value: ck.push_plates_secure, failTxt: 'Plates loose or damaged', action: 'Secure or replace plates' },
      { label: 'Drop-down seal functional', key: 'dropdown_seal', value: ck.dropdown_seal, failTxt: 'Drop-down seal not functioning', action: 'Adjust or replace drop-down seal' },
      { label: 'Spy viewer compliant' + (ck.spy_viewer === 'Yes' && cd.spy_viewer_fire_rated ? ` (fire-rated: ${cd.spy_viewer_fire_rated})` : ''), key: 'spy_viewer', value: ck.spy_viewer, failTxt: 'Spy viewer not fire-rated', action: 'Replace with fire-rated viewer' },
    ];

    // Iter-108ac / 108ag — separate the FAILURE REASON (measurements
    // over tolerance) from the RECOMMENDED REPAIR (BS 8214 title +
    // action text). Previously both were dumped into the Fail column
    // which produced the long "Threshold strip OR automatic drop-down
    // seal — Under-door gap reaches 7 mm…" block Dave flagged.
    //
    //   • gapFailReasons  → raw over-tolerance point list
    //                       (goes in the "Fail (if ✗)" column)
    //   • gapRecommend    → BS 8214 recommendation title + action
    //                       (goes in the "Recommended Action" column)
    //
    // Data source order (populate whichever we can find):
    //   1. cd.gap_measurements dict → newer app builds
    //   2. cd.gap_top_left / gap_hinge_top / gap_latch_* / gap_under_*
    //      legacy fields → shipped app builds
    //   3. Parse the "measurement fact" from each cd.gap_recommendations
    //      entry when neither of the above are present.
    const gapFailReasons: string[] = (() => {
      const out: string[] = [];
      const gm = cd.gap_measurements || {};
      const posLookup: Record<string, string> = {
        lock_top: 'Lock side — top', lock_mid: 'Lock side — middle', lock_bot: 'Lock side — bottom',
        bot_lock: 'Bottom — lock side', bot_mid: 'Bottom — middle', bot_hinge: 'Bottom — hinge side',
        hinge_bot: 'Hinge side — bottom', hinge_mid: 'Hinge side — middle', hinge_top: 'Hinge side — top',
        top_hinge: 'Top — hinge side', top_mid: 'Top — middle', top_lock: 'Top — lock side',
      };
      // Path 1 — new dict shape
      Object.entries(gm).forEach(([k, v]) => {
        const n = parseFloat(String(v || '0'));
        if (!isNaN(n) && n > 4) {
          const label = posLookup[k] || k;
          out.push(`${label}: ${n} mm (max 4 mm)`);
        }
      });
      // Path 2 — legacy flat fields (only if path 1 produced nothing)
      if (out.length === 0) {
        const legacyAliases: Record<string, string> = {
          gap_top_left:   'Top — hinge side',
          gap_top_center: 'Top — middle',
          gap_top_right:  'Top — lock side',
          gap_hinge_top:  'Hinge side — top',
          gap_hinge_center: 'Hinge side — middle',
          gap_hinge_bottom: 'Hinge side — bottom',
          gap_latch_top:    'Lock side — top',
          gap_latch_center: 'Lock side — middle',
          gap_latch_bottom: 'Lock side — bottom',
          gap_under_hinge:  'Bottom — hinge side',
          gap_under_center: 'Bottom — middle',
          gap_under_lock:   'Bottom — lock side',
        };
        Object.entries(legacyAliases).forEach(([field, label]) => {
          const n = parseFloat(String(cd[field] ?? ''));
          if (!isNaN(n) && n > 4) {
            out.push(`${label}: ${n} mm (max 4 mm)`);
          }
        });
      }
      // Path 3 — no raw measurements available but we have BS 8214
      // recommendations with the measurement fact baked into `detail`.
      // Each detail typically reads "Hinge side top 5.5 mm + lock side
      // top 3 mm = 8.5 mm (max 8 mm). The leaf is too narrow …". Split
      // at the first sentence break AFTER the closing "(max … mm)" so
      // we keep only the measurement half.
      if (out.length === 0) {
        const recs: any[] = Array.isArray(cd.gap_recommendations) ? cd.gap_recommendations : [];
        recs.forEach(r => {
          const detail = String(r?.detail || '').trim();
          if (!detail) return;
          // Grab everything up to and including the "(max X mm)."
          // If the pattern isn't there, fall back to the first sentence.
          const capMatch = detail.match(/^(.*?\(max\s*\d+(?:\.\d+)?\s*mm\)\.?)/i);
          const fact = capMatch ? capMatch[1] : detail.split(/\.\s+/)[0];
          if (fact) out.push(fact.trim().replace(/\.+$/, ''));
        });
      }
      return out;
    })();
    const gapRecommendActions: string[] = (() => {
      // Iter-108bk — Dedupe by title. Historic inspections (saved before
      // Iter-108bk) may have three "Framefit OR frame reset" entries in
      // cd.gap_recommendations — one per height that failed. Merge them
      // into ONE line so the "Recommended Action" column doesn't repeat
      // the same instruction three times.
      const out: string[] = [];
      const seenTitles = new Set<string>();
      const recs: any[] = Array.isArray(cd.gap_recommendations) ? cd.gap_recommendations : [];
      recs.forEach(r => {
        const title = String(r?.title || '').trim();
        const detail = String(r?.detail || '').trim();
        if (!title && !detail) return;
        // Skip if we already have a line with the same title. First-seen
        // wins so the earliest-height's detail is what surfaces.
        if (title && seenTitles.has(title.toLowerCase())) return;
        // Strip the measurement fact out of the detail if present so
        // the Action column reads like a repair instruction, not a
        // measurement re-print.
        const strippedDetail = detail.replace(
          /^.*?\(max\s*\d+(?:\.\d+)?\s*mm\)\.?\s*/i,
          '',
        ).trim();
        const line = title
          ? `${title}${strippedDetail ? ` — ${strippedDetail}` : ''}`
          : strippedDetail;
        if (line) {
          out.push(line);
          if (title) seenTitles.add(title.toLowerCase());
        }
      });
      return out;
    })();

    const itemTable = rows.map(r => {
      const failed = isFail(r.value);
      // Iter-108ac — reasons in the "Fail" column, recommended repair
      // in the "Recommended Action" column (was previously merged in
      // one column). Special-cased gap-tolerance row pulls from the
      // two split arrays above.
      let failCell = '—';
      let actionCell = failed ? r.action : '—';
      if (failed) {
        if (r.key === '__gap_tolerance__') {
          failCell = gapFailReasons.length ? fmtReasons(gapFailReasons) : r.failTxt;
          if (gapRecommendActions.length) {
            actionCell = fmtReasons(gapRecommendActions);
          }
        } else if (r.key === '__threshold_gap__') {
          // Iter-108ah — pull threshold reasons and any FCP notes so
          // the Fail column shows the picked reasons (which reads as
          // a clear failure description) while the Action column
          // keeps the generic remedy summary.
          const fc = failedByKey.get('threshold_gap');
          const reasons = fc ? parseFcReasons(fc?.notes) : (Array.isArray(cd.threshold_gap_reasons) ? cd.threshold_gap_reasons : []);
          failCell = reasons.length ? fmtReasons(reasons) : r.failTxt;
        } else if (r.key && failedByKey.has(r.key)) {
          const fc = failedByKey.get(r.key);
          const reasons = parseFcReasons(fc?.notes);
          failCell = reasons.length ? fmtReasons(reasons) : r.failTxt;
          // Iter-188 — double doors: say which leaf the fault is on.
          const leafTag = leafSuffix(fc?.leaf, isDoubleDoor(door, inspection)).replace(/^ — /, '');
          if (leafTag) failCell = `<div style="font-size:9pt; font-weight:800; color:#7C2D12; margin-bottom:2px;">${escapeHtml(leafTag)}</div>${failCell}`;
        } else {
          failCell = r.failTxt;
        }
      }
      return `<tr${failed ? ' style="background:#FEF2F2"' : ''}>
        <td>${r.label}</td>
        <td style="text-align:center;width:80px">${mark(r.value)}</td>
        <td>${failCell}</td>
        <td>${actionCell}</td>
      </tr>`;
    }).join('');

    // Gap-measurement table + door diagram. Numbers match the inspector's
    // gap diagram (single 1–12, double 1–15). All points are shown (not
    // just failures); out-of-tolerance readings (> 4 mm) are highlighted.
    // A fitted dropdown seal skips the single-leaf under-door points 4/5/6.
    const gm = cd.gap_measurements || {};
    const isDoubleLeaf = cd.door_leaf_type === 'double';
    const dropdownSeal = cd.threshold_gap_bypassed_by_seal === true;
    const { rows: diagramRows, threshold: thresholdRows } = gapRowsFor({ double: isDoubleLeaf, dropdownSeal });
    const gapMeasured: number[] = [];
    const failedNums = new Set<number>();
    const readVal = (k: string): number => {
      const v = gm[k];
      return typeof v === 'string' ? parseFloat(v) : (typeof v === 'number' ? v : NaN);
    };
    const renderGapRow = (r: GapRow): string => {
      const n = readVal(r.key);
      const has = !isNaN(n);
      if (has) gapMeasured.push(n);
      const outOfTol = has && n > 4;
      if (outOfTol && r.num > 0) failedNums.add(r.num);
      const valCell = has ? `${n} mm` : '—';
      const style = outOfTol ? ' style="background:#FEF2F2;color:#EF4444;font-weight:700"' : '';
      const numCell = r.num > 0 ? String(r.num) : '—';
      return `<tr${style}><td style="text-align:center">${numCell}</td><td>${r.pos}</td><td style="text-align:center">${valCell}</td></tr>`;
    };
    const gapNumberedRows = diagramRows.map(renderGapRow);
    const gapThresholdRows = thresholdRows.map(renderGapRow);
    const gapTable = gapNumberedRows.length
      ? gapNumberedRows.join('') + (gapThresholdRows.length
          ? `<tr><td colspan="3" style="background:#F3F4F6;font-weight:700;color:#374151">Threshold / under-door (not numbered on diagram)</td></tr>` + gapThresholdRows.join('')
          : '')
      : `<tr><td colspan="3" style="text-align:center; color:#6B7280;">No gap measurements recorded.</td></tr>`;
    const largestGap = gapMeasured.length > 0 ? Math.max(...gapMeasured) : 0;
    const withinTol = gapMeasured.every(v => v <= 4);
    const gapDiagramSvg = buildDoorDiagramSvg({ double: isDoubleLeaf, dropdownSeal, failedNums });

    // All photos at ~50×40mm (189×151px @96dpi)
    // Iter-108g — preserve aspect ratio. `object-fit: contain` scales
    // the photo down to fit within the fixed box without cropping;
    // pillar/letterbox space stays white and inspectors see the whole
    // shot. `cover` was cropping edges (esp. portrait phone shots).
    const photoStyle = 'width:50mm;height:40mm;object-fit:contain;border-radius:4px;border:1px solid #e0e0e0;margin:2mm;background:#fafafa';

    const allPhotos: { caption: string; src: string }[] = [];
    if (door.door_photo) allPhotos.push({ caption: 'Door — overview', src: door.door_photo });
    if (inspection?.label_photo) allPhotos.push({ caption: 'Fire door label', src: inspection.label_photo });
    // Iter-108d — support multi-photo failed_component_photos: prefer
    // fc.photos[] (post iter-107) but fall back to legacy fc.photo.
    (inspection?.failed_component_photos || []).forEach((f: any) => {
      const list: string[] = Array.isArray(f?.photos) && f.photos.length > 0
        ? f.photos.filter(Boolean)
        : (f?.photo ? [f.photo] : []);
      list.forEach((src, i) => allPhotos.push({
        caption: `FAIL: ${f.question}${list.length > 1 ? ` (${i + 1}/${list.length})` : ''}`,
        src,
      }));
    });
    (inspection?.photos || []).forEach((p: string, i: number) => allPhotos.push({ caption: 'Photo ' + (i+1), src: p }));

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      @page {
        size: A4;
        margin: 12mm 12mm 18mm 12mm;
        @bottom-right {
          content: "Page " counter(page) " of " counter(pages);
          font-family: Arial, sans-serif;
          font-size: 9pt;
          color: #888;
        }
        @bottom-left {
          content: "${postWorkContractor ? escapeHtml(brandName) : 'FD Check Ltd'} · Single Door Report";
          font-family: Arial, sans-serif;
          font-size: 9pt;
          color: #888;
        }
      }
      .page-break { page-break-before: always; break-before: page; }
      .avoid-break { page-break-inside: avoid; break-inside: avoid; }
      body { font-family: Arial, sans-serif; color: #1a1a1a; font-size: 11pt; }
      .brand-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #ED1C24; padding-bottom: 10px; margin-bottom: 16px; }
      .brand-header img { max-width: 180px; }
      .status-badge { display: inline-block; padding: 6px 14px; border-radius: 14px; color: #fff; background: ${statusColor}; font-weight: bold; text-transform: uppercase; font-size: 11pt; }
      h2 { color: #1a1a1a; margin: 4px 0; }
      .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 18px; margin-bottom: 14px; font-size: 11pt; }
      .meta div { padding: 2px 0; }
      .meta strong { color: #ED1C24; }
      .section-title { background: #f5f5f5; padding: 8px 12px; font-weight: bold; border-left: 4px solid #ED1C24; margin: 18px 0 8px 0; }
      table { width: 100%; border-collapse: collapse; font-size: 10pt; }
      th { background: #1a1a1a; color: #fff; padding: 8px; text-align: left; border: 1px solid #1a1a1a; }
      td { padding: 6px 8px; border: 1px solid #ddd; vertical-align: top; }
      .gap-summary { background: ${withinTol ? '#ECFDF5' : '#FEF2F2'}; border-left: 4px solid ${withinTol ? '#10B981' : '#EF4444'}; padding: 8px 12px; margin: 10px 0; }
      .photo-grid { display: flex; flex-wrap: wrap; gap: 1mm; margin-top: 8px; }
      .photo-cell { display: flex; flex-direction: column; align-items: center; }
      .photo-caption { font-size: 8pt; color: #555; text-align: center; max-width: 50mm; margin-top: 1mm; }
      .photo-note { font-size: 8.5pt; color: #1F2937; font-weight: 600; text-align: center; max-width: 50mm; margin-top: 0.5mm; line-height: 1.25; }
      .signature-line { font-family: 'Snell Roundhand', cursive; font-style: italic; font-size: 18pt; border-bottom: 1px solid #999; padding-bottom: 4px; margin-bottom: 4px; }
      .footer { margin-top: 30px; padding-top: 12px; border-top: 2px solid #e0e0e0; text-align: center; color: #666; font-size: 9pt; }
      .footer img { max-width: 100%; margin-top: 10px; }
      .no-access-banner { background: #FEF3C7; border-left: 4px solid #F59E0B; padding: 12px; margin: 10px 0; }
      .custom-block { background: #FAFAFA; border-left: 3px solid #ED1C24; padding: 10px 14px; margin: 6px 0 14px 0; line-height: 1.45; font-size: 10.5pt; color: #1f2937; }
    </style></head><body>

      <div class="brand-header">
        <img src="${brandLogo}" alt="${escapeHtml(brandName)}" />
        <div style="text-align: right">
          <h2>${isUninspected ? 'Door Survey Report' : (postWorkContractor ? 'Post-Work Fire Door Inspection' : 'Fire Door Inspection Report')}</h2>
          <span class="status-badge">${isUninspected ? 'UNINSPECTED' : String(inspection.status).replace('_', ' ')}</span>
          <p style="margin: 4px 0 0 0; font-size: 10pt; color: #666">Generated ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
        </div>
      </div>

      <div class="meta">
        <div><strong>Building:</strong> ${building.name}</div>
        <div><strong>Address:</strong> ${building.address}, ${building.postcode}</div>
        <div><strong>Door Location:</strong> ${door.location || '—'}</div>
        <div><strong>Unique Door Reference:</strong> ${door.door_reference}</div>
        <div><strong>Floor Level:</strong> ${normaliseFloorLabel(door.floor) || '—'}${door.apartment_number ? ' · Apt ' + door.apartment_number : ''}</div>
        <div><strong>Fire Rating:</strong> ${door.fd_rating || '—'} · ${door.door_leaf_type === 'double' ? 'Double' : 'Single'}</div>
        <div><strong>${isUninspected ? 'Report Generated:' : 'Inspection Date:'}</strong> ${inspectionDate}</div>
        <div><strong>${isUninspected ? 'Captured By:' : 'Inspector:'}</strong> ${inspection?.inspector_name || door.created_by_name || '—'}</div>
      </div>

      ${includeCompliance ? renderComplianceIntroHtml() : ''}

      ${renderTopSections(data.customSections)}

      ${isUninspected ? `
      <div class="no-access-banner">
        <strong>NO INSPECTION RECORDED YET</strong> — this report captures what was observed on site during initial survey / no-access visits. A full BS 8214:2026 inspection has not been carried out for this door.
        ${door.notes ? `<br/><br/><strong>Site notes:</strong> ${door.notes}` : ''}
      </div>
      ` : ''}

      ${!isUninspected && cd.no_access ? `
      <div class="no-access-banner">
        <strong>NO ACCESS</strong> — inspector observation from outside the door:<br/>
        ${cd.no_access_note || ''}
      </div>
      ` : (needsReplacement ? '' : (!isUninspected ? `

      <div class="gap-summary">
        <strong>Within Tolerance (max 4 mm):</strong> ${withinTol ? '✓ YES' : '✗ NO'} &nbsp;&nbsp;
        <strong>Largest Gap:</strong> ${largestGap.toFixed(1)} mm
      </div>

      <div class="section-title">Inspection Checklist</div>
      <table>
        <thead>
          <tr><th>Inspection Item</th><th style="width:80px;text-align:center">Status</th><th>Fail (if ✗)</th><th>Recommended Action</th></tr>
        </thead>
        <tbody>${itemTable}</tbody>
      </table>

      <div class="section-title">Door-to-Frame Gap Measurements (mm)</div>
      <div style="text-align:center; margin: 6px 0 10px 0;">
        <div style="display:inline-block; width:200px; vertical-align:top;">${gapDiagramSvg}</div>
        <div style="font-size:8pt;color:#6B7280;margin-top:2px;">Measurement point reference${isDoubleLeaf ? ' — double leaf' : ''}${dropdownSeal ? ' · dropdown seal fitted, under-door not measured' : ''}</div>
      </div>
      <table>
        <thead><tr><th style="width:55px;text-align:center">Point</th><th>Position</th><th style="width:110px;text-align:center">Gap (mm)</th></tr></thead>
        <tbody>${gapTable}</tbody>
      </table>
      <div style="font-size:8.5pt;color:#6B7280;margin-top:6px;">Target perimeter gap 2–4&nbsp;mm; readings over 4&nbsp;mm are highlighted and require remedial action.</div>
      ` : ''))}

      ${!isUninspected && cd.additional_observations ? `<div class="section-title">Additional Observations</div><p>${cd.additional_observations}</p>` : ''}
      ${renderCustomAnswersHtml(inspection)}
      ${inspection?.notes ? `<div class="section-title">Notes from Inspector</div><p>${inspection.notes}</p>` : ''}

      ${(() => {
        // Phase 5b — surface the failure-reason chips the inspector picked for
        // each failed component. Mirrors what's already in the building report
        // mini-cards but as a structured list on the single-door PDF.
        //
        // Iter-110ak — Partition into HARD FAILS (red) vs ADVISORIES
        // (blue) using the canonical DEFECT_RISK map. Same fix as the
        // mini-card renderer above: web-form inspections never
        // stamped `advisory: true` on Low-risk items like the
        // certification label, so rendering everything red produced
        // the "some red, some blue" inconsistency on the Darlington
        // report. Deriving from `riskFor(fc.key)` guarantees the same
        // outcome no matter which client wrote the inspection.
        const failed = ((inspection?.failed_component_photos || []) as { key?: string; question: string; photo?: string; notes?: string; advisory?: boolean; severity?: string; risk?: string; leaf?: string | null }[]).filter((fc: any) => !isSuppressedOptionalFcp(fc));
        if (failed.length === 0) return '';
        const isAdvisoryEntry = (fc: any): boolean => {
          // Iter-227 — evidence-backed optional fail (spy viewer / letterbox
          // present) is a hard fail; overrides stale advisory/observation/Low.
          if (isEvidenceBackedOptionalFail(fc)) return false;
          // Iter-110ak — Explicit hard-fail override wins.
          if (fc?.advisory === false) return false;
          if (fc?.needs_replacing === true) return false;
          if (fc?.severity === 'critical') return false;
          if (fc?.advisory === true) return true;
          if (fc?.severity === 'observation') return true;
          const risk = (fc?.risk as string) || riskFor(fc?.key || '');
          return risk === 'Low';
        };
        const hardFails = failed.filter((fc: any) => !isAdvisoryEntry(fc));
        const advisories = failed.filter((fc: any) => isAdvisoryEntry(fc));

        // Iter-163f — Same doorset-replacement override as the mini-
        // card renderer. When the inspector has ticked "This door
        // needs replacing", the individual component failures /
        // advisories are suppressed and a single overriding statement
        // is rendered on the single-door PDF report too.
        if (doorsetNeedsReplacement(inspection)) {
          return `
          <div class="section-title">Doorset Replacement Required</div>
          <div style="padding:12px 14px; background:#FEF2F2; border:1.5px solid #DC2626; border-left:6px solid #B91C1C; border-radius:6px;">
            <div style="display:inline-block; background:#B91C1C; color:#fff; font-size:10pt; font-weight:800; letter-spacing:0.5px; padding:3px 10px; border-radius:4px; margin-bottom:8px;">FULL DOORSET REPLACEMENT</div>
            <div style="font-size:11pt; color:#1a1a1a; line-height:1.55;">${DOORSET_REPLACEMENT_STATEMENT}</div>
          </div>
          ${renderDoorReplacementDetails(inspection, 'full')}
          `;
        }

        return `
          ${hardFails.length > 0 ? `
          <div class="section-title">Failed Components &mdash; Reasons</div>
          ${groupByLeaf(hardFails, isDoubleDoor(door, inspection)).map((g) => `
          ${g.heading ? `<div style="font-size:10.5pt; font-weight:800; color:#7C2D12; background:#FFF7ED; border-left:4px solid #EA580C; padding:5px 10px; border-radius:4px; margin:8px 0 6px;">${escapeHtml(g.heading)}</div>` : ''}
          <div style="display:flex; flex-direction:column; gap:8px;">
            ${g.items.map((fc: any) => {
              // Iter-163d — Surface bespoke inspector answers on
              // hard-fail cards as well as advisories. Consistent
              // treatment across every question — matches the
              // building-mini-card renderer.
              const customAnswers = extractInspectorCustomAnswers(fc.notes);
              const customAnswerBlock = customAnswers.length > 0 ? `
                <div style="margin-top:6px; padding:8px 10px; background:#F0F9FF; border-left:4px solid #0284C7; border-radius:6px;">
                  <div style="font-size:9.5pt; font-weight:800; color:#075985; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:4px;">Inspector's additional observation${customAnswers.length === 1 ? '' : 's'}</div>
                  ${customAnswers.map((a) => `<div style="font-size:10pt; color:#0C4A6E; line-height:1.5;">&bull; ${escapeHtml(a)}</div>`).join('')}
                </div>
              ` : '';
              return `
              <div style="border:1px solid #FCA5A5; border-left:4px solid #DC2626; background:#FEF2F2; border-radius:6px; padding:8px 10px;">
                <div style="font-size:11pt; font-weight:700; color:#991B1B;">${escapeHtml(fc.question)}</div>
                ${renderReasonChips(fc.notes, false, fc.key)}
                ${customAnswerBlock}
              </div>
            `;
            }).join('')}
          </div>
          `).join('')}
          ` : ''}
          ${advisories.length > 0 ? `
          <div class="section-title">Advisory Observations &mdash; Not a Failure</div>
          <div style="display:flex; flex-direction:column; gap:8px;">
            ${advisories.map((fc: any) => {
              const isCertLabel = fc.key === 'door_label_present';
              // Iter-163c — same custom-answer preservation as the
              // building mini-card renderer. Ensures anything the
              // inspector typed into "Add your own answer" reaches
              // the single-door PDF report too.
              const customAnswers = extractInspectorCustomAnswers(fc.notes);
              // Iter-163e — Canonical BS-8214 paragraph only fires
              // when the specific "Certification could not be
              // confirmed…" advisory box was ticked. Otherwise the
              // ticked canonical reasons render as flat text (matching
              // non-cert-label advisories) and custom answers surface
              // in the cyan block below.
              const advisoryBoxTicked = isCertLabel && !!fc.notes &&
                Array.from(ADVISORY_REASONS).some((r) => String(fc.notes).includes(String(r)));
              const showCanonicalCopy = isCertLabel && advisoryBoxTicked;
              const canonicalOnlyNotes = (!showCanonicalCopy && fc.notes)
                ? String(fc.notes)
                    .split(/\n/)
                    .map((l) => l.replace(/^\s*[\u2022*\-]\s*/, '').trim())
                    .filter(Boolean)
                    .filter((r) => !/^size of opening\s*:/i.test(r))
                    .filter((r) => !/^reason for failure\s*:/i.test(r))
                    .filter((r) => r.toLowerCase() !== 'door needs replacing')
                    .filter((r) => CANONICAL_FAILURE_REASONS.has(r))
                    .join(' · ')
                : '';
              const customAnswerBlock = customAnswers.length > 0 ? `
                <div style="margin-top:6px; padding:8px 10px; background:#F0F9FF; border-left:4px solid #0284C7; border-radius:6px;">
                  <div style="font-size:9.5pt; font-weight:800; color:#075985; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:4px;">Inspector's additional observation${customAnswers.length === 1 ? '' : 's'}</div>
                  ${customAnswers.map((a) => `<div style="font-size:10pt; color:#0C4A6E; line-height:1.5;">&bull; ${escapeHtml(a)}</div>`).join('')}
                </div>
              ` : '';
              return `
              <div style="border:1px solid #93C5FD; border-left:4px solid #2563EB; background:#EFF6FF; border-radius:6px; padding:8px 10px;">
                <div style="font-size:11pt; font-weight:700; color:#1E3A8A;">${escapeHtml(fc.question)}</div>
                ${showCanonicalCopy ? `<div style="font-size:10.5pt; color:#1E3A8A; margin-top:4px; line-height:1.5;">${CERTIFICATION_LABEL_ADVISORY_COPY}</div>` : ''}
                ${canonicalOnlyNotes ? `<div style="font-size:10pt; color:#1E3A8A; margin-top:4px;">${escapeHtml(canonicalOnlyNotes)}</div>` : ''}
                ${customAnswerBlock}
              </div>
            `;
            }).join('')}
          </div>
          ` : ''}
        `;
      })()}

      ${renderBottomSections(data.customSections)}

      ${allPhotos.length > 0 ? `
      <div class="section-title">Images</div>
      <div class="photo-grid">
        ${allPhotos.map(p => {
          const note = captionFor(inspection?.photo_captions, p.src);
          return `
          <div class="photo-cell">
            <img src="${p.src}" style="${photoStyle}" alt="${p.caption}" />
            <div class="photo-caption">${p.caption}</div>
            ${note ? `<div class="photo-note">${escapeHtml(note)}</div>` : ''}
          </div>
        `;
        }).join('')}
      </div>
      ` : ''}

      ${inspection?.signature ? `
      <div class="section-title">Inspector Signature</div>
      ${String(inspection.signature).startsWith('data:image')
        ? `<img src="${inspection.signature}" alt="Inspector signature" style="height:20mm; max-width:100%; display:block; border-bottom:1px solid #999; padding-bottom:4px; margin-bottom:4px;" />`
        : `<p class="signature-line">${escapeHtml(String(inspection.signature))}</p>`}
      <p style="font-size: 10pt; color: #666">Signed by ${escapeHtml(String(inspection.inspector_name || ''))} on ${inspectionDate}</p>
      ` : ''}

      ${renderPointsOfFailureHtml(
        'Points of Failure &mdash; Reference Glossary',
        'The following definitions explain the compliance criteria used during this inspection and the likely implications of any defect. They should be read alongside the door manufacturer&rsquo;s instructions, certification evidence, and the relevant fire risk assessment.',
        true
      )}

      ${postWorkContractor ? '' : ASSOCIATED_SERVICES_HTML}

      ${includeCompliance ? renderComplianceAppendixHtml() : ''}

      ${renderLegalDisclaimer()}

      <div class="footer">
        ${postWorkContractor ? `
          <p><strong>${escapeHtml(brandName)}</strong> &middot; Post-work fire door inspection carried out by the contractor above.</p>
          <p>Aligned to the BS 8214 code of practice · Maintained as part of the Building Safety Golden Thread</p>
          <p style="font-size: 9pt; color: #888; margin-top: 6px;">${escapeHtml(brandContactLine)}</p>
          <p style="font-size: 8pt; color: #999; margin-top: 10px; font-style: italic;">Produced using the FD Check app · © ${new Date().getFullYear()} FD Check Ltd</p>
        ` : `
          <p>This report was generated by FD Check App · © ${new Date().getFullYear()} FD Check Ltd</p>
          <p>Aligned to the BS 8214 code of practice · Maintained as part of the Building Safety Golden Thread</p>
          <p style="font-size: 9pt; color: #888; margin-top: 6px;">${FD_OFFICE_LINE}</p>
          <img src="${FOOTER_BANNER_BASE64}" alt="FD Check Certifications" />
        `}
      </div>

    </body></html>`;

    try {
      // Iter-108e — human-friendly filename: Building - Apt X (or door
      // reference when there's no apartment number) - YYYY-MM-DD.
      const dateTag = format(new Date(), 'yyyy-MM-dd');
      const bld = building?.name || 'Building';
      const doorTag = door?.apartment_number
        ? `Apt ${door.apartment_number}`
        : (door?.door_reference || 'Door');
      const suffix = isUninspected ? 'Survey' : 'Inspection';
      const friendly = `${bld} - ${doorTag} - ${suffix} ${dateTag}`;
      return await htmlToPdf(html, friendly);
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  },

  // Generate building report with all doors
  generateBuildingReport: async (data: BuildingReport) => {
    const { building, doors, inspections } = data;
    const includeCompliance = data.includeCompliance !== false;

    const totalDoors = doors.length;

    // Iter-109h — Count LATEST inspection per door, not every
    // historical row. Previous code counted every inspection, so a
    // door inspected 3× this year (fail → repair → pass → yearly
    // recheck) inflated "Compliant" by 2. On quarterly-inspected
    // communal doors this had been silently overstating compliance
    // for months. Also introduces a `not_inspected` bucket so doors
    // that have never been checked don't roll into "pass".
    const latestByDoor = new Map<string, any>();
    for (const insp of inspections as any[]) {
      const id = insp?.door_id;
      if (!id) continue;
      const prev = latestByDoor.get(id);
      const prevTs = prev ? new Date(prev.inspection_date || 0).getTime() : -Infinity;
      const nextTs = new Date(insp.inspection_date || 0).getTime();
      if (!prev || nextTs > prevTs) latestByDoor.set(id, insp);
    }
    const latestInspections = Array.from(latestByDoor.values());

    const totalInspections = latestInspections.length;

    // Iter-110 Phase A1 — count by EFFECTIVE status (see
    // reportChecklist.effectiveStatus). This is what promotes doors
    // that only have Low-risk / advisory-only failures from FAIL/PASS
    // into `pass_with_observations` so the cover statistics match the
    // web app's Building Report output.
    const effectiveStatuses = latestInspections.map((i: any) => effectiveStatus(i));
    const passCount = effectiveStatuses.filter((s) => s === 'pass').length;
    const passWithObsCount = effectiveStatuses.filter((s) => s === 'pass_with_observations').length;
    const failCount = effectiveStatuses.filter((s) => s === 'fail').length;
    const reviewCount = effectiveStatuses.filter((s) => s === 'review').length;
    const noAccessCount = effectiveStatuses.filter((s) => s === 'no_access').length;
    const notInspectedCount = Math.max(0, totalDoors - totalInspections);

    // Pull most-recent inspector name to fill Inspection Details
    const latestInspection = [...inspections].sort((a: any, b: any) => {
      const da = new Date(a.inspection_date || 0).getTime();
      const db = new Date(b.inspection_date || 0).getTime();
      return db - da;
    })[0];
    const inspectorName = latestInspection?.inspector_name || '—';
    const inspectionDateStr = latestInspection
      ? format(new Date(latestInspection.inspection_date), 'dd/MM/yyyy')
      : format(new Date(), 'dd/MM/yyyy');
    const clientName = building.client_name || building.name;
    const siteAddress = `${building.address}, ${building.postcode}`;

    // Iter-110 Phase A1 — canonical Report Reference Number in the
    // FD-{SLUG6}-{YYYY}-{MM}-{DD} format used by the web app.
    const reportReferenceNumber = buildReportReferenceNumber(building.name, new Date());

    // ── Iter-110 Phase B — derive Executive Summary + Remedial
    //    Action Schedule + Inspector's Summary data from the latest
    //    inspection per door. Mirrors buildBuildingReportContext.js on
    //    the web app so both platforms produce identical numbers.
    // ────────────────────────────────────────────────────────────

    // defect_counts (9 executive-summary buckets) + remedial_actions —
    // Iter-184: derived by the shared buildRemedialSchedule() so the
    // Excel / CSV export lists exactly the same lines as this PDF.
    const { remedialActions, defectCounts } = buildRemedialSchedule(doors, latestInspections);
    // Iter-185 — in-app tracking → Status column on the schedule.
    const trackingIdx = indexTracking(data.remedialTracking);
    const trackedDone = remedialActions.filter((r) => trackingFor(trackingIdx, r)?.status === 'completed').length;
    // Iter-189 — each fix once, with its door list.
    const consolidatedFixes = consolidateRemedialActions(remedialActions, trackingIdx);

    // Auto-generated Inspector's Summary paragraphs.
    const totalDefects = Object.values(defectCounts).reduce((a, b) => a + b, 0);
    const nonCompliantCount = failCount + reviewCount;
    const complianceRate = totalInspections > 0 ? Math.round(((passCount + passWithObsCount) / totalInspections) * 100) : 0;

    const inspectorSummaryParagraphs: string[] = [];
    inspectorSummaryParagraphs.push(
      `On ${inspectionDateStr}, an inspection of ${totalInspections} fire door${totalInspections === 1 ? '' : 's'} was carried out at ${building.name}, ${siteAddress}${totalDoors > totalInspections ? ` (${totalDoors - totalInspections} door${totalDoors - totalInspections === 1 ? '' : 's'} on the register not yet inspected)` : ''}. The assessment was carried out using a visual inspection methodology aligned to the BS 8214 code of practice, in support of the fire-door duties under the Fire Safety (England) Regulations 2022.`,
    );
    if (passCount + passWithObsCount > 0) {
      inspectorSummaryParagraphs.push(
        `Of the doors inspected, ${passCount} were found to be satisfactory${passWithObsCount > 0 ? `, and a further ${passWithObsCount} were satisfactory with advisory observations only (Low-risk items such as missing certification labels or signage that do not compromise fire performance)` : ''}. The overall satisfactory rate stands at ${complianceRate}%.`,
      );
    }
    if (nonCompliantCount > 0) {
      inspectorSummaryParagraphs.push(
        `${nonCompliantCount} door${nonCompliantCount === 1 ? '' : 's'} require${nonCompliantCount === 1 ? 's' : ''} remedial attention. ${totalDefects} defect${totalDefects === 1 ? '' : 's'} were identified across the estate and are itemised in the Remedial Action Schedule and ranked by risk severity, with recommended timescales for each level set out in the schedule key.`,
      );
    }
    if (noAccessCount > 0) {
      inspectorSummaryParagraphs.push(
        `${noAccessCount} door${noAccessCount === 1 ? '' : 's'} could not be accessed during this inspection. These should be re-attempted at the next visit; the Responsible Person is advised to make arrangements with the occupier(s) to enable access.`,
      );
    }
    inspectorSummaryParagraphs.push(
      `Where remedial works are required, these should be undertaken by a competent fire door maintenance company. Following completion, a post-work inspection and updated compliance record should be added to the building's Golden Thread. FD Check can provide details of approved contractors on request.`,
    );

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            @page {
              margin: 18mm 18mm 22mm 18mm;
              @bottom-right {
                content: "Page " counter(page) " of " counter(pages);
                font-family: Arial, sans-serif;
                font-size: 10px;
                color: #888;
              }
              @bottom-left {
                content: "FD Check Ltd · Fire Door Inspection Report";
                font-family: Arial, sans-serif;
                font-size: 10px;
                color: #888;
              }
            }
            .page-break { page-break-before: always; break-before: page; }
            .avoid-break { page-break-inside: avoid; break-inside: avoid; }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              color: #1a1a1a;
            }
            .header {
              text-align: center;
              border-bottom: 3px solid #ED1C24;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .header h1 {
              color: #ED1C24;
              margin: 0 0 10px 0;
            }
            .section {
              margin-bottom: 25px;
            }
            .section-title {
              background-color: #f5f5f5;
              padding: 10px;
              font-weight: bold;
              font-size: 16px;
              border-left: 4px solid #ED1C24;
              margin-bottom: 15px;
            }
            .info-row {
              padding: 8px 0;
              border-bottom: 1px solid #e0e0e0;
            }
            .stats {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              margin: 20px 0;
            }
            .stat-card {
              padding: 15px;
              border-radius: 8px;
              text-align: center;
            }
            .stat-value {
              font-size: 32px;
              font-weight: bold;
            }
            .stat-label {
              font-size: 14px;
              color: #666;
            }
            .door-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            .door-table th, .door-table td {
              padding: 10px;
              text-align: left;
              border-bottom: 1px solid #e0e0e0;
            }
            .door-table th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            /* Iter-254 — keep each door's row intact across page breaks so
               a door's cells never split (reference on one page, the rest on
               the next), and repeat the header row on every page. */
            .door-table thead { display: table-header-group; }
            .door-table tr { page-break-inside: avoid; break-inside: avoid; }
            .status-pass { color: #10B981; font-weight: bold; }
            .status-fail { color: #EF4444; font-weight: bold; }
            .status-review { color: #F59E0B; font-weight: bold; }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #e0e0e0;
              text-align: center;
              color: #666;
              font-size: 12px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="${LOGO_BASE64}" alt="FD Check Ltd" style="max-width: 240px; height: auto; display: block; margin: 0 auto 8px auto;" />
            <h2 style="color: #1a1a1a; margin: 4px 0;">Building Fire Door Inspection Report</h2>
            <h3 style="color: #ED1C24;">${building.name}</h3>
            <p style="margin: 4px 0;">Report generated on ${format(new Date(), 'dd/MM/yyyy HH:mm')} <span style="color:#9CA3AF; font-size:9.5px;">· Report engine build ${REPORT_ENGINE_BUILD}</span></p>
            <p style="margin: 4px 0; font-size: 11px; color: #555;">${FD_OFFICE_LINE}</p>
          </div>

          <!-- Inspection Details + Summary side by side -->
          <div class="section" style="display: flex; gap: 16px; flex-wrap: wrap;">
            <div style="flex: 1 1 260px; border: 1px solid #e0e0e0; border-radius: 8px; padding: 14px;">
              <div style="font-weight:700; font-size:14px; color:#ED1C24; margin-bottom: 10px; border-bottom: 2px solid #ED1C24; padding-bottom: 4px;">Inspection Details</div>
              <div style="display: grid; grid-template-columns: 90px 1fr; gap: 6px 12px; font-size: 12px;">
                <div style="color:#666;">Report ref</div><div style="font-family: monospace; font-size: 11px; color:#111;"><strong>${reportReferenceNumber}</strong></div>
                <div style="color:#666;">Client</div><div><strong>${clientName}</strong></div>
                <div style="color:#666;">Site</div><div>${siteAddress}</div>
                <div style="color:#666;">Building</div><div>${building.name}</div>
                <div style="color:#666;">Inspector</div><div>${inspectorName}</div>
                <div style="color:#666;">Date</div><div>${inspectionDateStr}</div>
              </div>
            </div>
            <div style="flex: 1 1 260px; border: 1px solid #e0e0e0; border-radius: 8px; padding: 14px;">
              <div style="font-weight:700; font-size:14px; color:#ED1C24; margin-bottom: 10px; border-bottom: 2px solid #ED1C24; padding-bottom: 4px;">Inspection Summary</div>
              <div style="display: grid; grid-template-columns: 1fr auto; gap: 8px 12px; font-size: 12px;">
                ${data.doorSelection
                  ? `<div style="color:#666;">Doors in this report</div><div style="text-align:right; font-weight:700;">${data.doorSelection.selected} of ${data.doorSelection.total}</div>`
                  : `<div style="color:#666;">Doors on register</div><div style="text-align:right; font-weight:700;">${totalDoors}</div>`}
                <div style="color:#666;">Doors inspected</div><div style="text-align:right; font-weight:700;">${totalInspections}</div>
                <div style="color:#666;">Satisfactory</div><div style="text-align:right; font-weight:700; color:#10B981;">${passCount}</div>
                ${passWithObsCount > 0 ? `<div style="color:#666;">Pass with observations</div><div style="text-align:right; font-weight:700; color:#0EA5E9;">${passWithObsCount}</div>` : ''}
                <div style="color:#666;">Remedial action required</div><div style="text-align:right; font-weight:700; color:#EF4444;">${failCount + reviewCount}</div>
                <div style="color:#666;">No access</div><div style="text-align:right; font-weight:700; color:#6B7280;">${noAccessCount}</div>
                ${notInspectedCount > 0 ? `<div style="color:#666;">Not inspected</div><div style="text-align:right; font-weight:700; color:#9CA3AF;">${notInspectedCount}</div>` : ''}
              </div>
            </div>
          </div>

          <!-- Cover letter (client-facing wording per FD Check template) -->
          <div class="section page-break">
            <div class="section-title">Cover Letter</div>
            <p style="font-size: 12px; line-height: 1.6; color: #1a1a1a; margin: 0 0 10px 0;">
              Dear ${clientName},
            </p>
            <p style="font-size: 12px; line-height: 1.6; color: #1a1a1a; margin: 0 0 10px 0;">
              Please find attached the Fire Door Inspection Report for
              <strong>${building.name}</strong>, ${siteAddress}, completed on
              <strong>${inspectionDateStr}</strong> by FD Check. This assessment
              has been carried out in accordance with current fire door
              standards and TRADA&#8209;aligned methodology, providing a detailed
              review of the condition and performance of each doorset at the
              time of inspection.
            </p>
            <p style="font-size: 12px; line-height: 1.6; color: #1a1a1a; margin: 0 0 10px 0;">
              This report supports the checks required under the Fire Safety
              (England) Regulations 2022. These checks do not replace the
              existing duty under the Regulatory Reform (Fire Safety) Order
              2005, including the responsible person&rsquo;s obligations under
              Article&nbsp;17 to ensure fire doors are subject to suitable
              inspection and maintenance in all buildings within scope,
              regardless of height.
            </p>
            <p style="font-size: 12px; line-height: 1.6; color: #1a1a1a; margin: 12px 0 6px 0;">
              For ease of reference, the report is organised into the
              following sections:
            </p>
            <ul style="font-size: 12px; color: #1a1a1a; margin: 0 0 12px 18px; padding: 0; line-height: 1.6;">
              <li style="margin-bottom: 4px;">
                <strong>Points of Failure</strong> &mdash; this section lists
                all identified points of failure, the reasons for
                non&#8209;compliance, and the corrective actions required to
                restore the doorset to a satisfactory condition.
              </li>
              <li style="margin-bottom: 4px;">
                <strong>Inspection Summary</strong> &mdash; a summarised
                review of the collected data, presenting key statistics
                and overall assessment observations.
              </li>
              <li style="margin-bottom: 4px;">
                <strong>Individual Door Reports</strong> &mdash; each doorset
                is assigned a Unique ID (UID), with detailed TRADA&#8209;aligned
                inspection findings, performance notes, and recommended
                remedial actions.
              </li>
            </ul>
            <p style="font-size: 12px; line-height: 1.6; color: #1a1a1a; margin: 0 0 10px 0;">
              Where remedial works are required, these should be undertaken by
              a competent fire door maintenance company to address the
              identified remedial actions. FD Check can provide details of
              approved contractors upon request.
            </p>
            <p style="font-size: 12px; line-height: 1.6; color: #1a1a1a; margin: 0 0 18px 0;">
              If you require clarification on any findings or wish to arrange
              remedial works, our team is available to assist.
            </p>
            <p style="font-size: 12px; line-height: 1.6; color: #1a1a1a; margin: 0;">
              Kind regards,<br/>
              <strong>FD Check</strong>
            </p>
          </div>

          ${includeCompliance ? renderComplianceIntroHtml() : ''}

          ${renderTopSections(data.customSections)}

          <!-- Iter-110 Phase B — Executive Summary page.
               Auto-generated compliance snapshot: stats grid + the
               9-category defect table. Byte-parity with the web app's
               buildingReportTemplate.js Executive Summary block. -->
          <div class="section page-break">
            <div class="section-title">Executive Summary</div>
            <p style="font-size: 12px; line-height: 1.6; color: #1a1a1a; margin: 0 0 12px 0;">
              This section provides a headline snapshot of the fire door estate at
              <strong>${building.name}</strong>, ${siteAddress}, as inspected on
              <strong>${inspectionDateStr}</strong> by <strong>${inspectorName}</strong>.
              Full findings and remedial actions are detailed in the sections that follow.
            </p>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 14px 0;">
              <div style="border:1px solid #E5E7EB; border-radius:8px; padding:12px;">
                <div style="font-size:10.5px; font-weight:800; color:#6B7280; text-transform:uppercase; letter-spacing:0.5px;">Satisfactory rate</div>
                <div style="font-size:28px; font-weight:800; color:${complianceRate >= 80 ? '#10B981' : complianceRate >= 50 ? '#F59E0B' : '#EF4444'}; margin-top:2px;">${complianceRate}%</div>
                <div style="font-size:11px; color:#6B7280;">${passCount + passWithObsCount} of ${totalInspections} inspected doors satisfactory</div>
              </div>
              <div style="border:1px solid #E5E7EB; border-radius:8px; padding:12px;">
                <div style="font-size:10.5px; font-weight:800; color:#6B7280; text-transform:uppercase; letter-spacing:0.5px;">Defects Identified</div>
                <div style="font-size:28px; font-weight:800; color:#111827; margin-top:2px;">${totalDefects}</div>
                <div style="font-size:11px; color:#6B7280;">Across ${nonCompliantCount} door${nonCompliantCount === 1 ? '' : 's'} requiring remedial action</div>
              </div>
            </div>

            ${totalDefects > 0 ? `
              <div class="section-title" style="font-size:14px; background-color:#F9FAFB;">Defect Summary by Category</div>
              <table style="width:100%; border-collapse:collapse; font-size:12px; margin-bottom: 10px;">
                <thead>
                  <tr style="background:#F3F4F6;">
                    <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #E5E7EB;">Defect Category</th>
                    <th style="text-align:right; padding:8px 10px; border-bottom:1px solid #E5E7EB; width: 90px;">Instances</th>
                  </tr>
                </thead>
                <tbody>
                  ${DEFECT_CATEGORY_ORDER.filter((cat) => defectCounts[cat] > 0).map((cat) => `
                    <tr>
                      <td style="padding:6px 10px; border-bottom:1px solid #F3F4F6;">${DEFECT_CATEGORY_LABELS[cat]}</td>
                      <td style="padding:6px 10px; border-bottom:1px solid #F3F4F6; text-align:right; font-weight:700;">${defectCounts[cat]}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : `
              <div style="padding:12px 14px; background:#ECFDF5; border-left:4px solid #10B981; border-radius:6px; margin-bottom:10px;">
                <div style="font-size:12px; color:#065F46;"><strong>No defects identified.</strong> All doors inspected passed BS 8214 checks.</div>
              </div>
            `}
          </div>

          <!-- Iter-163p — No-Access Cover Sheet.
               Prominent summary of every door the inspector could
               NOT enter this visit, so the responsible person can
               chase residents / arrange access before the next
               inspection cycle. Renders BETWEEN the Executive
               Summary and the Inspection Methodology so managers
               see it before the technical detail.
               Only shows when at least one door on this report has
               no_access status. Each row shows door reference,
               location, total attempts, latest reason and the
               dates of every attempt. -->
          ${(() => {
            const noAccessDoors = doors
              .map((d: any) => {
                const attempts = collectNoAccessAttempts(d.id, inspections);
                if (attempts.length === 0) return null;
                // Only surface doors whose LATEST inspection was
                // no_access — if a subsequent visit gained access
                // and completed a full inspection, the door is no
                // longer "outstanding" so it shouldn't be on this
                // cover sheet even though it has historic no-access
                // attempts.
                const latest = latestByDoor.get(d.id);
                const latestIsNoAcc =
                  !!latest && (latest.status === 'no_access' || latest?.checklist_data?.no_access === true);
                if (!latestIsNoAcc) return null;
                return { door: d, attempts };
              })
              .filter(Boolean) as { door: any; attempts: NoAccessAttempt[] }[];

            if (noAccessDoors.length === 0) return '';

            const totalAttempts = noAccessDoors.reduce((sum, r) => sum + r.attempts.length, 0);
            const multiAttemptCount = noAccessDoors.filter((r) => r.attempts.length >= 2).length;

            return `
              <div class="section page-break">
                <div class="section-title" style="background-color:#F3F4F6; border-left:4px solid #6B7280;">
                  Doors Requiring Re-Attempt (No Access)
                </div>
                <p style="font-size:12px; line-height:1.6; color:#1a1a1a; margin:0 0 10px 0;">
                  The following <strong>${noAccessDoors.length}</strong> door${noAccessDoors.length === 1 ? '' : 's'} could not be accessed at this inspection visit and remain outstanding. Across all attempts recorded, this report captures <strong>${totalAttempts}</strong> no-access event${totalAttempts === 1 ? '' : 's'}${multiAttemptCount > 0 ? ` — of which <strong>${multiAttemptCount}</strong> door${multiAttemptCount === 1 ? '' : 's'} ${multiAttemptCount === 1 ? 'has' : 'have'} failed two or more attempts.` : '.'}
                </p>
                <p style="font-size:11px; line-height:1.55; color:#4B5563; margin:0 0 10px 0;">
                  Under Regulation 10 of the Fire Safety (England) Regulations 2022 the Responsible Person must make <em>all reasonable efforts</em> to inspect every flat entrance fire door in the building. When multiple attempts have failed, escalate resident engagement (written notice, appointment letter, or accompany the inspector on the next visit) before finalising the compliance record.
                </p>
                <table style="width:100%; border-collapse:collapse; font-size:11.5px; margin-top:8px;">
                  <thead>
                    <tr style="background:#F3F4F6;">
                      <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #E5E7EB; width:88px;">Door Ref</th>
                      <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #E5E7EB;">Location</th>
                      <th style="text-align:center; padding:8px 10px; border-bottom:1px solid #E5E7EB; width:80px;">Attempts</th>
                      <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #E5E7EB; width:100px;">Latest visit</th>
                      <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #E5E7EB;">Latest inspector notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${noAccessDoors.map(({ door: d, attempts }) => {
                      const last = attempts[attempts.length - 1];
                      const lastDate = last?.date ? format(new Date(last.date), 'dd/MM/yyyy') : '—';
                      const lastNote = last?.note ? escapeHtmlWithBreaks(last.note) : '<em style="color:#9CA3AF;">No inspector notes recorded</em>';
                      const attemptPill = attempts.length >= 3
                        ? `<span style="display:inline-block; padding:2px 7px; border-radius:999px; background:#FEE2E2; color:#991B1B; font-size:10px; font-weight:800;">${attempts.length}× · ESCALATE</span>`
                        : attempts.length === 2
                          ? `<span style="display:inline-block; padding:2px 7px; border-radius:999px; background:#FEF3C7; color:#92400E; font-size:10px; font-weight:800;">${attempts.length}×</span>`
                          : `<span style="display:inline-block; padding:2px 7px; border-radius:999px; background:#E5E7EB; color:#374151; font-size:10px; font-weight:800;">${attempts.length}×</span>`;
                      const aptFloor = fmtAptFloor(d);
                      return `
                        <tr>
                          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top; color:#ED1C24; font-weight:800;">${escapeHtml(d.door_reference || '—')}</td>
                          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top; color:#1a1a1a;">
                            <strong>${escapeHtml(d.location || '—')}</strong>
                            ${aptFloor ? `<div style="font-size:10.5px; color:#6B7280;">${aptFloor}</div>` : ''}
                          </td>
                          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top; text-align:center;">${attemptPill}</td>
                          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top; color:#1a1a1a;">${lastDate}</td>
                          <td style="padding:8px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top; color:#4B5563; line-height:1.45;">${lastNote}</td>
                        </tr>
                      `;
                    }).join('')}
                  </tbody>
                </table>
              </div>
            `;
          })()}

          <!-- Iter-110 Phase B — Inspection Methodology page. -->
          <div class="section page-break">
            <div class="section-title">Inspection Methodology</div>
            <p style="font-size: 12px; line-height: 1.6; color: #1a1a1a; margin: 0 0 12px 0;">
              Each fire door is assessed against a standardised 19-point checklist derived from
              <strong>BS&nbsp;8214:2026 &mdash; Timber-based fire door assemblies &mdash; Code of practice</strong>
              and the TRADA fire door inspector methodology. Perimeter and threshold gaps are
              measured at 12 fixed points and cross-checked against a 4&nbsp;mm tolerance. All
              observations, photographic evidence and remedial recommendations are captured
              digitally in real time on FDCheck, then compiled into this compliance record.
            </p>
            <div style="display:grid; grid-template-columns: 1fr 1fr; gap: 6px 20px; font-size: 11.5px; color:#1a1a1a; margin: 8px 0 12px 0;">
              <div>1. Door leaf integrity and operation</div>
              <div>2. Frame condition &amp; fixings</div>
              <div>3. Hinges (min. 3 fitted)</div>
              <div>4. Self-closing device</div>
              <div>5. Handle &amp; latch operation</div>
              <div>6. Intumescent / smoke seals</div>
              <div>7. Cold-smoke seal contact</div>
              <div>8. Perimeter gaps (12-point)</div>
              <div>9. Threshold / dropdown seal</div>
              <div>10. Glazing integrity</div>
              <div>11. Glazing beads</div>
              <div>12. Fire door signage</div>
              <div>13. Certification / identification label</div>
              <div>14. Push / kick plates</div>
              <div>15. Spy viewer (fire-rated)</div>
              <div>16. Letterbox (if fitted): fire rating</div>
              <div>17. Letterbox operation</div>
              <div>18. Hold-open release mechanism</div>
              <div>19. Additional site observations</div>
            </div>
            <p style="font-size: 11.5px; line-height: 1.55; color: #6B7280; font-style: italic; margin: 8px 0 0 0;">
              Findings are classified by risk (Critical / High / Medium / Low). Any defect on
              a Critical item &mdash; or any perimeter gap exceeding 4&nbsp;mm &mdash; triggers a hard
              FAIL. Low-risk observations (e.g. absent certification label, missing signage)
              are recorded as &ldquo;Pass with observations&rdquo; and scheduled for remediation at
              the next planned maintenance cycle, in line with regulatory guidance.
            </p>
          </div>

          <!-- Points of Failure legend -->
          ${renderPointsOfFailureHtml(
            'Points of Failure',
            'This section outlines common fire door defects identified during inspection, explains why they matter, and highlights the likely implications for fire and smoke performance. Any defects should be reviewed against the door manufacturer&rsquo;s instructions, certification evidence, and the relevant fire risk assessment before remedial action is specified.',
            true
          )}

          <!-- Building Information (existing) -->
          <div class="section page-break">
            <div class="section-title">Building Information</div>
            <div class="info-row">
              <strong>Address:</strong> ${building.address}
            </div>
            <div class="info-row">
              <strong>Postcode:</strong> ${building.postcode}
            </div>
            <div class="info-row">
              <strong>Total Doors on Register:</strong> ${data.doorSelection ? data.doorSelection.total : totalDoors}
            </div>
            ${data.doorSelection ? `
            <div class="info-row" style="background:#FFF7ED; border-left:4px solid #F97316; padding:8px 10px; border-radius:4px; margin-top:8px;">
              <strong>Scope of this report:</strong> ${data.doorSelection.selected} of the ${data.doorSelection.total} doors on the register were
              selected by the report author (for example, doors re-inspected after remedial works). Doors not listed in this
              report were excluded from its scope and their status is unchanged from the previous full report.
            </div>` : ''}
          </div>

          <div class="section">
            <div class="section-title">Inspection Statistics</div>
            <div class="stats">
              <div class="stat-card" style="background-color: #E0F2FE;">
                <div class="stat-value">${totalInspections}</div>
                <div class="stat-label">Total Inspections</div>
              </div>
              <div class="stat-card" style="background-color: #D1FAE5;">
                <div class="stat-value" style="color: #10B981;">${passCount}</div>
                <div class="stat-label">Passed</div>
              </div>
              ${passWithObsCount > 0 ? `
              <div class="stat-card" style="background-color: #E0F2FE;">
                <div class="stat-value" style="color: #0EA5E9;">${passWithObsCount}</div>
                <div class="stat-label">Pass with obs.</div>
              </div>
              ` : ''}
              <div class="stat-card" style="background-color: #FEE2E2;">
                <div class="stat-value" style="color: #EF4444;">${failCount}</div>
                <div class="stat-label">Failed</div>
              </div>
              <div class="stat-card" style="background-color: #FEF3C7;">
                <div class="stat-value" style="color: #F59E0B;">${reviewCount}</div>
                <div class="stat-label">Review</div>
              </div>
              ${notInspectedCount > 0 ? `
              <div class="stat-card" style="background-color: #F3F4F6;">
                <div class="stat-value" style="color: #6B7280;">${notInspectedCount}</div>
                <div class="stat-label">Not inspected</div>
              </div>
              ` : ''}
            </div>
          </div>

          <div class="section page-break">
            <div class="section-title">Individual Door Reports</div>
            ${(() => {
              // Group doors: Apartment doors (12-monthly) vs Communal (3 or 6-monthly)
              const apartmentDoors = doors.filter((d: any) =>
                d.door_type === 'apartment' || d.inspection_frequency === '12_month'
              );
              const communalDoors = doors.filter((d: any) =>
                !(d.door_type === 'apartment' || d.inspection_frequency === '12_month')
              );

              const freqLabel = (f: string) => f === '3_month' ? '3 Months'
                : f === '6_month' ? '6 Months'
                : f === '12_month' ? '12 Months'
                : f || '—';

              const renderTable = (rows: any[], title: string, subtitle: string) => {
                if (rows.length === 0) return '';
                return `
                  <div style="margin-top: 16px;">
                    <h3 style="color:#ED1C24; margin-bottom:2px;">${title} (${rows.length})</h3>
                    <p style="font-size:12px; color:#666; margin:0 0 8px 0;">${subtitle}</p>
                    <table class="door-table">
                      <thead>
                        <tr>
                          <th>Reference</th>
                          <th>Apt / Floor</th>
                          <th>Location</th>
                          <th>Frequency</th>
                          <th>Last Inspection</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${rows.map((door: any) => {
                          const doorInspections = inspections.filter((i: any) => i.door_id === door.id).slice().sort((a: any, b: any) => new Date(b.inspection_date || 0).getTime() - new Date(a.inspection_date || 0).getTime());
                          const lastInspection = doorInspections.length > 0 ? doorInspections[0] : null;
                          const aptFloor = fmtAptFloor(door) || '—';
                          // Iter-110 Phase A1 — display EFFECTIVE status
                          // so doors with only Low-risk / advisory-only
                          // failures show as "Pass with observations".
                          const eff = lastInspection ? effectiveStatus(lastInspection) : '';
                          const effLabel = lastInspection ? statusLabel(eff) : '-';
                          const effHex = lastInspection ? statusColor(eff) : '#9CA3AF';
                          return `
                            <tr>
                              <td><strong>${door.door_reference}</strong></td>
                              <td>${aptFloor}</td>
                              <td>${door.location}</td>
                              <td>${freqLabel(door.inspection_frequency)}</td>
                              <td>${lastInspection ? format(new Date(lastInspection.inspection_date), 'dd/MM/yyyy') : 'Not inspected'}</td>
                              <td style="color:${effHex}; font-weight:700;">${effLabel}</td>
                            </tr>
                          `;
                        }).join('')}
                      </tbody>
                    </table>
                  </div>
                `;
              };

              // Per-door embedded mini-reports — each door gets its own visual panel
              // with status, photo, failed components and notes.
              //
              // Iter-108o — Fix "orphan heading pages". Previously we
              // forced a hard page break every 3 doors, which meant a
              // section heading (or subheading) at the top of a new
              // page could be followed by an empty half-page when the
              // door card underneath got shifted to the next page by
              // `page-break-inside: avoid`. Now:
              //   • Removed the "every 3 doors" forced break — the
              //     `.avoid-break` rule already keeps each card
              //     together, and modern print engines pack them
              //     tightly.
              //   • Section heading gets `page-break-after: avoid` so
              //     the heading and its first card print together —
              //     no more stranded titles.
              const renderMiniReports = (rows: any[], heading: string, perDoorPage: boolean = false) => {
                if (rows.length === 0) return '';
                // Iter-110 Phase B2 — pre-index maintenance jobs per
                // door_id so each mini-card gets its slice in O(1).
                const maintByDoor = new Map<string, any[]>();
                for (const j of (data.maintenanceJobs || []) as any[]) {
                  const did = j?.door_id;
                  if (!did) continue;
                  if (!maintByDoor.has(did)) maintByDoor.set(did, []);
                  maintByDoor.get(did)!.push(j);
                }
                // Iter-229 — group remedial actions per door so an
                // apartment owner's extracted page carries its own list.
                const remedialByDoor = new Map<string, any[]>();
                for (const ra of remedialActions as any[]) {
                  const did = ra?.doorId;
                  if (!did) continue;
                  if (!remedialByDoor.has(did)) remedialByDoor.set(did, []);
                  remedialByDoor.get(did)!.push(ra);
                }
                return `
                  <div style="margin-top: 22px;">
                    <h3 style="color:#ED1C24; margin: 0 0 4px 0; page-break-after: avoid; break-after: avoid;">${heading} &mdash; Door-by-door Details</h3>
                    <p style="font-size:11px; color:#666; margin: 0 0 8px 0; page-break-after: avoid; break-after: avoid;">Each card below is a condensed inspection record. Full single-door PDFs include the complete checklist and photo set.</p>
                    ${rows.map((door: any, idx: number) => {
                      const doorInspections = inspections.filter((i: any) => i.door_id === door.id).slice().sort((a: any, b: any) => new Date(b.inspection_date || 0).getTime() - new Date(a.inspection_date || 0).getTime());
                      const lastInspection = doorInspections.length > 0 ? doorInspections[0] : null;
                      // Iter-163p — Attempt history for this specific
                      // door, derived from ALL its historical no-access
                      // inspections. Passed through so the mini-card can
                      // render a chronological attempt log below the
                      // door photo.
                      const noAccessAttempts = collectNoAccessAttempts(door.id, inspections);
                      return renderDoorMiniReport(door, lastInspection, {
                        maintenanceJobsForDoor: maintByDoor.get(door.id) || [],
                        noAccessAttempts,
                        pageBreakBefore: perDoorPage && idx > 0,
                        remedialActionsForDoor: perDoorPage ? (remedialByDoor.get(door.id) || []) : [],
                      });
                    }).join('')}
                  </div>
                `;
              };

              return (
                renderTable(apartmentDoors, 'Apartment Doors — 12-monthly inspection',
                  'Flat entrance doors (FD30S) inspected annually per BS 8214:2026 / Reg 10 FSO 2022.') +
                renderTable(communalDoors, 'Communal Doors — 3 / 6-monthly inspection',
                  'Stairwell, lobby, riser, plant-room and cross-corridor doors (FD30S/FD60S) inspected at higher frequency.') +
                renderMiniReports(apartmentDoors, 'Apartment Doors', true) +
                renderMiniReports(communalDoors, 'Communal Doors')
              );
            })()}
          </div>

          ${renderBottomSections(data.customSections)}

          <!-- Iter-189 — Remedial Recommendations (consolidated).
               Each recommended fix stated ONCE with the doors it applies
               to, ranked by risk. The per-door schedule below is unchanged
               (it carries the in-app tracking status per door). -->
          ${consolidatedFixes.length > 0 ? `
          <div class="section page-break">
            <div class="section-title">Remedial Recommendations</div>
            <p style="font-size: 12px; line-height: 1.6; color: #1a1a1a; margin: 0 0 10px 0;">
              ${consolidatedFixes.length} distinct fix${consolidatedFixes.length === 1 ? '' : 'es'} across
              ${new Set(remedialActions.map((r) => r.doorRef)).size} door${new Set(remedialActions.map((r) => r.doorRef)).size === 1 ? '' : 's'}.
              Each recommendation is stated once with the doors it applies to; the door-by-door schedule follows.
            </p>
            ${consolidatedFixes.map((f) => {
              const riskColour = f.risk === 'Critical' ? '#7F1D1D' : f.risk === 'High' ? '#991B1B' : f.risk === 'Medium' ? '#92400E' : '#075985';
              const riskBg = f.risk === 'Critical' || f.risk === 'High' ? '#FEE2E2' : f.risk === 'Medium' ? '#FEF3C7' : '#E0F2FE';
              const doorList = f.doors.map((d) =>
                `<span style="display:inline-block; margin:2px 6px 2px 0; padding:1px 7px; border-radius:4px; background:${d.completed ? '#D1FAE5' : '#F3F4F6'}; color:${d.completed ? '#065F46' : '#111827'}; font-size:10px; ${d.completed ? 'text-decoration:line-through;' : ''}">${escapeHtml(d.doorRef)}${d.leaf ? ` <span style="color:#7C2D12; font-weight:700;">${escapeHtml(d.leaf)}</span>` : ''}${d.location ? ` <span style="color:#6B7280;">· ${escapeHtml(d.location)}</span>` : ''}</span>`,
              ).join('');
              return `
              <div class="avoid-break" style="border:1px solid #E5E7EB; border-left:5px solid ${riskColour}; border-radius:6px; padding:10px 12px; margin-bottom:10px; background:#fff;">
                <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap;">
                  <span style="display:inline-block; min-width:30px; text-align:center; padding:2px 7px; border-radius:6px; background:#111827; color:#fff; font-weight:800; font-size:10.5px; font-family:monospace;">${f.ref}</span>
                  <span style="font-size:12.5px; font-weight:800; color:#111827;">${escapeHtml(f.defect)}</span>
                  <span style="display:inline-block; padding:2px 8px; border-radius:999px; background:${riskBg}; color:${riskColour}; font-size:9.5px; font-weight:800; letter-spacing:0.3px; text-transform:uppercase;">${f.risk}</span>
                  <span style="margin-left:auto; font-size:10.5px; font-weight:700; color:#374151;">${f.doors.length} door${f.doors.length === 1 ? '' : 's'}${f.completed ? ` · ${f.completed} done` : ''}</span>
                </div>
                <div style="font-size:11px; color:#1a1a1a; margin-top:6px; line-height:1.5;">${escapeHtml(f.action)}</div>
                <div style="margin-top:6px; font-size:10px; color:#374151;"><strong>Applies to:</strong><br/>${doorList}</div>
              </div>`;
            }).join('')}
            ${REMEDIAL_TIMESCALE_KEY_HTML}
          </div>
          ` : ''}

          <!-- Iter-110 Phase B — Remedial Action Schedule.
               Every failed component / defect across the estate,
               sorted by risk severity (Critical → Low) with a target
               timescale and canonical recommended action. Mirrors the
               web app's Remedial Action Schedule table exactly. -->
          ${remedialActions.length > 0 ? `
          <div class="section page-break">
            <div class="section-title">Remedial Action Schedule</div>
            <p style="font-size: 12px; line-height: 1.6; color: #1a1a1a; margin: 0 0 10px 0;">
              The following table itemises every non-conformance identified during this inspection.
              Actions are ranked by risk severity and should be undertaken by a certified fire door
              maintenance company. Recommended timescales for each risk level are set out in the key beneath the table.
              ${trackedDone > 0 ? `<strong>${trackedDone} of ${remedialActions.length} action${remedialActions.length === 1 ? '' : 's'} recorded as completed</strong> at the time of this report.` : ''}
            </p>
            <table style="width:100%; border-collapse:collapse; font-size:11px; margin-bottom: 10px;">
              <thead>
                <tr style="background:#F3F4F6;">
                  <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #E5E7EB; width: 13%;">Door</th>
                  <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #E5E7EB; width: 16%;">Defect</th>
                  <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #E5E7EB; width: 9%;">Risk</th>
                  <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #E5E7EB;">Recommended Action</th>
                  <th style="text-align:left; padding:8px 10px; border-bottom:1px solid #E5E7EB; width: 13%;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${remedialActions.map((r) => {
                  const t = trackingFor(trackingIdx, r);
                  const st = t?.status || 'open';
                  const stBg = st === 'completed' ? '#D1FAE5' : st === 'in_progress' ? '#FEF3C7' : st === 'assigned' ? '#DBEAFE' : '#F3F4F6';
                  const stFg = st === 'completed' ? '#065F46' : st === 'in_progress' ? '#92400E' : st === 'assigned' ? '#1E40AF' : '#374151';
                  const statusCell = `
                    <span style="display:inline-block; padding:2px 7px; border-radius:999px; background:${stBg}; color:${stFg}; font-size:9.5px; font-weight:800;">${TRACKING_STATUS_LABEL[st] || 'Open'}</span>
                    ${t?.assigned_to ? `<div style="font-size:9.5px; color:#374151; margin-top:3px;">${escapeHtml(t.assigned_to)}</div>` : ''}
                    ${st === 'completed' && t?.date_completed ? `<div style="font-size:9.5px; color:#6B7280; margin-top:2px;">${escapeHtml(t.date_completed)}</div>` : ''}`;
                  const riskColour = r.risk === 'Critical' ? '#7F1D1D'
                    : r.risk === 'High' ? '#991B1B'
                    : r.risk === 'Medium' ? '#92400E'
                    : '#075985';
                  const riskBg = r.risk === 'Critical' ? '#FEE2E2'
                    : r.risk === 'High' ? '#FEE2E2'
                    : r.risk === 'Medium' ? '#FEF3C7'
                    : '#E0F2FE';
                  return `
                    <tr>
                      <td style="padding:6px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;"><strong>${r.doorRef}</strong>${r.location ? `<div style="font-size:10px; color:#6B7280;">${r.location}</div>` : ''}</td>
                      <td style="padding:6px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;">${r.defect}</td>
                      <td style="padding:6px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;">
                        <span style="display:inline-block; padding:2px 8px; border-radius:999px; background:${riskBg}; color:${riskColour}; font-size:9.5px; font-weight:800; letter-spacing:0.3px; text-transform:uppercase;">${r.risk}</span>
                      </td>
                      <td style="padding:6px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;">${r.action}</td>
                      <td style="padding:6px 10px; border-bottom:1px solid #F3F4F6; vertical-align:top;">${statusCell}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
            ${REMEDIAL_TIMESCALE_KEY_HTML}
          </div>
          ` : ''}

          <!-- Iter-110 Phase B — Fire Door Compliance Overview.
               Side-by-side breakdown showing door compliance status
               distribution (pass / PWO / fail / review / no-access /
               not-inspected) alongside the defect category breakdown
               as horizontal bars. -->
          ${totalInspections > 0 ? `
          <div class="section page-break">
            <div class="section-title">Fire Door Compliance Overview</div>
            <div style="display: flex; gap: 20px; margin-top: 8px; flex-wrap: wrap;">
              <!-- Door Compliance -->
              <div style="flex: 1 1 250px;">
                <div style="font-weight:700; font-size:13px; color:#111827; margin-bottom: 10px;">Door Compliance Breakdown</div>
                ${(() => {
                  const rows: { label: string; value: number; colour: string }[] = [
                    { label: 'Fully compliant', value: passCount, colour: '#10B981' },
                    { label: 'Pass with observations', value: passWithObsCount, colour: '#0EA5E9' },
                    { label: 'Failed', value: failCount, colour: '#EF4444' },
                    { label: 'Review required', value: reviewCount, colour: '#F59E0B' },
                    { label: 'No access', value: noAccessCount, colour: '#6B7280' },
                    { label: 'Not inspected', value: notInspectedCount, colour: '#9CA3AF' },
                  ];
                  const denom = totalDoors || 1;
                  return rows.filter((r) => r.value > 0).map((r) => `
                    <div style="margin-bottom: 8px;">
                      <div style="display:flex; justify-content:space-between; font-size:11px; margin-bottom:3px;">
                        <span style="color:#1a1a1a;">${r.label}</span>
                        <span style="font-weight:700; color:${r.colour};">${r.value} · ${Math.round((r.value / denom) * 100)}%</span>
                      </div>
                      <div style="height:8px; background:#F3F4F6; border-radius:4px; overflow:hidden;">
                        <div style="height:100%; width:${Math.min(100, Math.round((r.value / denom) * 100))}%; background:${r.colour};"></div>
                      </div>
                    </div>
                  `).join('');
                })()}
              </div>
              <!-- Defect Breakdown -->
              ${totalDefects > 0 ? `
              <div style="flex: 1 1 250px;">
                <div style="font-weight:700; font-size:13px; color:#111827; margin-bottom: 10px;">Defect Breakdown</div>
                ${DEFECT_CATEGORY_ORDER.filter((cat) => defectCounts[cat] > 0).map((cat) => {
                  const pct = Math.round((defectCounts[cat] / totalDefects) * 100);
                  return `
                    <div style="margin-bottom: 8px;">
                      <div style="display:flex; justify-content:space-between; font-size:11px; margin-bottom:3px;">
                        <span style="color:#1a1a1a;">${DEFECT_CATEGORY_LABELS[cat]}</span>
                        <span style="font-weight:700; color:#7F1D1D;">${defectCounts[cat]} · ${pct}%</span>
                      </div>
                      <div style="height:8px; background:#F3F4F6; border-radius:4px; overflow:hidden;">
                        <div style="height:100%; width:${pct}%; background:#DC2626;"></div>
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
              ` : ''}
            </div>
          </div>
          ` : ''}

          <!-- Iter-110 Phase B — Inspector's Summary.
               Auto-generated wrap-up paragraphs derived from the
               compliance rate, defect counts, and access outcomes. -->
          ${inspectorSummaryParagraphs.length > 0 ? `
          <div class="section avoid-break">
            <div class="section-title">Inspector&rsquo;s Summary</div>
            ${inspectorSummaryParagraphs.map((p) => `
              <p style="font-size: 12px; line-height: 1.65; color: #1a1a1a; margin: 0 0 10px 0;">${p}</p>
            `).join('')}
            <div style="margin-top: 14px; padding: 10px 12px; background:#F9FAFB; border-left: 3px solid #6B7280; border-radius: 4px;">
              <div style="font-size: 10.5px; font-weight: 800; color:#6B7280; text-transform: uppercase; letter-spacing: 0.5px;">Report reference</div>
              <div style="font-size: 13px; font-weight: 700; color:#111827; font-family: monospace; margin-top: 2px;">${reportReferenceNumber}</div>
              <div style="font-size: 11px; color:#6B7280; margin-top: 4px;">Inspector: <strong>${inspectorName}</strong> &middot; Date: ${inspectionDateStr}</div>
            </div>
          </div>
          ` : ''}

          ${ASSOCIATED_SERVICES_HTML}

          ${includeCompliance ? renderComplianceAppendixHtml() : ''}

          ${renderLegalDisclaimer()}

          <div class="footer">
            <p>This report was generated by FD Check App</p>
            <p>© ${new Date().getFullYear()} FD Check Ltd · Aligned to UK fire-safety regulations and the BS 8214 code of practice</p>
            <p style="font-size: 11px; color: #888; margin-top: 6px;">${FD_OFFICE_LINE}</p>
            <img src="${FOOTER_BANNER_BASE64}" style="max-width: 100%; margin-top: 20px;" alt="FD Check Certifications" />
          </div>
        </body>
      </html>
    `;

    try {
      // Iter-108e — human-friendly filename: Building - Fire Door
      // Inspection Report - YYYY-MM-DD. When there are zero
      // inspections we mark it as a "Door Register" instead so nobody
      // shares it as a full compliance report.
      const dateTag = format(new Date(), 'yyyy-MM-dd');
      const bld = data.building?.name || 'Building';
      const hasInsp = (data.inspections || []).length > 0;
      const suffix = hasInsp ? 'Fire Door Inspection Report' : 'Fire Door Register';
      const friendly = `${bld} - ${suffix} ${dateTag}`;
      return await htmlToPdf(html, friendly);
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  },

  // Share PDF file
  sharePDF: async (filePath: string) => {
    try {
      // Web: filePath is a Blob URL produced by htmlToPdf(). Open in a
      // new tab so the user can use the browser's "Save as PDF" / share.
      try { (globalThis as any).window?.open?.(filePath, '_blank'); } catch { /* ignore */ }
      return;
    } catch (error) {
      console.error('Error sharing PDF:', error);
      throw error;
    }
  },

  // Generate Quote PDF

  /**
   * Iter-163q — Resident Access Letter.
   *
   * Auto-generates a professional printable letter for a resident
   * whose fire door could not be inspected during recent visits.
   * Used when a door has failed 2+ inspection attempts and the
   * responsible person needs to formally request access before the
   * next inspection cycle. Cites the Regulation 10 Fire Safety
   * (England) Regulations 2022 duty of "all reasonable efforts" so
   * the resident understands why access is legally required.
   *
   * Data payload:
   *   door         — { door_reference, apartment_number, floor, location }
   *   building     — { name, address, postcode, client_name }
   *   attempts     — [{ date, inspector, note }]
   *   contractor   — { name, address, phone, email, logo?, license_no? }
   *                  Optional; when omitted the letter is signed off
   *                  from "FD Check Ltd" using the standard contact
   *                  line.
   *   customFrom   — Optional override for the "From" address block
   *                  when the letter comes from the building manager
   *                  rather than the contractor.
   */
  generateAccessLetter: async (data: {
    door: {
      id?: string;
      door_reference: string;
      apartment_number?: string;
      floor?: any;
      location?: string;
      door_photo?: string | null;
    };
    building: {
      name: string;
      address: string;
      postcode?: string;
      client_name?: string;
    };
    attempts: { date: string; inspector: string; note?: string }[];
    contractor?: {
      name?: string;
      address?: string;
      phone?: string;
      email?: string;
      logo?: string;
      license_no?: string;
    } | null;
    customFrom?: string;
  }) => {
    const { door, building, attempts } = data;
    const contractor = data.contractor || {};
    const today = format(new Date(), 'dd MMMM yyyy');
    const attemptRows = (attempts || [])
      .filter(Boolean)
      .map((a, i) => `
        <tr>
          <td style="padding:6px 8px; border-bottom:1px solid #E5E7EB; color:#111827; font-weight:600;">Attempt #${i + 1}</td>
          <td style="padding:6px 8px; border-bottom:1px solid #E5E7EB; color:#111827;">${a.date ? format(new Date(a.date), 'dd MMMM yyyy') : '—'}</td>
          <td style="padding:6px 8px; border-bottom:1px solid #E5E7EB; color:#374151;">${escapeHtml(a.inspector || '—')}</td>
          <td style="padding:6px 8px; border-bottom:1px solid #E5E7EB; color:#4B5563;">${a.note ? escapeHtmlWithBreaks(a.note) : '<em style="color:#9CA3AF;">No inspector notes recorded</em>'}</td>
        </tr>
      `)
      .join('');

    const fromBlock = data.customFrom ? escapeHtml(data.customFrom) : `
      <div style="font-weight:800; font-size:13px; color:#111827;">${escapeHtml(contractor.name || 'FD Check Ltd')}</div>
      ${contractor.address ? `<div style="color:#374151;">${escapeHtml(contractor.address)}</div>` : ''}
      ${contractor.phone ? `<div style="color:#374151;">Tel: ${escapeHtml(contractor.phone)}</div>` : ''}
      ${contractor.email ? `<div style="color:#374151;">Email: ${escapeHtml(contractor.email)}</div>` : ''}
    `;

    const apt = door.apartment_number ? `Flat ${escapeHtml(door.apartment_number)}` : '';
    const floorRaw = door.floor != null && door.floor !== '' ? String(door.floor).trim() : '';
    const floor = floorRaw ? escapeHtml(normaliseFloorLabel(floorRaw)) : '';
    const residentAddress = [apt, building.name, building.address, building.postcode]
      .filter(Boolean)
      .map((v) => escapeHtml(String(v)))
      .join('<br/>');

    const attemptsCount = attempts.length;
    const escalationLine = attemptsCount >= 3
      ? 'This is our <strong>third and final courtesy request</strong> before we escalate this matter to the freeholder/managing agent and formally document the door as inaccessible in our compliance report to the Responsible Person.'
      : 'To avoid escalation to the freeholder/managing agent and a formal "no access" record on our compliance report, please contact us to arrange a mutually convenient time.';

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Access Letter — ${escapeHtml(door.door_reference)}</title>
  <style>
    body { font-family: Georgia, 'Times New Roman', serif; margin: 0; padding: 24mm 22mm; color: #111827; font-size: 12.5px; line-height: 1.55; }
    .brand { display:flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
    .brand-name { font-family: Arial, Helvetica, sans-serif; font-weight:800; color:#ED1C24; font-size:22px; letter-spacing:0.5px; }
    .meta { font-family: Arial, Helvetica, sans-serif; font-size:11px; color:#6B7280; text-align:right; line-height:1.5; }
    .date-row { text-align:right; margin: 12px 0 24px; font-family: Arial, Helvetica, sans-serif; font-size:12px; color:#374151; }
    .to { font-family: Arial, Helvetica, sans-serif; font-size:12.5px; color:#111827; margin-bottom: 22px; line-height:1.5; }
    .subject { font-weight:800; font-size:14px; margin: 12px 0 16px; color:#111827; text-transform: uppercase; letter-spacing:0.5px; }
    p { margin: 0 0 12px 0; }
    .callout { padding: 12px 14px; background:#FEF3C7; border-left:4px solid #F59E0B; border-radius:4px; margin: 16px 0; font-family: Arial, Helvetica, sans-serif; font-size: 12px; color:#78350F; line-height: 1.55; }
    .attempts-table { width:100%; border-collapse: collapse; margin: 14px 0 8px; font-family: Arial, Helvetica, sans-serif; font-size: 11px; }
    .attempts-table th { text-align:left; padding: 8px; background:#F3F4F6; border-bottom:1px solid #D1D5DB; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:0.3px; font-size:10px; }
    .signature { margin-top: 32px; font-family: Arial, Helvetica, sans-serif; font-size:12px; color:#111827; line-height:1.55; }
    .legal-footer { margin-top: 28px; padding-top: 14px; border-top: 1px dashed #D1D5DB; font-family: Arial, Helvetica, sans-serif; font-size: 10px; color:#6B7280; line-height:1.5; }
    @page { size: A4; margin: 0; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="brand">
    <div>
      <div class="brand-name">${escapeHtml(contractor.name || 'FD Check Ltd')}</div>
      <div style="font-family: Arial, Helvetica, sans-serif; font-size:11px; color:#6B7280; margin-top:2px;">Fire Door Inspection &amp; Compliance</div>
    </div>
    <div class="meta">
      ${contractor.address ? `<div>${escapeHtml(contractor.address)}</div>` : ''}
      ${contractor.phone ? `<div>${escapeHtml(contractor.phone)}</div>` : ''}
      ${contractor.email ? `<div>${escapeHtml(contractor.email)}</div>` : ''}
      ${contractor.license_no ? `<div>License: ${escapeHtml(contractor.license_no)}</div>` : ''}
    </div>
  </div>

  <div class="date-row">${today}</div>

  <div class="to">
    To the Resident<br/>
    ${residentAddress || '—'}
    ${floor ? `<br/><span style="color:#6B7280;">${floor}</span>` : ''}
  </div>

  <div class="subject">Fire door inspection — Access required (Attempt ${attemptsCount === 1 ? '#1' : `#${attemptsCount}`})</div>

  <p>Dear Resident,</p>

  <p>
    Our team is carrying out the statutory fire door inspection at
    <strong>${escapeHtml(building.name)}</strong> in accordance with the
    <strong>Fire Safety (England) Regulations 2022</strong>. As part of this legal duty,
    every flat entrance door forming part of a shared escape route must be inspected
    on a routine basis and the findings recorded in the building's Golden Thread
    compliance record.
  </p>

  <p>
    We have visited your door (reference <strong>${escapeHtml(door.door_reference)}</strong>${apt ? ` at ${escapeHtml(apt.replace('Flat ', 'Flat '))}` : ''})
    on the following occasions but were unable to gain access:
  </p>

  <table class="attempts-table">
    <thead>
      <tr>
        <th style="width:88px;">Attempt</th>
        <th style="width:130px;">Date</th>
        <th style="width:130px;">Inspector</th>
        <th>Inspector notes on site</th>
      </tr>
    </thead>
    <tbody>
      ${attemptRows || '<tr><td colspan="4" style="padding:8px; color:#6B7280; font-style:italic;">No attempt log available.</td></tr>'}
    </tbody>
  </table>

  <div class="callout">
    <strong>Why this matters:</strong> Regulation 10 of the Fire Safety (England)
    Regulations 2022 requires the Responsible Person for this building to make
    <em>all reasonable efforts</em> to inspect every flat entrance fire door.
    A door that cannot be inspected is a compliance gap for the building — and
    for your own safety we need to confirm the door is structurally sound and
    correctly closing.
  </div>

  <p>
    <strong>Please arrange access.</strong> ${escalationLine}
  </p>

  <p>
    <strong>How to book a slot:</strong> ${contractor.phone
      ? `Call us on <strong>${escapeHtml(contractor.phone)}</strong>`
      : 'Call the number on our website'}${contractor.email
        ? ` or email <strong>${escapeHtml(contractor.email)}</strong>` : ''}.
    The inspection itself takes approximately 10–15 minutes and causes no
    disruption to your door or ironmongery.
  </p>

  <p>
    If you have concerns about the identity of our inspector, please ask to see
    photographic ID on arrival — every one of our engineers carries a company ID
    card${contractor.name ? ` bearing the ${escapeHtml(contractor.name)} name and photo` : ''}.
  </p>

  <p>Thank you for your co-operation with this important safety matter.</p>

  <div class="signature">
    Kind regards,<br/><br/>
    <em>The Fire Door Inspection Team</em><br/>
    ${fromBlock}
  </div>

  <div class="legal-footer">
    This notice has been issued in support of the building's statutory fire door
    inspection regime under the Fire Safety (England) Regulations 2022 (Reg. 10)
    and the Regulatory Reform (Fire Safety) Order 2005. It is not a legal notice
    and does not compel access. Access to your dwelling remains at your discretion;
    however, repeated inability to inspect will be formally recorded in the
    building's compliance record and reported to the freeholder / managing agent.
  </div>
</body>
</html>`;

    const filename = `Access-Letter-${sanitiseFilename(building.name)}-${sanitiseFilename(door.door_reference)}-${format(new Date(), 'yyyy-MM-dd')}`;
    return await htmlToPdf(html, filename);
  },

  /**
   * Render a clean PDF for an *external* (3rd-party) fire-door report.
   * This is NOT an FD Check inspection report \u2014 we make that clear with
   * a watermark/banner so nobody confuses it with our own deliverable.
   *
   * The PDF mirrors the on-screen layout: meta + property + summary +
   * a doors table with their defects.
   */
  generateExternalReportPDF: async (report: any) => {
    const ed = report?.extracted_data || {};
    const meta = ed.report_meta || {};
    const bldg = ed.building || {};
    const stats = ed.summary_stats || {};
    const doors = Array.isArray(ed.doors) ? ed.doors : [];

    const sevColour = (sev?: string) => {
      const s = (sev || '').toLowerCase();
      if (s === 'fail') return { bg: '#FEE2E2', fg: '#991B1B' };
      if (s === 'review') return { bg: '#FEF3C7', fg: '#92400E' };
      return { bg: '#DBEAFE', fg: '#1E40AF' };
    };

    const statusBadge = (s?: string) => {
      const c = sevColour(s);
      return s
        ? `<span style="display:inline-block;padding:2px 8px;border-radius:999px;background:${c.bg};color:${c.fg};font-size:10px;font-weight:700;letter-spacing:.4px;">${escapeHtml(String(s)).toUpperCase()}</span>`
        : '';
    };

    const doorRows = doors.length === 0
      ? `<tr><td colspan="5" style="padding:14px;text-align:center;color:#6B7280;font-style:italic;">No doors were extracted from this report.</td></tr>`
      : doors.map((d: any, idx: number) => {
        const defects = Array.isArray(d.defects) ? d.defects : [];
        const defectHtml = defects.length === 0
          ? '<span style="color:#6B7280;font-style:italic;">No defects listed</span>'
          : defects.map((dx: any) => {
            const c = sevColour(dx.severity);
            return `<div style="background:${c.bg};color:${c.fg};border-radius:6px;padding:6px 8px;margin:2px 0;">
                      <div style="font-size:12px;font-weight:600;">\u2022 ${escapeHtml(dx.reason || 'Defect')}</div>
                      ${dx.notes ? `<div style="font-size:11px;opacity:.85;margin-top:2px;">${escapeHtml(dx.notes)}</div>` : ''}
                    </div>`;
          }).join('');
        const headerBits = [d.location, d.floor, d.fire_rating, d.leaf_type].filter(Boolean).map((s: any) => escapeHtml(String(s))).join(' \u00b7 ') || '\u2014';
        return `
          <tr style="vertical-align:top;border-top:1px solid #E5E7EB;">
            <td style="padding:8px;font-weight:600;color:#111827;font-size:12px;">${escapeHtml(String(d.door_number ?? `Door ${idx + 1}`))}</td>
            <td style="padding:8px;color:#374151;font-size:11px;">${headerBits}</td>
            <td style="padding:8px;text-align:center;">${statusBadge(d.status)}</td>
            <td style="padding:8px;">${defectHtml}</td>
          </tr>
        `;
      }).join('');

    const row = (label: string, value: any) => `
      <tr><td style="padding:4px 0;color:#6B7280;font-size:11px;">${label}</td>
          <td style="padding:4px 0;color:#111827;font-size:12px;font-weight:500;text-align:right;">${value ? escapeHtml(String(value)) : '\u2014'}</td></tr>
    `;

    const statBox = (label: string, value: any, colour: string) => `
      <div style="flex:1;background:#F9FAFB;border-radius:8px;padding:10px;text-align:center;">
        <div style="font-size:24px;font-weight:800;color:${colour};">${value ?? '\u2014'}</div>
        <div style="font-size:11px;color:#6B7280;margin-top:2px;">${label}</div>
      </div>
    `;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>External Inspection Report \u2014 ${escapeHtml(bldg.name || report.filename || 'Report')}</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #111827; padding: 28px; margin: 0; }
        h1 { font-size: 22px; margin: 0 0 4px; }
        h2 { font-size: 14px; margin: 18px 0 6px; color: #374151; border-bottom: 1px solid #E5E7EB; padding-bottom: 4px; }
        .meta { color: #6B7280; font-size: 12px; margin-bottom: 18px; }
        .watermark {
          background: #FEF3C7; color: #92400E; border: 1px solid #FCD34D;
          padding: 8px 12px; border-radius: 8px; font-size: 11px; margin-bottom: 16px;
        }
        table.kv { width: 100%; border-collapse: collapse; }
        table.kv td { vertical-align: top; }
        table.doors { width: 100%; border-collapse: collapse; margin-top: 6px; }
        table.doors th {
          text-align: left; background: #F3F4F6; padding: 8px; font-size: 11px;
          color: #374151; text-transform: uppercase; letter-spacing: 0.5px;
        }
        .stats { display: flex; gap: 8px; margin-top: 6px; }
        .footer { margin-top: 24px; font-size: 10px; color: #6B7280; border-top: 1px solid #E5E7EB; padding-top: 10px; text-align: center; }
      </style></head>
      <body>
        <h1>External Inspection Report</h1>
        <div class="meta">
          ${meta.source_company ? escapeHtml(meta.source_company) + ' \u00b7 ' : ''}
          ${meta.inspection_date ? escapeHtml(meta.inspection_date) : ''}
        </div>

        <div class="watermark">
          <strong>EXTERNAL REPORT \u2014 NOT AN FD CHECK INSPECTION.</strong>
          This document was uploaded by a teammate and parsed by AI from the original
          file (${escapeHtml(report.filename || '\u2014')}). Information has not been
          independently verified by FD Check Ltd.
        </div>

        <h2>Report Details</h2>
        <table class="kv">
          ${row('Source company', meta.source_company)}
          ${row('Inspector', meta.inspector_name)}
          ${row('Inspection date', meta.inspection_date)}
          ${row('Report reference', meta.report_reference)}
          ${row('Title', meta.report_title)}
        </table>

        <h2>Property (from the report)</h2>
        <table class="kv">
          ${row('Name', bldg.name)}
          ${row('Address', bldg.address)}
          ${row('Postcode', bldg.postcode)}
          ${row('Client', bldg.client_name)}
        </table>

        <h2>Summary</h2>
        <div class="stats">
          ${statBox('Total', stats.doors_total, '#374151')}
          ${statBox('Satisfactory', stats.doors_passed, '#059669')}
          ${statBox('Remedial', stats.doors_failed, '#DC2626')}
          ${statBox('Review', stats.doors_review, '#D97706')}
        </div>
        ${stats.headline_findings ? `
          <div style="margin-top:10px;font-size:12px;color:#374151;line-height:1.5;">
            ${escapeHtml(stats.headline_findings)}
          </div>
        ` : ''}

        <h2>Doors (${doors.length})</h2>
        <table class="doors">
          <thead><tr>
            <th style="width:90px;">Door</th>
            <th>Location</th>
            <th style="width:90px;text-align:center;">Status</th>
            <th>Defects</th>
          </tr></thead>
          <tbody>${doorRows}</tbody>
        </table>

        ${ASSOCIATED_SERVICES_HTML}

        <div class="footer">
          ${FD_OFFICE_LINE}
        </div>
      </body></html>`;

    try {
      return await htmlToPdf(html, `external-report-${(report.id || 'report').slice(-8)}`);
    } catch (error) {
      console.error('Error generating external report PDF:', error);
      throw error;
    }
  },

  generateQuotePDF: async (quote: any, opts: { maintenanceCompany?: any; customSections?: CustomSections; includeCompliance?: boolean; displayMode?: 'labour_materials' | 'total_only' | null; doorPhotos?: QuoteDoorPhoto[] | null; showDoorPhotos?: boolean; photoAppendix?: boolean } = {}) => {
    const includeCompliance = opts.includeCompliance !== false;
    const created = format(new Date(quote.created_at), 'dd/MM/yyyy');
    const validUntil = quote.valid_until ? format(new Date(quote.valid_until), 'dd/MM/yyyy') : null;
    const quoteRef = quote.id ? quote.id.slice(-8).toUpperCase() : 'DRAFT';
    const mc = opts.maintenanceCompany || {};
    const mcName = mc.maintenance_company_name;
    const mcAddress = mc.maintenance_company_address;
    const mcPhone = mc.maintenance_company_phone;
    const mcEmail = mc.maintenance_company_email;
    const mcLogo = mc.maintenance_company_logo;

    // Iter-108bg — Quote template (layout + intro/footer wording +
    // labour-materials split). Applies to MAINTENANCE quotes only.
    // Inspection quotes stay locked to FD Check house style so every
    // client gets a consistent FD Check-branded inspection quote.
    const isInspectionQuote = (quote.quote_type || 'maintenance') === 'inspection';
    // Iter-172 — an explicit choice (per quote, or the client's saved
    // preference) always wins: "some clients just want a total, some
    // want to see labour and materials separately". Falls back to the
    // company default, and inspection quotes default to one total.
    const pickMode = (v: any): 'labour_materials' | 'total_only' | null =>
      (v === 'total_only' || v === 'labour_materials') ? v : null;
    // `quote.display_mode` is the choice saved on the quote itself, so
    // every other export route (Email Customer, re-download) matches
    // what was sent the first time.
    const explicitMode = pickMode(opts.displayMode) || pickMode(quote.display_mode);
    const tplMode: 'labour_materials' | 'total_only' = explicitMode
      ? explicitMode
      : (isInspectionQuote
        ? 'total_only'
        : (mc.quote_display_mode === 'total_only' ? 'total_only' : 'labour_materials'));
    const tplIntro: string = isInspectionQuote ? '' : String(mc.quote_intro_wording || '').trim();
    const tplFooter: string = isInspectionQuote ? '' : String(mc.quote_footer_wording || '').trim();
    const tplLabourRatio: number = (() => {
      const r = Number(mc.quote_default_labour_ratio);
      if (!isFinite(r) || r < 0 || r > 1) return 0.6;
      return r;
    })();
    // Helper: return [labour, materials] for a line item, using the
    // item's explicit split when present and falling back to the
    // company's default ratio otherwise.
    const splitLine = (it: any): { labour: number; materials: number } => {
      const cost = Number(it.cost || 0);
      const explicitLabour = it.labour_cost;
      const explicitMaterials = it.materials_cost;
      if (typeof explicitLabour === 'number' || typeof explicitMaterials === 'number') {
        const l = Number(explicitLabour || 0);
        const m = Number(explicitMaterials || 0);
        // If only one side is set, derive the other from cost.
        if (typeof explicitLabour === 'number' && typeof explicitMaterials !== 'number') {
          return { labour: l, materials: Math.max(0, cost - l) };
        }
        if (typeof explicitMaterials === 'number' && typeof explicitLabour !== 'number') {
          return { labour: Math.max(0, cost - m), materials: m };
        }
        return { labour: l, materials: m };
      }
      const labour = +(cost * tplLabourRatio).toFixed(2);
      return { labour, materials: +(cost - labour).toFixed(2) };
    };

    // Contractor branding (issuer) — when set, fully overrides the FD Check defaults
    // in the masthead, signature block and T&Cs. Falls back to FD Check when null.
    const contractor = quote.contractor || null;

    // Iter-108t — RM York contractors get a bespoke, branded quote
    // layout that mirrors their reference document (per-door totals,
    // legal boilerplate, RM York terms & closing). Every other
    // contractor keeps the existing generic FD Check layout below.
    if (pdfService._isRmYorkContractor(contractor)) {
      const rmHtml = pdfService._renderRmYorkQuoteHtml(quote, {
        maintenanceCompany: mc,
        displayMode: explicitMode,
        doorPhotos: opts.doorPhotos,
        showDoorPhotos: opts.showDoorPhotos,
        photoAppendix: opts.photoAppendix,
      });
      try {
        const dateTag = format(new Date(), 'yyyy-MM-dd');
        const siteTag = String(quote.title || 'Quote')
          .replace(/^Repairs for\s*/i, '')
          .replace(/[\\/:*?"<>|]/g, '')
          .trim() || 'Quote';
        const friendly = `RM York - ${siteTag} - Quotation ${dateTag}`;
        return await htmlToPdf(rmHtml, friendly);
      } catch (error) {
        console.error('Error generating RM York quote PDF:', error);
        throw error;
      }
    }

    const issuerName = contractor?.name || mcName || 'FD Check Ltd';
    const issuerLogo = contractor?.logo_base64 || (isInspectionQuote ? LOGO_BASE64 : (mcLogo || LOGO_BASE64));
    const issuerStrap = contractor?.name
      ? '' // strapline only meaningful for the default FD Check brand
      : (mcName ? '' : 'UK Fire Door Compliance Specialists');
    const issuerAddress = contractor?.address || (isInspectionQuote ? '' : (mcAddress || ''));
    const issuerEmail = contractor?.email || (isInspectionQuote ? '' : (mcEmail || ''));
    const issuerPhone = contractor?.phone || (isInspectionQuote ? '' : (mcPhone || ''));
    const issuerVat = contractor?.vat_number || '';
    const issuerReg = contractor?.registration_number || '';
    const issuerBank = contractor?.bank_details || '';
    // Iter-188 — T&Cs waterfall: the issuing contractor's own terms →
    // the company's quote-template terms → the built-in standard bullets.
    const issuerTerms = contractor?.default_terms || String(mc.quote_terms || '').trim();
    const paymentTermsDays = Number.isFinite(Number(quote.payment_terms_days)) && quote.payment_terms_days !== null && quote.payment_terms_days !== undefined
      ? Number(quote.payment_terms_days) : null;

    // Free-form custom fields (e.g. PO Number, Site Contact) rendered in the meta strip
    const customFields = Array.isArray(quote.custom_fields)
      ? quote.custom_fields.filter((f: any) => f && (f.label || f.value))
      : [];

    // Iter-173 — one failure photo per door (resolved by the caller from
    // /api/quotes/{id}/door-photos). When present the door column gets a
    // little wider to make room for the 40mm thumbnail.
    const doorPhotoMap = opts.showDoorPhotos === false ? {} : buildDoorPhotoMap(opts.doorPhotos);
    const hasDoorPhotos = Object.keys(doorPhotoMap).length > 0;
    // Iter-174 — optional appendix of the same photos, printed large.
    const photoAppendixHtml = opts.photoAppendix
      ? renderQuotePhotoAppendix(opts.doorPhotos, quoteDoorLocations(quote))
      : '';

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            @page {
              size: A4;
              margin: 14mm 14mm 20mm 14mm;
              @bottom-right {
                content: "Page " counter(page) " of " counter(pages);
                font-family: Arial, sans-serif;
                font-size: 9pt;
                color: #888;
              }
              @bottom-left {
                content: "${issuerName.replace(/"/g, '\\"')} · Quote";
                font-family: Arial, sans-serif;
                font-size: 9pt;
                color: #888;
              }
            }
            body { font-family: Arial, sans-serif; padding: 24px; color: #1a1a1a; }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 3px solid #ED1C24;
              padding-bottom: 16px;
              margin-bottom: 24px;
            }
            .brand img { max-width: 220px; height: auto; display: block; margin-bottom: 6px; }
            .brand h1 { color: #ED1C24; margin: 0; font-size: 22px; }
            .brand p { margin: 4px 0; color: #666; font-size: 12px; }
            .quote-meta { text-align: right; }
            .quote-meta h2 { color: #1a1a1a; margin: 0 0 8px 0; }
            .quote-meta p { margin: 2px 0; font-size: 12px; color: #666; }
            .section { margin-bottom: 24px; }
            .section-title {
              background: #f5f5f5;
              padding: 10px 12px;
              font-weight: bold;
              font-size: 15px;
              border-left: 4px solid #ED1C24;
              margin-bottom: 12px;
            }
            .customer-box {
              background: #f9f9f9;
              padding: 16px;
              border-radius: 8px;
              border-left: 4px solid #ED1C24;
            }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            th { background: #1a1a1a; color: #fff; padding: 10px; text-align: left; font-size: 13px; }
            td { padding: 10px; border-bottom: 1px solid #e0e0e0; font-size: 13px; }
            tbody tr { page-break-inside: avoid; }
            /* Iter-172 — the jobs to be done on a door, listed without
               individual prices (the door total is on the same row). */
            .job-list { margin: 6px 0 0 0; padding-left: 16px; }
            .job-list li { font-size: 12px; color: #333; margin-bottom: 3px; line-height: 1.4; }
            .job-list li strong { color: #1a1a1a; }
            .text-right { text-align: right; }
            .totals {
              margin-top: 16px;
              margin-left: auto;
              width: 50%;
              background: #fff;
              border: 2px solid #e0e0e0;
              border-radius: 8px;
              overflow: hidden;
            }
            .total-row { display: flex; justify-content: space-between; padding: 10px 16px; border-bottom: 1px solid #f0f0f0; font-size: 14px; }
            .grand-total {
              background: #ED1C24;
              color: #fff;
              font-size: 18px;
              font-weight: bold;
              border-bottom: none;
            }
            .notes-box {
              background: #FFFBEB;
              border-left: 4px solid #F59E0B;
              padding: 12px;
              border-radius: 4px;
              font-size: 13px;
            }
            .terms {
              margin-top: 32px;
              padding: 16px;
              background: #f9f9f9;
              border-radius: 8px;
              font-size: 11px;
              color: #555;
              line-height: 1.5;
            }
            .signatures {
              display: flex;
              gap: 32px;
              margin-top: 32px;
              page-break-inside: avoid;
            }
            .signature-box {
              flex: 1;
              padding: 16px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              background: #fff;
            }
            .signature-label { font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
            .signature-line {
              border-bottom: 1px solid #1a1a1a;
              height: 48px;
              margin-bottom: 8px;
            }
            .signature-meta { font-size: 11px; color: #666; }
            .signature-meta strong { color: #1a1a1a; }
            .accepted-stamp {
              font-family: 'Snell Roundhand', cursive;
              font-style: italic;
              font-size: 20px;
              color: #10B981;
              border-bottom: 1px solid #10B981;
              padding-bottom: 4px;
              margin-bottom: 4px;
              text-align: center;
            }
            .footer {
              margin-top: 40px;
              padding-top: 16px;
              border-top: 2px solid #e0e0e0;
              text-align: center;
              color: #666;
              font-size: 11px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="brand">
              <img src="${issuerLogo}" alt="${issuerName}" />
              <h1 style="margin-top:4px;">${issuerName}</h1>
              ${issuerStrap ? `<p>${issuerStrap}</p>` : ''}
              ${issuerAddress ? `<p>${issuerAddress.replace(/\n/g, '<br/>')}</p>` : ''}
              ${(issuerEmail || issuerPhone) ? `<p>${[issuerEmail, issuerPhone].filter(Boolean).join(' · ')}</p>` : ''}
              ${(issuerVat || issuerReg) ? `<p>${[issuerVat ? 'VAT ' + issuerVat : '', issuerReg ? 'Reg ' + issuerReg : ''].filter(Boolean).join(' · ')}</p>` : ''}
              <p style="color:#888; font-size: 10px; margin-top: 6px;">Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
            </div>
            <div class="quote-meta">
              <h2>QUOTATION</h2>
              <p><strong>Ref:</strong> Q-${quoteRef}</p>
              <p><strong>Date:</strong> ${created}</p>
              ${validUntil ? `<p><strong>Valid Until:</strong> ${validUntil}</p>` : ''}
              ${paymentTermsDays !== null ? `<p><strong>Payment terms:</strong> ${paymentTermsDays} day${paymentTermsDays === 1 ? '' : 's'} from invoice</p>` : ''}
              <p><strong>Status:</strong> ${quote.status.toUpperCase()}</p>
              ${customFields.map((f: any) =>
                `<p><strong>${escapeHtml(f.label || '')}:</strong> ${escapeHtml(f.value || '') || '—'}</p>`
              ).join('')}
            </div>
          </div>

          <div class="section">
            <div class="section-title">Quote Title</div>
            <p style="font-size: 18px; font-weight: bold; margin: 0;">${quote.title}</p>
          </div>

          ${quote.customer_name || quote.customer_email ? `
          <div class="section">
            <div class="section-title">Customer Details</div>
            <div class="customer-box">
              ${quote.customer_name ? `<p style="margin: 0; font-weight: bold;">${quote.customer_name}</p>` : ''}
              ${quote.customer_email ? `<p style="margin: 4px 0 0 0; color: #666;">${quote.customer_email}</p>` : ''}
            </div>
          </div>
          ` : ''}

          <div class="section">
            <div class="section-title">Items & Repairs</div>
            ${tplIntro ? `
              <div style="margin: 0 0 14px 0; padding: 12px 14px; background:#F8FAFC; border-left:4px solid #ED1C24; border-radius:6px; font-size:12.5px; color:#1a1a1a; line-height:1.5; white-space:pre-wrap;">${escapeHtml(tplIntro)}</div>
            ` : ''}
            <table>
              <thead>
                <tr>
                  ${tplMode === 'labour_materials' ? `
                    <th style="width: ${hasDoorPhotos ? '52%' : '44%'};">Door &amp; works to be carried out</th>
                    <th style="width: ${hasDoorPhotos ? '6%' : '8%'};" class="text-right">Jobs</th>
                    <th style="width: 14%;" class="text-right">Labour</th>
                    <th style="width: 14%;" class="text-right">Materials</th>
                    <th style="width: 14%;" class="text-right">Door total</th>
                  ` : `
                    <th style="width: ${hasDoorPhotos ? '66%' : '60%'};">Door &amp; works to be carried out</th>
                    <th style="width: ${hasDoorPhotos ? '11%' : '15%'};" class="text-right">Jobs</th>
                    <th style="width: 23%;" class="text-right">Door total</th>
                  `}
                </tr>
              </thead>
              <tbody>
                ${(() => {
                  // Phase B + iter-59 PDF cover-page rendering:
                  //   • Groups roll into a single cover line.
                  //   • Ungrouped items are collapsed by door — one
                  //     line per door with the door total. The full
                  //     per-defect breakdown lives on the Appendix
                  //     page (added below).
                  //   • The "⚠ Unmatched —" prefix used internally to
                  //     flag un-priced defects is stripped from the
                  //     customer-facing PDF (the £0 still shows so
                  //     they know it's unpriced).
                  const items: any[] = quote.items || [];
                  const groups: any[] = quote.groups || [];
                  const memberByGroup: Record<string, any[]> = {};
                  groups.forEach((g: any) => { memberByGroup[g.id] = []; });
                  items.forEach((it: any) => {
                    if (it.group_id && memberByGroup[it.group_id]) {
                      memberByGroup[it.group_id].push(it);
                    }
                  });

                  const cleanComponent = (s: string): string =>
                    String(s || '').replace(/^\s*⚠\s*Unmatched\s*[—-]\s*/i, '').trim();

                  // Iter-108bg — helper that renders the price cells for
                  // one row honouring the company's quote-template mode.
                  // In `labour_materials` mode we surface a Labour and a
                  // Materials column alongside the Total. In `total_only`
                  // mode we keep the historical single-column layout.
                  // For rolled-up "bucket" rows we pass explicit labour /
                  // materials pre-computed from the members.
                  const priceCells = (
                    qty: number,
                    subtotal: number,
                    labour?: number,
                    materials?: number,
                  ): string => {
                    if (tplMode === 'labour_materials') {
                      const l = typeof labour === 'number' ? labour : subtotal * tplLabourRatio;
                      const m = typeof materials === 'number' ? materials : (subtotal - l);
                      return `
                        <td class="text-right">${qty}</td>
                        <td class="text-right">£${l.toFixed(2)}</td>
                        <td class="text-right">£${m.toFixed(2)}</td>
                        <td class="text-right"><strong>£${subtotal.toFixed(2)}</strong></td>
                      `;
                    }
                    // total-only fallback (single money column)
                    return `
                      <td class="text-right">${qty}</td>
                      <td class="text-right"><strong>£${subtotal.toFixed(2)}</strong></td>
                    `;
                  };

                  // Iter-172 — price cells for a DOOR row. The "Jobs"
                  // column carries the number of jobs on that door (the
                  // jobs themselves are listed in the first cell), and
                  // the money columns are the door TOTAL. No price is
                  // ever shown against an individual job.
                  const doorPriceCells = (
                    jobs: number,
                    total: number,
                    labour: number,
                    materials: number,
                  ): string => {
                    if (tplMode === 'labour_materials') {
                      return `
                        <td class="text-right">${jobs}</td>
                        <td class="text-right">£${labour.toFixed(2)}</td>
                        <td class="text-right">£${materials.toFixed(2)}</td>
                        <td class="text-right"><strong>£${total.toFixed(2)}</strong></td>
                      `;
                    }
                    return `
                      <td class="text-right">${jobs}</td>
                      <td class="text-right"><strong>£${total.toFixed(2)}</strong></td>
                    `;
                  };

                  const rows: string[] = [];

                  // 1) Group cover-lines.
                  groups.forEach((g: any) => {
                    const members = memberByGroup[g.id] || [];
                    if (!members.length) return;
                    let subtotal = 0;
                    let labourSum = 0;
                    let materialsSum = 0;
                    members.forEach((it: any) => {
                      const qty = it.quantity || 1;
                      subtotal += (it.cost || 0) * qty;
                      const sp = splitLine(it);
                      labourSum += sp.labour * qty;
                      materialsSum += sp.materials * qty;
                    });
                    const doorCount = new Set(
                      members.map((m: any) => m.door_id).filter(Boolean),
                    ).size;
                    rows.push(`
                      <tr style="background-color: #EFF6FF;">
                        <td>
                          <strong>${g.label}</strong>
                          <br/><span style="color: #2563EB; font-size: 11px;">
                            ${doorCount || members.length} door${doorCount === 1 ? '' : 's'}
                            · ${members.length} line${members.length === 1 ? '' : 's'}
                            · See appendix for full breakdown
                          </span>
                        </td>
                        ${priceCells(1, subtotal, labourSum, materialsSum)}
                      </tr>
                    `);
                  });

                  // 2) Ungrouped items — ONE row per door (user spec,
                  // iter-172): the client sees the list of jobs to be
                  // done on that door and a single door total, never a
                  // price against each individual job. The whole-quote
                  // (building) total sits in the totals box below.
                  const ungrouped = items.filter((it: any) => !it.group_id);
                  const _knownRefs = knownDoorRefs(items);
                  const doorBuckets: Record<string, { ref: string; loc: string; mems: any[] }> = {};
                  const nonDoor: any[] = [];
                  ungrouped.forEach((it: any) => {
                    const ref = quoteLineDoorKey(it, _knownRefs);
                    if (ref) {
                      const slot = doorBuckets[ref] || { ref, loc: it.door_location || '', mems: [] };
                      if (!slot.loc && it.door_location) slot.loc = it.door_location;
                      slot.mems.push(it);
                      doorBuckets[ref] = slot;
                    } else {
                      nonDoor.push(it);
                    }
                  });

                  // "Hinges — hinges loose/damaged" reads far better on a
                  // client-facing quote than the bare component name, so
                  // keep the reason but strip the internal prefixes and
                  // the repeated door label.
                  const jobLine = (it: any, ref?: string): string => {
                    const qty = it.quantity || 1;
                    const name = cleanComponent(it.component) || 'Repair';
                    let detail = cleanJobText(it.description, ref);
                    if (detail.toLowerCase() === name.toLowerCase()) detail = '';
                    return `<li><strong>${escapeHtml(name)}</strong>${qty > 1 ? ` &times;${qty}` : ''}${detail ? ` &mdash; ${escapeHtml(detail)}` : ''}</li>`;
                  };

                  Object.values(doorBuckets).forEach(({ ref, loc, mems }) => {
                    const locTag = loc ? ` &middot; ${escapeHtml(String(loc))}` : '';
                    let subtotal = 0;
                    let labourSum = 0;
                    let materialsSum = 0;
                    mems.forEach((it: any) => {
                      const qty = it.quantity || 1;
                      subtotal += (it.cost || 0) * qty;
                      const sp = splitLine(it);
                      labourSum += sp.labour * qty;
                      materialsSum += sp.materials * qty;
                    });
                    // Iter-173 — the door's failure photo sits beside its
                    // list of works so the client can see the defect.
                    // Wider thumbnail in total-only mode (that layout has
                    // two fewer money columns, so there's room for it).
                    const photo = doorPhotoMap[String(ref)]
                      || doorPhotoMap[String(mems.find((m: any) => m.door_id)?.door_id || '')];
                    const photoMm = tplMode === 'total_only' ? 48 : 34;
                    const worksHtml = `
                      <strong>Door ${escapeHtml(String(ref))}</strong>${locTag}
                      <ul class="job-list">${mems.map((m: any) => jobLine(m, String(ref))).join('')}</ul>`;
                    rows.push(`
                      <tr>
                        <td>
                          ${photo ? `
                            <table style="width:100%; border-collapse:collapse; margin:0;">
                              <tr>
                                <td style="border:none; padding:0; vertical-align:top;">${worksHtml}</td>
                                <td style="border:none; padding:0 0 0 8px; width:${photoMm + 2}mm; vertical-align:top;">${doorPhotoBlock(photo, photoMm)}</td>
                              </tr>
                            </table>
                          ` : worksHtml}
                        </td>
                        ${doorPriceCells(mems.length, subtotal, labourSum, materialsSum)}
                      </tr>
                    `);
                  });

                  // Items with no door at all (e.g. site-wide works or
                  // an ad-hoc line an admin typed in) — grouped under one
                  // "Additional works" block on the same basis.
                  if (nonDoor.length) {
                    let subtotal = 0;
                    let labourSum = 0;
                    let materialsSum = 0;
                    nonDoor.forEach((it: any) => {
                      const qty = it.quantity || 1;
                      subtotal += (it.cost || 0) * qty;
                      const sp = splitLine(it);
                      labourSum += sp.labour * qty;
                      materialsSum += sp.materials * qty;
                    });
                    rows.push(`
                      <tr>
                        <td>
                          <strong>Additional works</strong>
                          <ul class="job-list">${nonDoor.map((m: any) => jobLine(m)).join('')}</ul>
                        </td>
                        ${doorPriceCells(nonDoor.length, subtotal, labourSum, materialsSum)}
                      </tr>
                    `);
                  }

                  return rows.join('');
                })()}
              </tbody>
            </table>
          </div>

          <div class="totals">
            ${(() => {
              // Iter-172 — say what the subtotal covers: "N doors" so the
              // client can see the door totals above roll up to this
              // building total.
              const doorCount = new Set(
                (quote.items || []).map(quoteLineDoorKey).filter(Boolean),
              ).size;
              return doorCount > 1
                ? `<div class="total-row" style="color:#475569;"><span>Doors quoted</span><span>${doorCount}</span></div>`
                : '';
            })()}
            ${tplMode === 'labour_materials' ? (() => {
              // Iter-108bg — Labour / Materials subtotals rendered above
              // the VAT + grand total. Uses each item's explicit split
              // when present, falling back to the company's default
              // labour-ratio when the item was auto-generated pre-split.
              let labourSum = 0;
              let materialsSum = 0;
              (quote.items || []).forEach((it: any) => {
                const qty = it.quantity || 1;
                const sp = splitLine(it);
                labourSum += sp.labour * qty;
                materialsSum += sp.materials * qty;
              });
              return `
                <div class="total-row">
                  <span>Labour subtotal</span>
                  <span>£${labourSum.toFixed(2)}</span>
                </div>
                <div class="total-row">
                  <span>Materials subtotal</span>
                  <span>£${materialsSum.toFixed(2)}</span>
                </div>
              `;
            })() : ''}
            <div class="total-row">
              <span>${(() => {
                const doorCount = new Set(
                  (quote.items || []).map(quoteLineDoorKey).filter(Boolean),
                ).size;
                return doorCount > 1 ? 'All doors — subtotal' : 'Subtotal';
              })()}</span>
              <span>£${quote.subtotal.toFixed(2)}</span>
            </div>
            ${(Number(quote.discount_amount) || 0) > 0 ? `<div class="total-row" style="color:#047857;"><span>${escapeHtml(String(quote.discount_label || 'Discount'))}</span><span>&minus;£${(Number(quote.discount_amount) || 0).toFixed(2)}</span></div>` : ''}
            <div class="total-row">
              <span>VAT (20%)</span>
              <span>£${quote.vat.toFixed(2)}</span>
            </div>
            <div class="total-row grand-total">
              <span>TOTAL</span>
              <span>£${quote.total.toFixed(2)}</span>
            </div>
          </div>

          <div style="clear: both;"></div>

          ${renderReplacementScopeHtml(quote, mc, contractor?.name ? '#1F2937' : '#ED1C24')}

          ${quote.notes ? `
          <div class="section" style="margin-top: 32px;">
            <div class="section-title">Notes</div>
            <div class="notes-box">${quote.notes.replace(/\n/g, '<br/>')}</div>
          </div>
          ` : ''}

          ${(() => {
            // Appendix — full per-door breakdown of each group.
            // Only emitted if the quote has at least one group.
            const items: any[] = quote.items || [];
            const groups: any[] = quote.groups || [];
            if (!groups.length) return '';
            let html = '<div style="page-break-before: always;"></div>';
            html += '<div class="section" style="margin-top: 24px;">';
            html += '<div class="section-title">Appendix — Per-door breakdown</div>';
            html += '<p style="color:#475569;font-size:12px;margin:0 0 12px 0;">The cover page rolls grouped works into single lines. This appendix lists the underlying per-door, per-defect detail for transparency.</p>';
            groups.forEach((g: any) => {
              const members = items.filter((it: any) => it.group_id === g.id);
              if (!members.length) return;
              // Bucket by door_reference so each door has its own sub-table.
              const byDoor: Record<string, any[]> = {};
              members.forEach((it: any) => {
                const k = it.door_reference || it.door_id || 'Unassigned';
                (byDoor[k] = byDoor[k] || []).push(it);
              });
              const subtotal = members.reduce(
                (s: number, it: any) => s + (it.cost || 0) * (it.quantity || 1), 0,
              );
              html += `
                <div style="margin-top: 16px; border:1px solid #DBEAFE; border-radius:8px; overflow:hidden;">
                  <div style="background:#EFF6FF; padding:10px 12px;">
                    <strong style="font-size:13px;color:#0F172A;">${g.label}</strong>
                    <span style="float:right;font-size:13px;font-weight:700;color:#0F172A;">£${subtotal.toFixed(2)}</span>
                  </div>
                  <table class="items-table" style="margin:0;">
                    <thead>
                      <tr>
                        <th style="width: 25%;">Door</th>
                        <th style="width: 45%;">Component / Defect</th>
                        <th style="width: 10%;" class="text-right">Qty</th>
                        <th style="width: 20%;" class="text-right">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${Object.entries(byDoor).map(([doorRef, mems]) => mems.map((it: any, i: number) => `
                        <tr>
                          ${i === 0 ? `<td rowspan="${mems.length}" style="vertical-align:top;"><strong>${doorRef}</strong>${(() => {
                            const p = doorPhotoMap[String(doorRef)]
                              || doorPhotoMap[String(mems.find((m: any) => m.door_id)?.door_id || '')];
                            return p ? `<div style="margin-top:6px;">${doorPhotoBlock(p, 32)}</div>` : '';
                          })()}</td>` : ''}
                          <td>
                            ${it.component}
                            ${it.defect_type ? `<br/><span style="color:#666;font-size:11px;">${it.defect_type}</span>` : ''}
                          </td>
                          <td class="text-right">${it.quantity}</td>
                          <td class="text-right">£${(it.cost * it.quantity).toFixed(2)}</td>
                        </tr>
                      `).join('')).join('')}
                    </tbody>
                  </table>
                </div>
              `;
            });
            html += '</div>';
            return html;
          })()}

          ${(() => {
            // Iter-88 — Appendix B: Original Findings (audit trail for
            // the "Replace as New Door" wizard). When an admin bundled
            // a door's failing components into a single new-door line
            // item, the individual defects are stashed on the quote as
            // `original_findings`. They MUST still appear in the PDF
            // for the client's records and for the engineer's benefit
            // on site — but on a dedicated appendix page so the main
            // quote body stays clean.
            const findings: any[] = Array.isArray((quote as any).original_findings)
              ? (quote as any).original_findings
              : [];
            if (!findings.length) return '';
            let html = '<div style="page-break-before: always;"></div>';
            html += '<div class="section" style="margin-top: 24px;">';
            html += '<div class="section-title">Appendix — Original Findings</div>';
            html += '<p style="color:#475569;font-size:12px;margin:0 0 12px 0;">' +
              'One or more doors on this quote were rolled into a single "Replace as new door" ' +
              'bundle for pricing clarity. The individual defect line items that were replaced ' +
              'are preserved below for audit &amp; site-inspection purposes.' +
              '</p>';
            findings.forEach((f: any) => {
              const doorRef = f.door_reference || f.door_id || 'Unknown door';
              const style = f.style ? ` \u00b7 ${escapeHtml(String(f.style))}` : '';
              const reason = f.reason || 'Replaced with new-door bundle';
              const at = f.at ? format(new Date(f.at), 'dd/MM/yyyy HH:mm') : '';
              const items: any[] = Array.isArray(f.items) ? f.items : [];
              const subtotal = items.reduce(
                (s: number, it: any) =>
                  s + (Number(it.cost || 0)) * (Number(it.quantity || 1)),
                0,
              );
              html += `
                <div style="margin-top: 16px; border:1px solid #FDE68A; border-radius:8px; overflow:hidden;">
                  <div style="background:#FEF3C7; padding:10px 12px;">
                    <strong style="font-size:13px;color:#7C2D12;">
                      ${escapeHtml(String(doorRef))}${style}
                    </strong>
                    <span style="float:right;font-size:11px;color:#92400E;">${escapeHtml(at)}</span>
                    <div style="font-size:11px;color:#92400E;margin-top:4px;">
                      ${escapeHtml(String(reason))}
                      ${items.length ? ` \u00b7 ${items.length} original line ${items.length === 1 ? 'item' : 'items'} \u00b7 <strong>Originally £${subtotal.toFixed(2)}</strong>` : ''}
                    </div>
                  </div>
                  ${items.length ? `
                    <table class="items-table" style="margin:0;">
                      <thead>
                        <tr>
                          <th style="width: 55%;">Original Component / Defect</th>
                          <th style="width: 10%;" class="text-right">Qty</th>
                          <th style="width: 15%;" class="text-right">Original Cost</th>
                          <th style="width: 20%;" class="text-right">Line Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${items.map((it: any) => `
                          <tr>
                            <td>
                              ${escapeHtml(String(it.component || ''))}
                              ${it.description ? `<br/><span style="color:#666;font-size:11px;">${escapeHtml(String(it.description))}</span>` : ''}
                              ${it.defect_type ? `<br/><span style="color:#B45309;font-size:11px;">${escapeHtml(String(it.defect_type))}</span>` : ''}
                            </td>
                            <td class="text-right">${it.quantity ?? 1}</td>
                            <td class="text-right">£${Number(it.cost || 0).toFixed(2)}</td>
                            <td class="text-right">£${(Number(it.cost || 0) * Number(it.quantity || 1)).toFixed(2)}</td>
                          </tr>
                        `).join('')}
                      </tbody>
                    </table>
                  ` : '<div style="padding:12px;color:#78350F;font-size:12px;font-style:italic;">(No individual line items were captured for this replacement.)</div>'}
                </div>
              `;
            });
            html += `<p style="color:#78350F;font-size:10px;font-style:italic;margin:14px 0 0 0;">
              Note: The costs shown here are the ORIGINAL per-defect quotes. They are not billed as
              individual items — the main quote body already reflects the consolidated
              "Replacement Door" line (single combined price) for each replaced door.
            </p>`;
            html += '</div>';
            return html;
          })()}

          ${photoAppendixHtml}

          <div class="signatures">
            <div class="signature-box">
              <div class="signature-label">For ${issuerName}</div>
              <div class="signature-line"></div>
              <div class="signature-meta">
                <strong>Name:</strong> ____________________<br/>
                <strong>Position:</strong> ____________________<br/>
                <strong>Date:</strong> ${format(new Date(), 'dd/MM/yyyy')}
              </div>
            </div>
            <div class="signature-box">
              <div class="signature-label">Customer Acceptance</div>
              ${quote.status === 'accepted' ? `
                ${quote.decision_signature_data_uri ? `
                  <img src="${quote.decision_signature_data_uri}" style="height:20mm; max-width:100%; display:block; margin-bottom:2px;" />
                ` : `
                  <div class="accepted-stamp">${quote.decision_signature_name || quote.customer_name || 'Customer'}</div>
                `}
                <div class="signature-meta">
                  ${quote.decision_signature_name ? `<strong>Signed by:</strong> ${escapeHtml(String(quote.decision_signature_name))}<br/>` : ''}
                  <strong>✓ ACCEPTED</strong> on ${quote.decision_at ? format(new Date(quote.decision_at), 'dd/MM/yyyy HH:mm') : (quote.updated_at ? format(new Date(quote.updated_at), 'dd/MM/yyyy HH:mm') : format(new Date(), 'dd/MM/yyyy'))}
                </div>
              ` : `
                <div class="signature-line"></div>
                <div class="signature-meta">
                  <strong>Name:</strong> ${quote.customer_name || '____________________'}<br/>
                  <strong>Signature:</strong> ____________________<br/>
                  <strong>Date:</strong> ____________________
                </div>
              `}
            </div>
          </div>

          <div class="terms">
            <strong>Terms &amp; Conditions:</strong><br/>
            ${issuerTerms
              ? issuerTerms.replace(/\n/g, '<br/>')
              : `
                • This quotation is valid for 30 days from the issue date unless otherwise stated.<br/>
                • Prices include VAT at the current rate of 20%.<br/>
                • Works are subject to site survey and access to all fire doors.<br/>
                • Payment terms: 50% deposit on order acceptance, balance due on completion.<br/>
                • All work is carried out in accordance with UK Fire Safety Regulations.
              `}
          </div>

          ${tplFooter ? `
            <div style="margin-top: 20px; padding: 14px 16px; background:#F9FAFB; border-left:4px solid #6B7280; border-radius:6px; font-size:12px; color:#374151; line-height:1.55; white-space:pre-wrap;">${escapeHtml(tplFooter)}</div>
          ` : ''}

          ${issuerBank ? `
            <div class="terms" style="margin-top: 12px; background:#F0FDF4; border-color:#10B981;">
              <strong>Payment Details:</strong><br/>
              ${issuerBank.replace(/\n/g, '<br/>')}
            </div>
          ` : ''}

          ${renderBottomSections(opts.customSections)}

          ${includeCompliance ? renderComplianceAppendixHtml() : ''}

          <div class="footer">
            <p>This quote was generated by FD Check App</p>
            <p>© ${new Date().getFullYear()} FD Check Ltd - Aligned to UK fire-safety regulations</p>
            ${mcName ? `
              <div style="margin: 14px auto 8px auto; padding: 12px 14px; background:#FFFBEB; border:1px solid #FCD34D; border-radius:10px; max-width: 520px; text-align: left;">
                <div style="display: flex; align-items: center; gap: 12px;">
                  ${mcLogo ? `<img src="${mcLogo}" alt="${mcName}" style="width:64px; height:64px; object-fit:contain; border-radius:6px;" />` : ''}
                  <div style="flex:1;">
                    <div style="font-size:10px; font-weight:800; color:#B45309; letter-spacing:0.5px; text-transform: uppercase;">Maintenance carried out by</div>
                    <div style="font-size:14px; font-weight:800; color:#1a1a1a; margin-top:2px;">${mcName}</div>
                    ${mcAddress ? `<div style="font-size:11px; color:#555;">${mcAddress}</div>` : ''}
                    ${(mcPhone || mcEmail) ? `<div style="font-size:11px; color:#555;">${[mcPhone, mcEmail].filter(Boolean).join(' · ')}</div>` : ''}
                  </div>
                </div>
              </div>
            ` : ''}
            <img src="${FOOTER_BANNER_BASE64}" style="max-width: 100%; margin-top: 16px;" alt="FD Check Certifications" />
          </div>
        </body>
      </html>
    `;

    try {
      return await htmlToPdf(html, `fdcheck-quote-${quote?.id?.slice(-8) || 'quote'}`);
    } catch (error) {
      console.error('Error generating quote PDF:', error);
      throw error;
    }
  },

  /**
   * Save a generated PDF to the backend so it can be re-downloaded
   * later from the building / door / quote screen — and from the
   * client portal.
   *
   * On native (iOS/Android) `uri` is a `file://` path written by
   * `Print.printToFileAsync`; we read it back as base64 and post it.
   *
   * On web, `Print.printToFileAsync` isn't supported and `uri` is a
   * Blob URL containing HTML, not a PDF. In that case we silently
   * skip the upload — the user is expected to use the browser's
   * "Print → Save as PDF" then drag-and-drop a saved copy in via the
   * upload UI (Phase 2). We return `null` so callers can decide
   * whether to surface a hint.
   *
   * Failures are swallowed (just logged) so a flaky network doesn't
   * block the user from sharing/printing the PDF they just made.
   */

  // Iter-108bh — INVOICE RENDERER + PDF GENERATOR
  //
  // Renders a full invoice PDF using the same visual language as the
  // quote PDFs but tuned for invoicing: header wording block, invoice
  // number + date + due-date meta grid, DRAFT/PAID/CANCELLED watermark,
  // items snapshot table, subtotal/VAT/total, bank details + footer
  // wording. Never fires the RM York bespoke quote path — invoice
  // layout is uniform across contractors so it looks consistent when
  // clients pay.
  _renderInvoiceHtml: (
    invoice: any,
    opts: { maintenanceCompany?: any; contractor?: any } = {},
  ): string => {
    const mc = opts.maintenanceCompany || {};
    const contractor = opts.contractor || {};
    const header = String(mc.invoice_header_wording || '').trim();
    const intro = String(mc.invoice_intro_wording || '').trim();
    const footer = String(mc.invoice_footer_wording || '').trim();

    const isInspection = (invoice.invoice_type || 'maintenance') === 'inspection';
    const issuerLogo = invoice.issuer_logo_base64
      || contractor.logo_base64
      || (isInspection ? LOGO_BASE64 : (mc.maintenance_company_logo || LOGO_BASE64));
    const issuerName = invoice.issuer_name
      || contractor.name
      || (isInspection ? 'FD Check Ltd' : (mc.maintenance_company_name || 'FD Check Ltd'));
    const issuerAddress = invoice.issuer_address
      || contractor.address
      || (isInspection ? '' : (mc.maintenance_company_address || ''));
    const issuerVat = invoice.issuer_vat_number || contractor.vat_number || '';
    const issuerEmail = contractor.email || (isInspection ? '' : (mc.maintenance_company_email || ''));
    const issuerPhone = contractor.phone || (isInspection ? '' : (mc.maintenance_company_phone || ''));
    const issuerBank = String(contractor.bank_details || '').trim();

    const invNum = String(invoice.invoice_number || invoice.id || 'DRAFT');
    const created = invoice.invoice_date
      ? format(new Date(invoice.invoice_date), 'dd MMM yyyy')
      : format(new Date(), 'dd MMM yyyy');
    const due = invoice.due_date ? format(new Date(invoice.due_date), 'dd MMM yyyy') : '—';

    const status = String(invoice.status || 'draft').toLowerCase();
    // Bold coloured banner reflecting the invoice's current state.
    const statusMeta: Record<string, { bg: string; fg: string; label: string }> = {
      draft:     { bg: '#F3F4F6', fg: '#374151', label: 'DRAFT' },
      sent:      { bg: '#DBEAFE', fg: '#1E40AF', label: 'ISSUED' },
      paid:      { bg: '#D1FAE5', fg: '#065F46', label: 'PAID' },
      cancelled: { bg: '#E5E7EB', fg: '#6B7280', label: 'CANCELLED' },
    };
    const sm = statusMeta[status] || statusMeta.draft;

    const items: any[] = Array.isArray(invoice.items_snapshot) ? invoice.items_snapshot : [];
    // Group items by door_reference so the invoice reads like the quote
    // it was generated from (one row per door, single "Additional works"
    // block for line-items without a door).
    type Bucket = { ref: string; label: string; items: any[]; total: number };
    const buckets: Record<string, Bucket> = {};
    const nonDoor: any[] = [];
    items.forEach((it: any) => {
      const ref = it.door_reference;
      const lineTotal = (Number(it.cost) || 0) * (Number(it.quantity) || 1);
      if (ref) {
        const slot = buckets[ref] || { ref, label: ref, items: [], total: 0 };
        slot.items.push(it);
        slot.total += lineTotal;
        buckets[ref] = slot;
      } else {
        nonDoor.push(it);
      }
    });
    const bucketList = Object.values(buckets).sort((a, b) => {
      const na = parseInt(String(a.ref).replace(/\D+/g, ''), 10);
      const nb = parseInt(String(b.ref).replace(/\D+/g, ''), 10);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return String(a.ref).localeCompare(String(b.ref));
    });

    const itemRows = (list: any[]): string => list.map((it: any) => {
      const raw = String(it.description || it.component || '').trim();
      const cleaned = raw.replace(/^Repair:\s*/i, '').replace(/^\s*⚠\s*Unmatched\s*[—-]\s*/i, '');
      const line = (Number(it.cost) || 0) * (Number(it.quantity) || 1);
      return `
        <tr>
          <td style="padding:8px 10px; border-bottom:1px dotted #E5E7EB; font-size:10.5pt; vertical-align:top;">
            <div style="font-weight:700; color:#1f2937;">${escapeHtml(it.component || '')}</div>
            ${cleaned && cleaned !== (it.component || '') ? `<div style="color:#4B5563; margin-top:2px;">${escapeHtml(cleaned)}</div>` : ''}
          </td>
          <td style="padding:8px 10px; border-bottom:1px dotted #E5E7EB; font-size:10.5pt; text-align:right; vertical-align:top;">${Number(it.quantity) || 1}</td>
          <td style="padding:8px 10px; border-bottom:1px dotted #E5E7EB; font-size:10.5pt; text-align:right; vertical-align:top;">£${(Number(it.cost) || 0).toFixed(2)}</td>
          <td style="padding:8px 10px; border-bottom:1px dotted #E5E7EB; font-size:10.5pt; text-align:right; vertical-align:top; font-weight:700;">£${line.toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    const doorSections = bucketList.map((b) => `
      <div style="margin-top:14px;">
        <div style="background:#F3F4F6; padding:8px 12px; border-left:4px solid #ED1C24; border-radius:4px; font-weight:800; color:#111827; font-size:11.5pt;">
          Door <span style="color:#6B7280; font-weight:600; font-size:10.5pt;">— ${escapeHtml(String(b.label))}</span>
        </div>
        <table style="width:100%; border-collapse:collapse; margin-top:6px;">
          <thead>
            <tr style="background:#FAFAFA;">
              <th style="text-align:left; padding:8px 10px; font-size:10pt; border-bottom:1px solid #E5E7EB; color:#374151;">Description</th>
              <th style="text-align:right; padding:8px 10px; font-size:10pt; border-bottom:1px solid #E5E7EB; color:#374151; width:70px;">Qty</th>
              <th style="text-align:right; padding:8px 10px; font-size:10pt; border-bottom:1px solid #E5E7EB; color:#374151; width:90px;">Unit</th>
              <th style="text-align:right; padding:8px 10px; font-size:10pt; border-bottom:1px solid #E5E7EB; color:#374151; width:100px;">Line total</th>
            </tr>
          </thead>
          <tbody>
            ${itemRows(b.items)}
          </tbody>
        </table>
      </div>
    `).join('');

    const nonDoorSection = nonDoor.length ? `
      <div style="margin-top:14px;">
        <div style="background:#F3F4F6; padding:8px 12px; border-left:4px solid #6B7280; border-radius:4px; font-weight:800; color:#111827; font-size:11.5pt;">
          Additional Works
        </div>
        <table style="width:100%; border-collapse:collapse; margin-top:6px;">
          <thead>
            <tr style="background:#FAFAFA;">
              <th style="text-align:left; padding:8px 10px; font-size:10pt; border-bottom:1px solid #E5E7EB; color:#374151;">Description</th>
              <th style="text-align:right; padding:8px 10px; font-size:10pt; border-bottom:1px solid #E5E7EB; color:#374151; width:70px;">Qty</th>
              <th style="text-align:right; padding:8px 10px; font-size:10pt; border-bottom:1px solid #E5E7EB; color:#374151; width:90px;">Unit</th>
              <th style="text-align:right; padding:8px 10px; font-size:10pt; border-bottom:1px solid #E5E7EB; color:#374151; width:100px;">Line total</th>
            </tr>
          </thead>
          <tbody>
            ${itemRows(nonDoor)}
          </tbody>
        </table>
      </div>
    ` : '';

    const noItemsFallback = (!bucketList.length && !nonDoor.length) ? `
      <div style="margin-top:14px; padding:14px 16px; background:#F9FAFB; border:1px dashed #D1D5DB; border-radius:6px; color:#6B7280; font-size:11pt; text-align:center;">
        This invoice was created without a linked quote — line items are not available. The total is still legally binding.
      </div>
    ` : '';

    // Snapshot custom fields (e.g. PO number) so the invoice reads like
    // the quote it was generated from.
    const customFields: any[] = Array.isArray(invoice.custom_fields) ? invoice.custom_fields : [];
    const customFieldRows = customFields
      .filter((f: any) => (f?.label || '').trim() && (f?.value || '').trim())
      .map((f: any) => `
        <div class="lbl">${escapeHtml(String(f.label))}</div>
        <div>${escapeHtml(String(f.value))}</div>
      `).join('');

    const paymentDetails = invoice.payment_date ? `
      <div style="margin-top:18px; padding:12px 14px; background:#ECFDF5; border:1px solid #10B981; border-radius:6px;">
        <div style="font-weight:800; color:#065F46; margin-bottom:6px; font-size:11pt;">Payment Received</div>
        <div style="font-size:10.5pt; color:#0F172A;">
          <strong>${format(new Date(invoice.payment_date), 'dd MMM yyyy')}</strong>
          ${invoice.payment_amount != null ? ` · £${Number(invoice.payment_amount).toFixed(2)}` : ''}
          ${invoice.payment_method ? ` · ${escapeHtml(String(invoice.payment_method).replace(/_/g, ' '))}` : ''}
        </div>
        ${invoice.payment_reference ? `<div style="font-size:10pt; color:#374151; margin-top:4px;">Reference: ${escapeHtml(String(invoice.payment_reference))}</div>` : ''}
      </div>
    ` : '';

    const bankHtml = issuerBank ? `
      <div style="margin-top:18px; padding:12px 14px; background:#EFF6FF; border:1px solid #93C5FD; border-radius:6px; font-size:10.5pt; line-height:1.5;">
        <div style="font-weight:800; margin-bottom:6px; color:#1E3A8A;">Payment Details</div>
        ${escapeHtml(issuerBank).replace(/\n/g, '<br/>')}
      </div>
    ` : '';

    return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  @page { size: A4; margin: 14mm 14mm 20mm 14mm; @bottom-right { content: "Page " counter(page) " of " counter(pages); font-family: Arial, sans-serif; font-size: 9pt; color: #888; } @bottom-left { content: "${issuerName.replace(/"/g, '\\"')} · Invoice ${invNum.replace(/"/g, '\\"')}"; font-family: Arial, sans-serif; font-size: 9pt; color: #888; } }
  body { font-family: Arial, sans-serif; color: #1a1a1a; padding: 4px; }
  .brand-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #ED1C24; padding-bottom: 14px; margin-bottom: 18px; }
  .brand-header img { max-width: 200px; height: auto; }
  .brand-block { text-align: right; font-size: 10.5pt; color: #4B5563; line-height: 1.4; }
  .brand-block .name { font-size: 15pt; font-weight: 800; color: #1a1a1a; }
  h1.title { font-size: 22pt; color: #ED1C24; margin: 0; letter-spacing: 1px; }
  .status-pill { display:inline-block; padding: 4px 12px; border-radius: 999px; font-weight: 900; font-size: 11pt; letter-spacing: 1px; margin-left: 12px; vertical-align: middle; background: ${sm.bg}; color: ${sm.fg}; }
  .header-wording { margin: 6px 0 12px 0; padding: 8px 12px; background: #FFF7ED; border-left: 4px solid #F59E0B; border-radius: 4px; font-size: 10.5pt; color: #78350F; white-space: pre-wrap; }
  .meta-grid { display: grid; grid-template-columns: 140px 1fr; gap: 4px 12px; font-size: 11pt; margin: 14px 0; padding: 12px 14px; background: #F9FAFB; border-left: 4px solid #ED1C24; border-radius: 4px; }
  .meta-grid .lbl { color: #6B7280; font-weight: 700; }
  .intro-block { margin: 12px 0 6px 0; padding: 12px 14px; background: #F8FAFC; border-left: 4px solid #ED1C24; border-radius: 6px; font-size: 10.5pt; color: #1a1a1a; line-height: 1.55; white-space: pre-wrap; }
  .totals-box { margin: 22px 0 0 auto; width: 55%; border: 2px solid #1a1a1a; border-radius: 6px; overflow: hidden; }
  .totals-row { display: flex; justify-content: space-between; padding: 8px 14px; font-size: 11.5pt; border-bottom: 1px solid #E5E7EB; }
  .totals-row.grand { background: #ED1C24; color: #fff; font-weight: 800; font-size: 13pt; border-bottom: none; }
  .footer-block { margin-top: 18px; padding: 14px 16px; background: #F9FAFB; border-left: 4px solid #6B7280; border-radius: 6px; font-size: 10.5pt; color: #374151; line-height: 1.55; white-space: pre-wrap; text-align: justify; }
</style></head><body>

  <div class="brand-header">
    <img src="${issuerLogo}" alt="${escapeHtml(issuerName)}" />
    <div class="brand-block">
      <div class="name">${escapeHtml(issuerName)}</div>
      ${issuerAddress ? `<div>${escapeHtml(issuerAddress).replace(/\n/g, '<br/>')}</div>` : ''}
      ${issuerPhone ? `<div>Tel ${escapeHtml(issuerPhone)}</div>` : ''}
      ${issuerEmail ? `<div>${escapeHtml(issuerEmail)}</div>` : ''}
      ${issuerVat ? `<div style="font-size:9.5pt;">VAT ${escapeHtml(issuerVat)}</div>` : ''}
    </div>
  </div>

  <div>
    <h1 class="title">INVOICE<span class="status-pill">${sm.label}</span></h1>
    ${header ? `<div class="header-wording">${escapeHtml(header)}</div>` : ''}
  </div>

  <div class="meta-grid">
    <div class="lbl">Invoice #</div><div><strong>${escapeHtml(invNum)}</strong></div>
    <div class="lbl">Issue date</div><div>${created}</div>
    <div class="lbl">Due date</div><div>${due}</div>
    <div class="lbl">Payment terms</div><div>${Number(invoice.payment_terms_days || 30)} days</div>
    <div class="lbl">Bill to</div><div><strong>${escapeHtml(invoice.client_name || '—')}</strong>${invoice.building_name ? `<br/>${escapeHtml(invoice.building_name)}` : ''}</div>
    ${customFieldRows}
  </div>

  ${intro ? `<div class="intro-block">${escapeHtml(intro)}</div>` : ''}

  ${doorSections}
  ${nonDoorSection}
  ${noItemsFallback}

  <div class="totals-box">
    <div class="totals-row"><span>Sub-total</span><span>£${(Number(invoice.amount) || 0).toFixed(2)}</span></div>
    <div class="totals-row"><span>VAT (20%)</span><span>£${(Number(invoice.vat) || 0).toFixed(2)}</span></div>
    <div class="totals-row grand"><span>TOTAL DUE</span><span>£${(Number(invoice.total) || 0).toFixed(2)}</span></div>
  </div>

  ${paymentDetails}
  ${bankHtml}

  ${invoice.notes ? `
    <div style="margin-top: 18px; padding: 12px 14px; background:#FFFBEB; border-left:4px solid #F59E0B; border-radius:6px; font-size:10.5pt; color:#374151;">
      <div style="font-weight:800; margin-bottom:4px;">Notes</div>
      ${escapeHtml(String(invoice.notes)).replace(/\n/g, '<br/>')}
    </div>
  ` : ''}

  ${footer ? `<div class="footer-block">${escapeHtml(footer)}</div>` : ''}

</body></html>`;
  },

  /**
   * Iter-108bh — Generate the final invoice PDF. Consumes the same
   * companiesApi.me() blob as generateQuotePDF so the invoice picks
   * up the company-wide Invoice Template settings.
   */
  generateInvoicePDF: async (invoice: any, opts: { maintenanceCompany?: any; contractor?: any } = {}) => {
    const html = pdfService._renderInvoiceHtml(invoice, opts);
    const friendly = `fdcheck-invoice-${(invoice?.invoice_number || invoice?.id || 'draft').toString().replace(/[^A-Za-z0-9_-]/g, '_')}`;
    return await htmlToPdf(html, friendly);
  },

  /**
   * Iter-108bh — Return the raw HTML for use in the in-app preview
   * modal (rendered inside a WebView / iframe). Kept separate from
   * generateInvoicePDF so the preview never touches the printer /
   * share sheet.
   */
  buildInvoicePreviewHtml: (invoice: any, opts: { maintenanceCompany?: any; contractor?: any } = {}): string => {
    return pdfService._renderInvoiceHtml(invoice, opts);
  },

  saveToBackend: async (
    uri: string,
    meta: {
      building_id?: string | null;
      door_id?: string | null;
      inspection_id?: string | null;
      quote_id?: string | null;
      report_type: string;
      title: string;
      // Iter-108ce-10 — Optional. Derived from `title` when omitted.
      // Every caller was passing this as undefined previously, which
      // 422'd the backend (min_length=1). Silently. Losing PDFs.
      file_name?: string;
      summary?: Record<string, any>;
    },
  ): Promise<{ id: string } | null> => {
    // Iter-108ce-10 — Always resolve a sensible `file_name` before
    // hitting the backend. The saved-reports POST rejects empty
    // strings with 422; the previous callers were passing
    // `file_name: undefined` and the failure was silently swallowed
    // by the outer catch, so PDFs never landed in the library.
    const safeSlug = (s: string) =>
      String(s || '').trim().replace(/[^\w\-. ]+/g, '_').replace(/\s+/g, '_').slice(0, 160);
    const resolvedFileName = (
      meta.file_name && meta.file_name.trim()
        ? meta.file_name.trim()
        : `${safeSlug(meta.title) || 'report'}.pdf`
    );
    // Iter-108w — the web branch now also saves to backend.
    // `htmlToPdf` on web caches the freshly-rendered PDF's base64
    // against the blob URL it returns, so we don't have to re-render.
    {
      const cache = (globalThis as any).__fdcheck_pdf_cache || {};
      const entry = cache[uri];
      if (!entry || !entry.pdf_base64) {
        // No cached PDF for this URL — the server-side renderer wasn't
        // reachable and we fell back to an HTML preview. The caller
        // (triggerDownload) has already told the user; nothing to save.
        return null;
      }
      // Iter-251 — if the backend render job already persisted the report
      // server-side, don't POST a duplicate — just report success.
      if (entry.saved_report_id) {
        return { id: entry.saved_report_id as string };
      }
      const pdf_base64 = entry.pdf_base64 as string;
      const size_bytes = (pdf_base64.length * 3) / 4;
      // Iter-251 — surface failures. Previously every error here was
      // swallowed (console.warn + null), so a report that failed to save
      // (too large, server error, offline) vanished with no feedback.
      // We now throw so the caller shows a clear toast.
      const res = await savedReportsApi.create({
        building_id: meta.building_id ?? null,
        door_id: meta.door_id ?? null,
        inspection_id: meta.inspection_id ?? null,
        quote_id: meta.quote_id ?? null,
        report_type: meta.report_type,
        title: meta.title,
        file_name: resolvedFileName,
        pdf_base64,
        size_bytes,
        summary: meta.summary,
      });
      return { id: res.data?.id };
    }
  },
};
